import { speedMul, afflict, afflicted } from './sim/afflictions.ts';
// Forager, wildlife, pods and pickups. Everything moves in pixels and
// collides against the tile grid through callbacks on the game object.
import {
  sprite, spriteOutline, particle, spawnRing, flameSprite,
  drawSeed, drawGlim, drawSigil, drawPod, drawLeaf, drawSickle, TS, T,
  UI, BODY_EDGE, spriteRows, FRAMES, facing, drawWeapon, itemIcon } from './art.ts';
import { COLS, ROWS } from './world.ts';
import { isWet } from './passable.ts';
import { SCORCH_LINE } from './sim/fields.ts';
import { traitsOf, matterOf } from './matter.ts';
import { THIRST_FRAMES, THIRSTY, DRINK_SIGHT, DRINK_FRAMES, DOWNPOUR, SHELTER_SIGHT, AWARE, SCENT_TRAIL, FEARED, PEACEABLE, SHY_REACH, PROVOKED_FRAMES, standingOf, GUARD_REACH, ROUT_FRAMES, MEND_FRAMES, DIETS, HUNGER_FRAMES, HUNGRY, MIND_PLAYER, GRAZE_FRAMES, BITE_FRAMES, GRAZE_MEAL, seedHunger, findMeal, mealAt, mealLive } from './sim/food.ts';
import { PARTICLE_EDGE } from './art.ts';
import { poseFor, rigSprite, rigFlags, maskNormals, RIG_W, RIG_H, RIG_WADE_H, RIG_OX, RIG_OY } from './rig.ts';
import { SWING, SWING_BUFFER } from './swing.ts';
import { creatureSprite, hasRig } from './creatures.ts';

/* LS-178: the dodge, the reel, the flip, the torch */
/* THE DODGE IS A ROLL, NOT A BLINK (LS-210). Vijay: "dodge is too fast, dodge
 * should have extra iframes after, also dodge into strike should be a stab
 * combo". Fourteen frames that start quick and ease out (about the distance
 * the nine-frame dash covered), invulnerable through them and for DODGE_GRACE
 * frames after; a press during the roll is kept and becomes a thrust out of
 * its second half, and a press in the riposte window after it is a thrust. */
export const DODGE_FRAMES = 14, DODGE_SPEED = 2.4, DODGE_COOL = 18, RIPOSTE_FRAMES = 30, DODGE_GRACE = 12;   // the cooldown runs from the roll's start: four frames after it ends you can roll again (Vijay: "i should be able to dodge almost as soon as i finish the last one")
/* A HIT IS A FALL, NOT A FLICKER (LS-212). Vijay: "the flashing for iframes
 * doesn't look great anymore with this setup, we need a knock back into
 * ragdoll state instead". Being hit throws the body back for fourteen frames
 * and leaves it a ragdoll for RAGDOLL_FRAMES in all: flung, on its back, and
 * getting up, with no control until it is up; the invulnerability runs on
 * past that as a quiet grace and the body never strobes. */
export const RAGDOLL_FRAMES = 26;
/* ...AND ONLY A BIG HIT KNOCKS YOU DOWN (Vijay: "i shouldn't fall every time
 * that's for bigger damage, for smaller i should just get knocked back a
 * little with a hurt animation"). A touch for one is a FLINCH: a short shove
 * back, the body bent away from the blow with the arms up, and the stick is
 * yours again after a few frames. A blow for two or more -- a charge, a
 * lunge, a boss -- is the ragdoll. */
export const FLINCH_FRAMES = 14, FLINCH_LOCK = 10;
/* THE BLADE MEETS STONE, OR METAL (LS-213). Vijay: "when the sword hits
 * something like rock or metal i need a specific sound and animation and
 * slight knockback for that". A live swing whose blade reaches rock, a cliff
 * face, a standing stone, a wall or a cairn, or a shell that turns it, stops
 * there: a chip (stone) or a clang (metal), chips or sparks off the point,
 * the strike cut into its follow-through with the blade kicked back and the
 * body shoved a few pixels away, and the chain broken. Once per swing. */
export const RECOIL_FRAMES = 8;
/* A CRASH SOUNDS LIKE WHAT WAS HIT (LS-214). Vijay: "boar charge hit sound
 * should be contextual on what it hit it always sounds like metal right now".
 * The cue for a heavy body meeting the world at (x, y): stone for rock, a
 * cliff, masonry or a cairn; wood for a tree; brush for thorn or a nest; a
 * splash for water; the clang only for bars, a door or a gate. */
export function crashCue(game, x, y) {
  const tx = Math.floor((x) / TS), ty = Math.floor((y) / TS);
  const t = game.tileAt ? game.tileAt(tx, ty) : undefined;
  if (t === undefined) return 'crash_stone';   // the world's edge is rock
  if (t === T.TREE) return 'crash_wood';
  if (t === T.THORN || t === T.NEST) return 'crash_brush';
  if (t === T.WATER || t === T.SHALLOW) return 'splash';
  if (t === T.PUSH) return 'clang';
  if (t === T.ROCK || t === T.RUBBLE || t === T.CRACK || t === T.STONE || t === T.CAIRN || t === T.DEBRIS) return 'crash_stone';
  return 'thud';
}
/* THE SICKLE'S GRAMMAR (LS-183): hold this long after a swing and let go to
 * spin; a chained press must land within this many frames of a swing's end. */
export const SPIN_HOLD = 28, COMBO_WINDOW = 22;
/** THE SWING'S CLOCK (LS-207). Vijay: "the swiping is a little too slow".
 *  A swing is written as eighteen frames of `attack` -- wind-up above 14,
 *  live to 4, settle to 0 -- and every pose, hitbox and cue reads that
 *  scale; the counter simply runs faster than a frame, so the same arc
 *  plays in thirteen frames instead of eighteen and nothing else moves. */
/** The first opaque row of a rig's mask (cached on the rig): where a body's crown is, against its cell's top. */
export function crownRow(rig: any): number {
  if (!rig || !rig.mask || !rig.w || !rig.h) return 0;
  if (rig.crown !== undefined) return rig.crown;
  let row = 0;
  outer: for (let y = 0; y < rig.h; y++) { for (let x = 0; x < rig.w; x++) if (rig.mask[y * rig.w + x]) { row = y; break outer; } row = y + 1; }
  rig.crown = Math.min(row, rig.h);
  return rig.crown;
}
/** The weapon a swing is struck with: the one it began with, or the sickle before any swing. Read here and nowhere else (the robustness lens, round 45). */
export function swingWeaponOf(player: any) { return (player && player.swingWeapon) || ITEMS.fist; }
/** A body is wet when it is Soaked or standing out in rain worth the name (2026-09-11): its picture says so. */
export function isWetBody(body: any, game: any): boolean {
  if (!body || !game) return false;
  if (afflicted(body, 'Soaked')) return true;
  return !!(game.systems && game.systems.rain && game.area && game.systems.rain(game) > 0.25 && !body.submerged);
}   // bare hands, not the bag's sickle (LS-238)
export const SWING_SPEED = 1;   // the clock runs in frames again: the speed is in SWING's shape (src/swing.ts)
/** HOW LONG A WEAPON TAKES TO COME ROUND, AND HOW SOON ITS CHAIN OPENS -- in one place, because three things read
 *  them: the swing itself, the balance check, and the docs (2026-09-18).
 *
 *  A press is taken as soon as the swing is in its follow-through, so the period of mashed blows is
 *  `SWING.total - SWING.follow - open + max(0, recover)` and NOT `SWING.total / speed`, which is what the weapon
 *  table's speed column used to be read as. Speed below one buys recovery, speed above one buys an earlier chain.
 *  THE STONEHEART takes a third off a heavy weapon's PERIOD, which is what its note promises; taking a third off
 *  the recovery alone was 19% on the axe and 23% on the maul. */
export function openOf(spd: number) { return spd > 1 ? Math.round((1 - 1 / spd) * 5) : 0; }
export function recoveryOf(spd: number, stoneheart = false) {
  const base = Math.round((1 / spd - 1) * 20);
  if (!stoneheart || base <= 0) return base;
  const period = SWING.total - SWING.follow - openOf(spd) + base;
  return Math.max(0, Math.round(base - period / 3));
}
/** The frames between mashed blows with this weapon: what the table's speed column actually buys. */
export function swingPeriod(w: any, stoneheart = false) {
  const spd = (w && w.speed) || 1;
  const stone = stoneheart && !!(w && w.heavy);
  return SWING.total - SWING.follow - openOf(spd) + Math.max(0, recoveryOf(spd, stone));
}
export const REEL_FRAMES = 18, FLIP_FRAMES = 120, CHARGE_STUN = 60, TORCH_FRAMES = 240;
import { domeFromMask } from './light.ts';
import { wieldedWeapon, ITEMS, wearing, wearGear, bagOf } from './sim/inventory.ts';
import * as M3 from './model3d.ts';

/* THE BODIES GET RELIEF TOO, and from the same pixels the picture is made of.
 *
 * `light.js` bakes the tiles and the canopy; until this round nothing baked
 * the ACTORS, so the world had shape and everything walking around on it was
 * flat. The distance transform of a sprite's own mask is the right height
 * field for a body — thickest through the middle, falling to nothing at the
 * outline — which is exactly what `domeFromMask` was written for and why it
 * was exported. It had no callers.
 *
 * Cached by sprite name: the game has 22 baked bodies and they are the same
 * 22 every frame, so this runs about forty times in a whole session.
 */
const NORMAL_CACHE = new Map();
export function bodyNormals(name, flip = false) {
  const key = name + (flip ? '|f' : '');
  if (NORMAL_CACHE.has(key)) return NORMAL_CACHE.get(key);
  let out = null;
  const rows = spriteRows(name);
  if (rows && typeof document !== 'undefined') {
    const h = rows.length, w = rows[0].length;
    const mask = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const ch = rows[y][flip ? w - 1 - x : x];
        mask[y * w + x] = (ch && ch !== '.') ? 1 : 0;
      }
    }
    const rgba = domeFromMask(mask, w, h);
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const g2 = c.getContext('2d');
    if (g2 && g2.createImageData) {
      const img = g2.createImageData(w, h);
      /* THE SAME GUARD `light.js` HAS, THREE LINES FROM WHERE IT HAS IT.
       *
       * `bakeTileNormals` opens with `if (!img || !img.data) return null;` and
       * this did not copy it — so a context that hands back something without
       * `.data` (width 0, memory pressure) threw a TypeError out of
       * `pushLight`, out of `render`, and into the frame loop's recover-every-
       * frame path. Found by a robustness review reproducing it in the
       * project's own harness. */
      if (!img || !img.data) { NORMAL_CACHE.set(key, null); return null; }
      img.data.set(rgba);
      g2.putImageData(img, 0, 0);
      out = c;
    }
  }
  NORMAL_CACHE.set(key, out);
  return out;
}

export const PW = COLS * TS, PH = ROWS * TS;

/* A WEAPON'S CHAIN (LS-336; Vijay: "every equippable weapon needs its own unique attack pattern").
 *
 * Every weapon swung the same swing: `fore`, then `back`, then `thrust`, stretched by the weapon's
 * reach and paced by its speed, so the axe was a slow sickle and the spear was a long one. The
 * SPEAR goes first, as the shape that proves the rest can follow -- tick, tick, push: two straight
 * jabs that keep the shaft on one axis and hold their distance, then a third that drives and throws.
 *
 * The position in the chain is what advances now, not the KIND. It could not be the kind: `combo`
 * was read back off `fore`/`back`, so a weapon that thrusts twice would have reset its own chain
 * every swing and never reached a third. And the throw is the chain's LAST swing rather than any
 * thrust, which leaves every other weapon exactly as it was -- their third swing is the thrust -- and
 * stops a spear launching a body on the first jab. */
const CHAIN: Record<string, string[]> = {
  axe: ['chop', 'back', 'heave'],
  spear: ['thrust', 'thrust', 'thrust'],
  /* THE SECOND RANK SWINGS AS ITS VERB DOES (LS-335, 2026-09-15). This table is keyed by KIND, not by verb, so
   * every weapon added without a row here swung like a sickle -- and the swing's own boxes are where reach lives (a
   * thrust lays them out to 33 + reach - 16, a sweep to 19 + that). Measured before this row existed: the pike,
   * whose reach is 34, struck a body no further off than the spear's 26, because it was not thrusting at all. A
   * weapon's verb and its reach are both lies until its chain says what it does. */
  pike: ['thrust', 'thrust', 'thrust'],
  harpoon: ['thrust', 'thrust', 'thrust'],
  shard: ['thrust', 'thrust', 'thrust'],
  maul: ['chop', 'back', 'heave'],
  club: ['chop', 'back', 'heave'],
  firebrand: ['sweep', 'sweep', 'sweep'],
  billhook: ['fore', 'back', 'spin'],
  shellblade: ['fore', 'back', 'fore'],   // the knife's chain: no turn in a short blade
  spike: ['thrust', 'thrust', 'thrust'],
  hornpike: ['thrust', 'thrust', 'thrust'],
  tuskpike: ['thrust', 'thrust', 'thrust'],
  stonehammer: ['chop', 'back', 'heave'],
  knife: ['fore', 'back', 'fore'],   // no turn in it: a knife is too short to reap a circle with
  sickle: ['fore', 'back', 'spin'],   // cut, cut, turn: the turn is the spin, which already had its own arc, sound and shake
  torch: ['sweep', 'sweep', 'sweep'],   // sweep, sweep, sweep: three low fire arcs, the way the spear is three thrusts (LS-336)
  /* THE SICKLE: CUT, CUT, TURN (LS-336, codex's design). The turn is the SPIN, which already existed
   * as the charged attack -- its own full-circle arc, its own sound and shake, and it reaps in eight
   * directions -- so it is told apart at sixteen pixels with no new pose.
   *
   * IT TOOK THREE ATTEMPTS AND BOTH FAILURES WERE REAL FAULTS SOMEWHERE ELSE. The first made a drawn
   * game drift from a headless one, which was `cutTurf` being handed `layTuft` so that cutting turf
   * changed TILES only while drawing (see `game.ts` `onSwing`). The second looked like a dull blade
   * hurting a Great Beast, which the rule forbids -- and was a check counting every point the beast
   * lost as the blade's: the point was tagged `body:`, from a sporeling the spin had thrown INTO the
   * sow. A dull blade can still throw a small body, because the bare-hands rule spares beasts and a
   * sporeling is not one. The blade took nothing, exactly as the rule says.
   *
   * It also forced the launch rule to become what it meant: `swingLaunches` read `kind === 'thrust'`,
   * which was the same thing while every chain ended in one, and a sickle that cannot throw is a
   * STARTING weapon that cannot topple a shellback -- the counter LS-331 exists to make reachable,
   * met by a player with nothing else in hand. The chain's LAST swing throws now, whatever kind it
   * is; the charged spin is excluded by `chained` and still throws nothing, as before. */
  /* THE SPEAR: TICK, TICK, PUSH (LS-336, codex's design). Two straight jabs that keep the shaft on
   * one axis and hold their distance, then a third that drives and throws.
   *
   * IT TOOK TWO ATTEMPTS AND THE SECOND ONE FIXED THE INSTRUMENT, NOT THE DESIGN. The first read 5
   * of 6 Great Beasts felled against the shared chain's 6, and I went after the geometry: a thrust
   * is a LINE with a short fan while fore and back sweep an ARC, so I widened the jab. That made it
   * WORSE -- 4 of 6 -- and widening it further with the jab's lunge cut to 1.2 made no difference.
   * Statically the two chains were level all along: twenty seconds of mashing at an old shell does
   * 84 damage with the chain and 80 without, with no blow refused by the flash window in either.
   *
   * What was wrong was the harness. `beasts.ts` teleported the player beside the beast every frame
   * and never set their FACING, and never recentred them after moving. A fore or a back sweeps 2.3
   * radians and lands from a wrong facing; a thrust keeps the facing and misses. It was measuring
   * aim. With the harness facing what it hits -- every chain equally -- this chain fells 6 of 6 and
   * so does the shared one, which is what says the fix was to the instrument and not to the scale.
   * Found by codex reading the harness after I had spent an afternoon on the boxes. */
};
const CHAIN_DEFAULT = ['fore', 'back', 'thrust'];
export const chainOf = (kind: string) => ((globalThis as any).__sharedChain ? CHAIN_DEFAULT : CHAIN[kind]) || CHAIN_DEFAULT;   // `__sharedChain` puts every weapon back on one pattern: the control arm

export const ENEMY_DEFS = {
  sporeling: { hp: 2, speed: 0.62, touch: 1, art: 'sporeling', shoots: 'spore', size: 13 },
  bramble:   { hp: 3, speed: 0.78, touch: 1, art: 'bramble', size: 13 },   // touch 1 (2026-09-26): the commonest creature near home, and the quickest; at 2 it took ten of the newcomer probe's vigor in a minute on the sow's island
  wisp:      { hp: 1, speed: 1.2, touch: 1, art: 'wisp', ghost: true, size: 12 },
  gloam:     { hp: 1, speed: 0.9, touch: 0, art: 'gloam', ghost: true, size: 12, night: true },
  cinder:    { hp: 2, speed: 0.5, touch: 1, art: 'cinder', size: 13 },   // the fire-eater (LS-293): walks into a fire and eats it, and grows a life on each
  eel:       { hp: 3, speed: 0.75, touch: 2, art: 'eel', size: 14, aquatic: true },   // the silt eel (LS-293): the water is its ground and the land its wall; it hunts what wades   // a night moth (LS-293): out after dusk, drawn to any light, its dust the end of a torch; it burns
  shellback: { hp: 4, speed: 0.5, touch: 1, art: 'shellback', size: 14, armoured: true },   // touch 1 (2026-09-26): its plate is a lesson in going round, and at 2 a touch -- a fifth of a new body's vigor -- the newcomer probe died on the sow's beach before it could learn it
  /* TWO MORE KINDS KEYED TO SYSTEMS THAT WERE SCENERY (LS-335, 2026-09-16; Vijay: "and more creatures too"). The
   * tidecrab is keyed to the TIDE: it is out on a coast at the ebb only and gone at the flood, so the tide decides
   * what a shore holds. The mossback is keyed to WETNESS: it goes to wet ground and the dry kills it, so a fire or
   * a scorched country is its wall. Both are walkers; neither charges. */
  tidecrab:  { hp: 3, speed: 0.55, touch: 1, art: 'tidecrab', size: 13, armoured: true, lowTide: true },
  mossback:  { hp: 2, speed: 0.35, touch: 1, art: 'mossback', size: 13, wet: true },
  /* ...AND TWO FOR THE DRY COUNTRIES (LS-335, 2026-09-16), which had the thinnest pools. The sunbasker is keyed to
   * the DAY and the HEAT: out by day only, and half again as fast on ground hot enough to scorch you, so the ash at
   * noon is its country and the night is not. The screehopper is keyed to nothing new: a shelled hopper that fears
   * no fire, for the scree and the ash, where a fearful thing would never come near a player with a torch. */
  sunbasker: { hp: 2, speed: 0.7, touch: 1, art: 'sunbasker', size: 13, day: true, sun: true },
  screehopper: { hp: 3, speed: 0.6, touch: 1, art: 'screehopper', size: 13, armoured: true },
  /* ...AND TWO KEYED TO THE WEATHER AND THE HOUR (LS-335, 2026-09-16). The stormgull is in a coast's roster only while the
   * RAIN falls; it flies (ghost: over water and thickets) and a torch barely keeps it off. The duskling is out at DUSK only,
   * the hour the sun goes, and hides from fire. Neither is anywhere the clock and the sky do not put it. */
  stormgull: { hp: 2, speed: 1.0, touch: 1, art: 'stormgull', size: 13, ghost: true, rain: true },
  duskling:  { hp: 1, speed: 0.8, touch: 1, art: 'duskling', size: 12, dusk: true },
  charger:   { hp: 3, speed: 0.6, touch: 2, art: 'charger', size: 14, charges: true },
  sow:       { hp: 40, speed: 0.55, touch: 2, art: 'charger', size: 30, charges: true, boss: true, beast: true },   // a Great Beast (LS-225): a boar's habits at a boar's four times the weight
  oldshell:  { hp: 50, speed: 0.35, touch: 2, art: 'shellback', size: 34, armoured: true, boss: true, beast: true },   // blocks from the front; slow; the spear's reach is the answer
  bramblemother: { hp: 45, speed: 0.42, touch: 2, art: 'bramble', size: 32, boss: true, beast: true },   // a walking hedge: fire hurts it twice over
};

/* EIGHT WAYS TO GO (LS-194, the mechanics half). Vijay: "upgrade both
 * movement mechanics and art". The art landed first: a creature LOOKS the way
 * it travels, on the diagonal too. But `faceToward` still answered with one
 * of four, so a boar drawn running south-east was in fact running south,
 * then east, in a staircase -- the oldest tell of an axis-locked chase. The
 * four diagonals are unit vectors, so a creature on one covers the same
 * ground per frame as one on an axis. */
const D = Math.SQRT1_2;
const DIRVEC: Record<string, [number, number]> = {
  up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0],
  'up-right': [D, -D], 'down-right': [D, D], 'down-left': [-D, D], 'up-left': [-D, -D],
};
/** The four-way axis of any of the eight, for everything that asks an axis
 *  question: the tile ahead, the way a shot flies, which way a wall was hit. */
const AXIS_OF: Record<string, string> = {
  up: 'up', down: 'down', left: 'left', right: 'right',
  'up-right': 'right', 'down-right': 'right', 'down-left': 'left', 'up-left': 'left',
};
export const clampBox = (e) => { e.cx = e.x + e.w / 2; e.cy = e.y + e.h / 2; };

export function overlaps(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/** THE EIGHT POINTS A BODY COLLIDES ON.
 *
 *  Exported because the safety nets have to ask the same question the
 *  movement does. `freeThePlayer` and `canLeaveFrom` both asked "is the
 *  CENTRE inside something solid?" — and the player is a 13x13 body, so a
 *  position that straddles four tiles with one of them solid is stuck while
 *  its centre stands in open floor. Both nets certified it as fine. Measured:
 *  31% of wild wake points and 36 of 150 long bot runs ended frozen at
 *  exactly that offset, with the save recording the frozen position.
 */
/* THE PROBES ARE SYMMETRIC, AND THEY ARE NOT THE WHOLE BOX.
 *
 * A body is allowed to overlap what stops it by a pixel. That is not sloppy:
 * the player is 13px in a 16px doorway, and taking the inset away leaves 3px
 * of clearance instead of 5, which is the difference between a door you walk
 * through and a door you have to aim at. Measured, with the inset removed:
 * the bot got through 31% of 384 door approaches and finished 0 of 16 runs.
 *
 * What was actually wrong was that the inset was not symmetric — `x + 1` on
 * the left against `x + w - 2` on the right, which is one pixel in from the
 * left edge and TWO in from the exclusive right edge. Measured resting
 * against a tree: 1.20px inside it walking right, 0.15px short walking left.
 *
 * `w - IN - EDGE` is the same pixel as the old `w - 2` when x is whole, and
 * the right one when it is not: a box spans [x, x+w), so its last pixel is
 * `ceil(x+w) - 1`, and the player is at a fractional position in 56% of
 * frames.
 */
/** how far inside its own edge a body is tested — the give in a doorway */
const IN = 1;
/** ...and a hair inside the exclusive edge, so the probe is the same pixel at
 *  every sub-pixel phase rather than only at whole numbers */
const EDGE = 1 / 1024;

export function bodyPoints(x, y, w, h) {
  const x0 = x + IN, x1 = x + w - IN - EDGE;
  const y0 = y + IN, y1 = y + h - IN - EDGE;
  return [
    [x0, y0], [x1, y0],
    [x0, y1], [x1, y1],
    [x + w / 2, y0], [x + w / 2, y1],
    [x0, y + h / 2], [x1, y + h / 2],
  ];
}

function hits(game, e, x, y, ghost) {
  for (const [px, py] of bodyPoints(x, y, e.w, e.h)) {
    if (game.solid(px, py, ghost, e)) return true;
  }
  return false;
}

/* ...AND A BLOCKED STEP STOPS AT THE WALL, NOT WHEREVER THE LAST WHOLE STEP
 * HAPPENED TO LAND.
 *
 * This was all-or-nothing: the step either happened or it did not, so walking
 * into a tree left the body up to one frame of travel — about 1.4px at walking
 * speed — short of it, at a distance that depended on the sub-pixel phase you
 * arrived with. Different every approach, different in each direction, and
 * exactly the "I stop early on one side and not the other" it feels like.
 *
 * A blocked step now binary-searches the largest fraction of itself that is
 * legal, so the body comes to rest against what stopped it to within 1/64 of a
 * pixel, from every direction. Eight probes times seven bisections, and only
 * on the frames something is actually in the way.
 */
/** `ghost` is optional — a wisp crossing water asks for it, everything else
 *  does not. It was an undeclared fifth parameter that eleven of thirteen
 *  callers omitted, which reads at runtime as `undefined` and worked, and
 *  reads to a type checker as eleven mistakes. */
/* `ghost` is THREE-VALUED, which is why it was never declared: `false` for an
 * ordinary body, `true` where any pass-through will do, and the string
 * `'launch'` for a pod-thrown player, who crosses things a walker cannot. */
function moveAxis(game, e, dx, dy, ghost: string | boolean = false) {
  const nx = e.x + dx, ny = e.y + dy;
  if (!hits(game, e, nx, ny, ghost)) { e.x = nx; e.y = ny; return false; }
  let lo = 0, hi = 1;
  for (let i = 0; i < 7; i++) {
    const mid = (lo + hi) / 2;
    if (hits(game, e, e.x + dx * mid, e.y + dy * mid, ghost)) hi = mid;
    else lo = mid;
  }
  e.x += dx * lo; e.y += dy * lo;
  return true;
}

// ------------------------------------------------------------------ player
/** THE HIT'S FLASH (round 52's art lens, 2026-09-11): fourteen frames after a
 *  hurt the body's crop brightened by nothing -- only the camera shook. The
 *  creatures flash; the player did not. The rig's own mask, drawn white over
 *  the pose for six frames, one canvas a pose key, cached. */
const FLASHES = new Map<string, HTMLCanvasElement>();
function flashOf(key: string, spr: any): HTMLCanvasElement | null {
  if (typeof document === 'undefined' || !spr || !spr.mask) return null;
  const had = FLASHES.get(key); if (had) return had;
  const c = document.createElement('canvas'); c.width = RIG_W; c.height = RIG_H;
  const g = c.getContext('2d'); if (!g) return null;
  const img = g.createImageData(RIG_W, RIG_H); const d = img.data; const m = spr.mask;
  for (let i = 0; i < RIG_W * RIG_H && i < m.length; i++) if (m[i]) { d[i * 4] = 255; d[i * 4 + 1] = 250; d[i * 4 + 2] = 236; d[i * 4 + 3] = 255; }
  g.putImageData(img, 0, 0);
  FLASHES.set(key, c); return c;
}

export class Player {
  flash = 0;   // frames of the hit's white flash (round 52)
  fleckA: any;   // the blade's angle last frame, for the flecks' direction (LS-209)
  dodgeStab: any;   // a press kept during the roll, spent as a thrust (LS-210)
  ragdoll: any; ragdollDir: any;   // frames of ragdoll left after a hit, and the way the body flew (LS-212)
  /* HOW LONG THIS RAGDOLL WAS MEANT TO LAST. The poser reads the FRACTION
   * remaining, and it used to be told `RAGDOLL_FRAMES` whatever the body was
   * doing -- so a ragdoll of another length played at the wrong point of its
   * own fall. The death fall is one: it is 96 frames, and it stops halfway,
   * lying, instead of getting up (2026-09-10). */
  ragdollMax: any;
  prowler: any; fleeing: any;   // the night's pack: came for the player, and leaves at dawn (LS-379)
  flinch: any;   // frames of the small hit's hurt pose left (LS-212)
  recoil: any; recoilDir: any; recoilSwing: any;   // the blade kicked back off stone or metal (LS-213)
  landing = 0;   // frames of the dip after a fall lands
  /* THE SWING'S OWN STATE (2026-09-06), the last of this class the checker could not see: every one is
   * assigned inside `update` or `onSwing` and never in the constructor. `tsc` runs first in `npm test`, which
   * has not passed in a long time, so these went unread. */
  buffered: any; charge: any; combo: any; comboT: any; lunge: any; lungeDir: any; swingKind: any;
  swingLaunches = false;   // this swing is the chain's finisher: it throws a small body (LS-336)
  /* FIELDS, DECLARED.
   *
   * The migration's single biggest source of type errors — 4,646 of about
   * 5,300 — was this: properties assigned in methods rather than in the
   * constructor are invisible to the checker, so every read of one was an
   * error. Declaring them is what turns a class from a bag into a shape, and
   * a shape is what catches the defect that started this: `drawWorld`'s parts
   * object was missing `tiles` and nothing said so until it was reported from
   * play.
   *
   * They start as `any` because 44,000 lines cannot be typed in one step and
   * a wrong type is worse than a missing one. Narrowing them is the work that
   * follows, and it is worth doing in the order the bugs came from.
   */
  anim: any;
  dir8: any;
  stride: any;
  strideX: any;
  strideY: any;
  lastRig: any;
  ghosts: any;
  attack: any;
  cx: any;
  cy: any;
  dir: any;
  h: any;
  heldDirs: any;
  invuln: any;
  lit: any;                 // frames of carried fire (LS-176)
  dodge: any; dodgeCool: any; dodgeDir: any; riposte: any;   // the dodge and its riposte (LS-178)
  launchFrom: any;          // where the last launch began
  target: any;              // the body the current swing seeks (LS-203)
  kb: any;
  kbSafe: any;
  launchEnded: any;
  launchGrace: any;
  moving: any;
  submerged: any;
  swimming: any;
  w: any;
  wading: any;
  x: any;
  y: any;


  constructor(x, y) {
    this.x = x; this.y = y; this.w = 13; this.h = 13;
    this.dir = 'down';
    this.dir8 = 'down';   // the eight-way heading (LS-194); `dir` stays the axis
    this.anim = 0; this.attack = 0; this.invuln = 0; this.dodge = 0; this.dodgeCool = 0; this.riposte = 0; this.dodgeDir = [0, 1]; this.dodgeStab = false; this.ragdoll = 0; this.ragdollDir = [1, 0]; this.flinch = 0; this.recoil = 0; this.recoilDir = [1, 0]; this.recoilSwing = -1;
    this.combo = 0; this.comboT = 0; this.buffered = false; this.charge = 0; this.swingKind = 'fore'; this.lunge = 0;
    this.kb = null; this.moving = false; this.wading = false; this.swimming = false;
    clampBox(this);
  }

  /** Recompute the cached centre after an external move (screen change,
   *  respawn), otherwise cx/cy stay one transition behind. */
  recentre() { clampBox(this); }

  recover = 0;   // frames after a swing before the next may start (the weapon's speed, LS-228)
  /** HOW SOON THIS SWING'S CHAIN OPENS (2026-09-18). The chain opens at `SWING.follow` and the weapon's recovery
   *  holds it shut for longer -- which made SPEED ABOVE ONE do nothing at all: the knife, the shell saw, the whip,
   *  the shard and the thornwhip all landed a blow every ten frames, exactly as the sickle does, while carrying
   *  0.6 to 0.9 of its damage. Half the second rank was strictly dominated and every one of their notes ("shreds
   *  what is slow", "a knife with more bite") was false. A fast weapon opens its chain a few frames sooner, by the
   *  same shape the recovery uses and at half its scale, so a knife reads about a tenth under the sickle's damage a
   *  frame instead of thirty per cent under it. */
  swingOpen = 0;
  bufferedUntil = 0;
  bufferedSlot = -1;
  bufferedKind: string | null = null;
  swingWait() {
    return Math.max(1, Math.max(0, this.attack - SWING.follow - this.swingOpen) + Math.max(0, this.recover));
  }
  swingWeapon: any = null;   // the weapon this swing began with (round 42)
  wieldShow = 0;             // frames left of the raise when a weapon is picked (LS-238)
  swingSlot = -1;            // the bag slot the swing began from: the one that wears (the robustness lens, round 43 rerun)
  plateAt: any;              // the run frame the shell plate last turned a blow (LS-239)
  update(game, input) {
    if (!input) input = game.input || {};
    const wait = this.swingWait(), selected = bagOf(game.state).selected;
    if (this.buffered && (this.kb || this.dodge > 0 || this.swimming || this.submerged
        || game.bagOpen || game.runFrames > this.bufferedUntil
        || selected !== this.bufferedSlot || wieldedWeapon(game.state).kind !== this.bufferedKind)) this.buffered = false;
    // Keep one near-ready follow-up, never a press from the long part of recovery.
    if (input.attackPressed && wait > 1 && wait <= SWING_BUFFER && !this.buffered
        && !this.kb && this.dodge <= 0 && !this.swimming && !this.submerged && !game.bagOpen) {
      this.buffered = true; this.bufferedUntil = game.runFrames + SWING_BUFFER;
      this.bufferedSlot = selected; this.bufferedKind = wieldedWeapon(game.state).kind;
    }
    if (this.wieldShow > 0) this.wieldShow--;
    if (this.recover > 0 && this.attack <= SWING.follow + this.swingOpen) this.recover--;   // counts from the follow-through, where the chain would start
    this.anim++;
    /* THE LEGS ARE DRIVEN BY THE GROUND (LS-194 follow-up). Vijay: "walking
     * animation looks very strange". The gait's phase was `anim * 0.3` -- a
     * clock that runs whether or not the body moves -- so the feet cycled at
     * a fixed rate however fast you were going (wading at half speed, the
     * legs still sprinting), and every step you took began at whatever phase
     * the clock happened to be at, which is the snap you see when you start
     * walking. The stride is now measured in DISTANCE TRAVELLED: 0.21 radians
     * per pixel, so a full cycle is thirty pixels of ground whatever the
     * speed, and it carries over from step to step instead of restarting. */

    if (this.landing > 0) this.landing--;
    if (this.invuln > 0) this.invuln--;
    if (this.ragdoll > 0) this.ragdoll--;
    if (this.flinch > 0) this.flinch--;
    if (this.recoil > 0) this.recoil--;
    if (this.attack > 0) this.attack = Math.max(0, this.attack - SWING_SPEED);
    if (this.comboT > 0) this.comboT--; else this.combo = 0;
    if (this.attack > SWING.follow + 2 && this.lunge > 0 && this.dodge <= 0) {
      // the swing carries you: a step into a forehand, a stride into a thrust
      const [lx, ly] = this.lungeDir || [0, 0];
      moveAxis(game, this, lx * this.lunge, 0);
      moveAxis(game, this, 0, ly * this.lunge);
      clampBox(this);
    }
    this.wading = !game.aboard && game.isWadeAt(this.cx, this.cy);   // aboard, the water is under the hull and not the feet (LS-383)
    // Out of your depth. isSwimAt() was written for this and never wired to
    // anything — the headline verb of a whole charm had no mechanical
    // identity: deep water moved at wading speed and you could swing a sickle
    // mid-lake. Swimming is slower, and both hands are busy.
    this.swimming = !game.aboard && game.isSwimAt(this.cx, this.cy);
    this.stillFor = this.moving ? 0 : (this.stillFor || 0) + 1;   // for the plate's brace
    // --- THE SILT LUNG --------------------------------------------------
    // `this.submerged = this.swimming` is what made this charm a permission
    // rather than a tool: you were AUTOMATICALLY under the moment you stepped
    // into deep water, so there was no decision, no button and nothing to
    // learn. Going under is a use of the tool now (`Game.toggleSubmerge`); all
    // that survives here is that leaving the water brings you up.
    if (!this.swimming) this.submerged = false;

    if (this.kb) {
      // A launch flies over water and thorns; ordinary knockback does not.
      // Walls, trees and stone stop both — being pitched into a mountain is
      // not a technique, it is a bug report.
      const speed = this.kb.launched ? 3.6 : this.kb.light ? 1.3 : 2.6;   // a light shove is the blade's recoil (LS-213)
      const ghost = this.kb.launched ? 'launch' : false;
      /* BOTH AXES ARE ATTEMPTED, THEN COMBINED — see the note on the other
       * two of these. `|` does not short-circuit and `||` does, and a body in
       * knockback has to be offered both axes or it stops dead on a corner. */
      const hitX = moveAxis(game, this, this.kb.dx * speed, 0, ghost);
      const hitY = moveAxis(game, this, 0, this.kb.dy * speed, ghost);
      const hit = hitX || hitY;
      const from = this.kb.launched ? [this.kb.fromX, this.kb.fromY] : null;
      // ...and remember the furthest point along the flight that still had a
      // way out, so a blast that ends badly gives back the travel it can
      // rather than all of it. Sampled, not every frame: this is a flood
      // fill, and a launch is 26 frames long.
      if (this.kb && this.kb.launched && this.kb.t % 5 === 0
        && !game.solid(this.cx, this.cy, false, this)
        && game.canLeaveFrom(this.cx, this.cy)) {
        this.kbSafe = [this.x, this.y];
      }
      // A LATCH THAT OUTLIVES THE FLIGHT BY TWO FRAMES. `checkEdges` guards on
      // `kb && kb.launched`, and this line clears `kb` on the frame the launch
      // expires — so if that frame is also the out-of-bounds frame, the guard
      // sees nothing and the ordinary scroll runs. Measured over 262,528 real
      // blasts: 7,310 crossed a seam anyway, 270 of them through a closed tier
      // gate, and the arrival save fired on 40 of 40.
      if (this.kb.launched) this.launchEnded = 2;
      if (--this.kb.t <= 0 || (hit && !ghost)) this.kb = null;
      clampBox(this);
      // landing in something solid is the risk you took
      if (!this.kb && game.solid(this.cx, this.cy, false, this)) game.freeThePlayer();
      // ...but landing somewhere you cannot walk out of is not a risk, it is a
      // lost run. 0.15% of bare-handed launches came down in a pocket with no
      // reachable exit, no enemies and nothing to die to; a bot ran forty
      // thousand frames in one and never left.
      /* ...TO THE LAST PLACE ON THE FLIGHT YOU COULD HAVE LEFT FROM.
       *
       * Landing somewhere with no way out is a lost run and has to be undone.
       * Undoing it back to WHERE YOU WERE STANDING is the game cancelling a
       * thing you did on purpose — damage-boosting off your own pod is a way
       * to travel, and being snapped back to the launch pad reads as a bug.
       * It was reported as one.
       *
       * A first attempt let the landing stand whenever the region was bigger
       * than a pocket. That traded an annoyance for a disaster: soak's own
       * "never strands you where you land" caught 9 of 145 launches stranded.
       * The size of the room is not the question — whether you can get out of
       * it is.
       *
       * So the flight remembers the last point it passed through that had a
       * way out, and falls back to THAT. The blast still carries you; it just
       * cannot carry you somewhere you could never leave.
       */
      /* ...AND THE GUARD RUNS LAST, AFTER THE RESCUE NET HAS MOVED YOU.
       *
       * `freeThePlayer` fires above when a flight ends inside something
       * solid, and it has no leavability test of its own — its comment
       * delegates that to "the launch revert", which had already signed off.
       * So a flight could end over water, pass the guard at its landing
       * point, and THEN be displaced by the net into a four-tile pocket:
       * measured over 625 launches, 1 stranded exactly that way while the
       * suite stayed green.
       *
       * Checking after every displacement is the only order that holds. The
       * net moves you; then, wherever you have ended up, you must be able to
       * leave it.
       */
      if (!this.kb && from) {
        let tries = 0;
        while (tries++ < 3 && !game.canLeaveFrom(this.cx, this.cy)) {
          const safe = this.kbSafe || from;
          this.x = safe[0]; this.y = safe[1]; this.recentre();
          if (game.solid(this.cx, this.cy, false, this)) game.freeThePlayer();
          // ...and if the net moved us again, the loop asks again. Falling
          // back to `from` on the second pass means the launch pad, which is
          // by definition somewhere the player could stand and leave.
          this.kbSafe = null;
        }
      }
      if (!this.kb) this.kbSafe = null;
      return;
    }
    /* COMMITTED TO THE SWING, STILL WALKING (LS-207). The wind-up and the
     * strike used to return here and the body stood still for them; the
     * facing is held (the sweep's direction is read live from it) and the
     * dodge waits for the follow-through, but the legs and the stick keep
     * the body moving, because "striking while moving should not stop
     * movement". */
    const committed = this.attack > SWING.follow;

    // FULL AXIS WHEN THERE IS ONE. A stick points wherever a thumb does; the
    // four flags can only say which 45-degree sector that fell in, so seven of
    // every eight headings a player expresses are not the heading they get.
    // `ax`/`ay` carry the real vector, scaled by how far the stick is pushed,
    // so a light touch walks and a full push runs. The flags remain the answer
    // for a keyboard, where a key IS a cardinal.
    let dx = 0, dy = 0, mag = 1;
    if (input.ax || input.ay) {
      const m = Math.hypot(input.ax, input.ay);
      mag = Math.min(1, m);
      dx = input.ax / m; dy = input.ay / m;
    } else {
      if (input.left) dx -= 1;
      if (input.right) dx += 1;
      if (input.up) dy -= 1;
      if (input.down) dy += 1;
      // a keyboard diagonal is two full steps; normalise it or corner-running
      // is 41% faster than walking straight, the oldest bug in top-down movement
      if (dx && dy) { dx *= Math.SQRT1_2; dy *= Math.SQRT1_2; }
    }
    // DIAGONALS. This used to read `if (dx && dy) dy = 0` — four-way movement,
    // which was defensible when the only inputs were four arrow keys and four
    // pad arms. The pad is an analog stick now and points wherever a thumb
    // does, so throwing away one of its two axes meant a player pushing up-
    // right walked flat right and the control lied about itself.
    //
    // Each axis is still moved SEPARATELY through `moveAxis`, so a diagonal
    // cannot squeeze through a corner that neither axis could pass on its own
    // — the gating proof depends on that and it still holds over 300 seeds.
    this.moving = !!(dx || dy);
    /* FOOTSTEPS (LS-191): one a stride, on what you are walking on. Silent
     * without the sample bank (there is no synthesised step). */
    if (this.moving && !this.swimming && !this.submerged && this.dodge <= 0 && this.anim % 14 === 0 && game.tileKindAt) {
      const t = game.tileKindAt(this.cx, this.cy);
      /* THE STONE STEP COULD NEVER PLAY (2026-09-08; found while writing a check for a mutant codex
       * proposed -- replacing this whole line with a flat `step_grass` SURVIVED the entire suite).
       * The stone group here was ROCK, STONE, CLIFF and RUBBLE, and `passable.ts` says NONE OF THE
       * FOUR IS WALKABLE: the whole walkable set is GROUND, SAND, MOSS, DEBRIS, SCORCH, TUFT and
       * RAMP. So the player could never be standing on a tile that asked for it, and three
       * `step_stone` samples have shipped in the bank unheard. The stone-like ground you CAN stand
       * on is a ramp -- cut steps -- and debris, which is broken stone; they ask for it now. The
       * unwalkable four stay in the list because a body that is not the player can be on them. */
      const on = this.wading ? 'water'
        : (t === T.SAND) ? 'sand'
        : (t === T.RAMP || t === T.DEBRIS || t === T.ROCK || t === T.STONE || t === T.CLIFF || t === T.RUBBLE) ? 'stone'
        : 'grass';
      game.sfx('step_' + on, this.x + 8);
    }
    // The sprites are four-directional, so the facing takes whichever axis is
    // doing more of the work rather than always preferring the horizontal.
    //
    // ...AND A DEAD HEAT KEEPS THE FACING IT HAD. Two arrow keys give exactly
    // equal components, `>=` handed every one of those to the horizontal, and
    // an examiner measured the consequence: 55,019 of 55,019 diagonal frames
    // faced left or right. You could not face up or down while moving
    // diagonally at all — and `swingAngle`, `spray`, `lightBrazierAhead` and
    // `surge` all read `dir`, so holding a diagonal silently deleted half the
    // aiming space. Walking up and then adding right should leave you facing
    // up; it is the new key that is the qualifier, not the whole heading.
    if ((dx || dy) && !committed) {
      const ax = Math.abs(dx), ay = Math.abs(dy);
      const horiz = dx < 0 ? 'left' : 'right';
      const vert = dy < 0 ? 'up' : 'down';
      /* THE DIRECTION YOU PRESSED LAST IS THE DIRECTION YOU FACE.
       *
       * Three rules were tried here and the first two were both a bias:
       *
       *   - `ax >= ay` gave every diagonal tie to the horizontal. Measured,
       *     55,019 of 55,019 diagonal frames faced left or right — you could
       *     not face up or down while moving diagonally at all.
       *   - "keep the facing, and on a reversal take the axis that changed"
       *     inverted it: 600 frames of sustained diagonal play measured 100%
       *     vertical, 0% horizontal, because a flipping vertical input fired
       *     that branch every frame and nothing ever moved it back.
       *
       * Both failed the same way: they inferred intent from the heading, and
       * a diagonal heading does not carry one. A KEYPRESS does. So the newest
       * key wins outright, which is the rule every game of this shape uses and
       * the only one that lets a player choose while still holding a diagonal
       * — press RIGHT while running up-right and you turn to face right,
       * without letting go of anything.
       *
       * A stick has no keys, so an analog heading still takes its dominant
       * axis; a tie there keeps whatever it had rather than flickering.
       */
      const nowHeld = { up: !!input.up, down: !!input.down,
        left: !!input.left, right: !!input.right };
      const was = this.heldDirs || {};
      const fresh = [];
      for (const d of ['up', 'down', 'left', 'right']) {
        if (nowHeld[d] && !was[d]) fresh.push(d);
      }
      this.heldDirs = nowHeld;
      // ONE new key names a facing. TWO on the same frame name nothing —
      // there is no "most recent" between them, and taking the last of a
      // fixed list ['up','down','left','right'] silently means "always the
      // horizontal": measured, up+right, up+left, down+right and down+left
      // pressed together from a standstill faced left or right 4 times out of
      // 4, which is the original bias sneaking back in through the new rule.
      // Two at once falls through to the tie rule below, which keeps the axis
      // already being faced.
      if (fresh.length === 1) this.dir = fresh[0];
      else if (ax > ay + 1e-6) this.dir = horiz;
      else if (ay > ax + 1e-6) this.dir = vert;
      else if (this.dir !== horiz && this.dir !== vert) {
        // neither candidate is the facing and nothing was just pressed:
        // stay on the axis already being faced rather than snapping across
        this.dir = (this.dir === 'left' || this.dir === 'right') ? horiz : vert;
      }
      /* EIGHT FACINGS (LS-194). Vijay: "upgrade all sprites to have 8
       * directions of movement, upgrade both movement mechanics and art".
       *
       * `dir` stays one of four and keeps its meaning for everything that
       * reads it as an axis -- the doorway nudge, the tile ahead, the four
       * baked sprites of the old renderer -- because those questions ARE
       * about an axis. What was missing is the HEADING: the picture and the
       * blade should point where you are actually going. `dir8` is that, and
       * on a diagonal it is built from the same two components `dir` chose
       * between, so the two can never disagree about which way is forward.
       *
       * The threshold is on the components, not on an angle: a stick pushed
       * within a fifth of an axis is a straight walk, so a thumb resting a
       * little off true does not put you permanently on a diagonal. */
      const diagonal = ax > 0.2 && ay > 0.2 && Math.min(ax, ay) / Math.max(ax, ay) > 0.36;
      this.dir8 = diagonal ? (vert + '-' + horiz) : this.dir;
    }

    // ...and being under is slower still: it is a place to hide, not a way
    // to travel faster than the thing hunting you.
    // `dx`/`dy` are already a unit vector, so the length is carried by `mag`:
    // a stick half pushed is half speed, and a diagonal is never faster than
    // a straight line because the direction is normalised before it gets here.
    // a ragdoll has no say (LS-212): down, and getting up, until it is up; a flinch holds the stick for a few frames
    if (this.ragdoll > 0 && !this.kb) { this.moving = false; clampBox(this); return; }
    if (this.flinch > FLINCH_FRAMES - FLINCH_LOCK && !this.kb) { this.moving = false; clampBox(this); return; }
    if (this.dodgeCool > 0) this.dodgeCool--;
    if (this.riposte > 0) this.riposte--;
    if (this.dodge > 0) {
      this.dodge--;
      (this.ghosts = this.ghosts || []).push([this.x, this.y]);
      if (this.ghosts.length > 3) this.ghosts.shift();
      // quick out of the blocks, easing to a stop: 1.4x the speed at the start, 0.6x at the end
      const ease = DODGE_SPEED * (0.6 + 0.8 * (this.dodge + 1) / DODGE_FRAMES) * (game && game.state && wearing(game.state, 'greaves') ? 1.5 : 1);   // the hopper greaves (LS-335): a dodge carries half again as far
      moveAxis(game, this, this.dodgeDir[0] * ease, 0);
      moveAxis(game, this, 0, this.dodgeDir[1] * ease);
      clampBox(this);
      if (this.dodge % 3 === 0) game.puff(this.cx, this.cy + 5, UI.linen, 2, 'splash');
      // a press during the roll is kept, and the roll's second half becomes the stab (LS-210)
      if (game.input && game.input.attackPressed) { this.dodgeStab = true; game.input.attackPressed = false; }
      /* THE HARE FOOT IS WORTH WHAT IT SAYS (2026-09-18). "You can roll again a third sooner" -- and it cut only the
       * COOLDOWN, which runs from the roll's START, while the roll's own fourteen frames stood under it as a floor:
       * 18 frames became 15, not 12, because no roll may begin while one is running and this branch returns before
       * the press is ever read. The charm now lets the next roll begin in the TAIL of this one -- its slow, easing
       * end -- so the chain is a third quicker and each roll still carries its full distance. The press is left
       * standing for the block below, which starts the roll this frame, the way the stab already cuts the tail. */
      if (this.hareFoot(game) && this.dodge <= DODGE_FRAMES - Math.round(DODGE_COOL * 2 / 3)
          && (this.dodgeCool || 0) <= 0 && game.input && game.input.dodgePressed && !this.dodgeStab) { this.dodge = 0; this.ghosts = []; }
      else if (this.dodgeStab && this.dodge <= DODGE_FRAMES >> 1 && !this.swimming) {
        this.dodgeStab = false; this.dodge = 0;
        const [dx, dy] = this.dodgeDir;
        this.dir = Math.abs(dx) >= Math.abs(dy) ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down');
        this.startSwing(game, 'thrust');
      }
      if (this.dodge > 0) return;   // ...and a roll cut short by the next one falls through to the block that starts it
    }
    // STRIKING DOES NOT STOP THE WALK (LS-207). Vijay: "striking while moving
    // should not stop movement". The swing used to cut the pace to less than
    // half; it costs nothing now -- the lunge rides on top of the walk.
    const treads = !!(game && game.state && wearing(game.state, 'treads'));   // treads (LS-239): a sixth faster on land, no drag in the shallows
    /* REED FINS (LS-335) are the treads' mirror: the treads are a sixth faster
     * on land and cost nothing, the fins are half again as fast in the water
     * and a tenth slower out of it. Both live in the feet slot, so a map of
     * fen and coast asks for one and a map of plain asks for the other. */
    const fins = !!(game && game.state && wearing(game.state, 'fins'));
    const dry = !this.submerged && !this.swimming && !this.wading;
    const base = this.submerged ? (fins ? 0.54 : 0.36)
      : this.swimming ? (fins ? 0.75 : 0.5)
      : this.wading ? (fins ? 0.72 * 1.5 : treads ? 1 : 0.72)   // the fins' own half again (2026-09-18): they shared the treads' flat 1, which is 1.39x, and the promise is made in three places
      : 1.45;
    const hasted = !!(game && game.state && (game.state.haste || 0) > game.runFrames);   // quickroot (LS-335): the treads' sixth, for twenty seconds, and it does not stack with them
    // the scale charm (LS-335): the sunbasker's pace on ground over the scorch line, read off the same field the creature reads
    const scaled = dry && game && game.state && wearing(game.state, 'scalecharm') && hotUnder(game, this) ? 1.5 : 1;
    const speed = base * ((treads || hasted) && !this.submerged && !this.swimming ? 1.16 : 1) * (fins && dry ? 0.9 : 1) * mag * speedMul(this) * scaled;   // Soaked or Chilled, slower (LS-224)
    // WHICH AXIS WAS BLOCKED IS KNOWN HERE, so say so. `nudge` used to
    // re-derive it from `this.dir` — which on a diagonal is the DOMINANT axis
    // only, so both of these calls asked about the same axis and one of them
    // was always the wrong one. Measured over 6 seeds x 16 screens x 900
    // frames: 13,559 blocked-axis events on eight-way input, 4,262 of them
    // (31.4%) nudging along the axis that was not blocked.
    const was: [number, number] = [this.x, this.y];
    /* ABOARD (LS-383): the stick steers the hull and the body rides it; a push toward ground you could stand on is a
     * step ashore. Ashore, a push into water that a hull lies in is a step aboard. */
    if (game.aboard) { game.steerBoat(dx, dy); was[0] = this.x; was[1] = this.y; }   // carried, not striding: the legs are in the hull (Vijay: "why am i walking on the boat?")
    else {
      const bx = dx && moveAxis(game, this, dx * speed, 0), by = dy && moveAxis(game, this, 0, dy * speed);
      if (bx) this.nudge(game, 'x', Math.sign(dx));
      if (by) this.nudge(game, 'y', Math.sign(dy));
      if ((dx || dy) && game.boardIfBeside) game.boardIfBeside(dx, dy);   // blocked or not: a body that can wade (the lung) walked past the hull into the deep and drowned
    }
    clampBox(this);
    /* THE LEGS ARE DRIVEN BY THE GROUND (LS-194 follow-up). Vijay: "walking
     * animation looks very strange". The gait's phase was `anim * 0.3` -- a
     * clock that runs whatever the body is doing -- so the feet cycled at the
     * same rate wading at half speed as sprinting on grass, and every walk
     * began at whatever phase the clock had reached, which is the snap at the
     * first step. The stride is the ground actually covered, 0.21 radians a
     * pixel (a full cycle every thirty pixels), and it is measured against
     * the step this frame was allowed to take, so being placed somewhere by a
     * seam crossing or the harness is not a stride. */
    const step = Math.min(Math.hypot(this.x - was[0], this.y - was[1]), speed * 1.05);
    this.stride = ((this.stride ?? 0) + step * 0.21) % (Math.PI * 2);

    /* THE SICKLE HAS A GRAMMAR (LS-183). A press while the blade is still out
     * is not lost: it is kept, and the next swing starts the frame this one
     * ends. Three in a row are a forehand, a backhand and a thrust, and the
     * thrust throws. Hold the button after a swing and let go to spin. */
    if (input.attackHeld && this.attack <= SWING.follow + this.swingOpen && this.dodge <= 0 && !this.swimming) {
      this.charge++;
      if (this.charge === SPIN_HOLD) game.sfx('clang', this.x + 8);
      if (this.charge >= SPIN_HOLD && this.charge % 6 === 0) game.puff(this.cx + (this.anim % 3 - 1) * 4, this.cy - 6, UI.gold, 2, 'spark');
    } else if (!input.attackHeld && this.charge > 0) {
      const spin = this.charge >= SPIN_HOLD && this.attack <= SWING.follow + this.swingOpen && !this.swimming;
      this.charge = 0;
      if (spin) this.startSwing(game, 'spin', false);   // the charged spin is not a chain's last swing: it throws nothing
    }
    // ...and the next swing starts out of the follow-through (LS-207): a press
    // in the tail of one swing is the start of the next, which is the chain
    // ...AND THE WEAPON'S RECOVERY GATES THE CHAIN (the design lens, round 40): the axe was priced slow in the table and the
    // chain never read it -- five axe blows in forty-four frames. Only a weapon with recover > 0 waits.
    if ((input.attackPressed || this.buffered) && this.attack <= SWING.follow + this.swingOpen && this.recover <= 0 && !this.swimming) {
      this.buffered = false;
      // out of a dodge, the strike is the stab (LS-210): any press in the riposte window
      const chain = chainOf(game && game.state ? wieldedWeapon(game.state).kind : 'sickle');
      this.startSwing(game, this.riposte > 0 ? 'thrust' : chain[Math.min(this.combo, chain.length - 1)]);
    }
    /* THE DODGE (LS-178). A short dash the way you are moving, or facing,
     * with a moment of invulnerability in it, on a cooldown; and for a
     * second after it the sickle hits twice as hard -- the riposte. This is
     * the player's answer to a telegraph: step out of the charge, the ring,
     * the spore, and come back in. */
    /* A PRESS IS ONE PRESS. `dodgePressed` was read here and never cleared,
     * so a single tap of the dodge button stayed true through the cooldown
     * and fired again the frame it ended, for ever -- Vijay: "why does
     * clicking dodge cause me to continually dodge". The keyboard hid it
     * because keyup cleared the flag; a touch button sets it once and lets
     * go. The flag is consumed on read now, whether or not the dodge is
     * allowed this frame: a press that lands inside the cooldown is dropped,
     * which is what the button already tells the player. */
    const wantDodge = input.dodgePressed;
    input.dodgePressed = false;
    if (wantDodge && !committed && !game.aboard && this.dodge <= 0 && (this.dodgeCool || 0) <= 0 && !this.swimming && !this.submerged) {   // ...and not aboard a hull (2026-09-17): the roll left the boat and steerBoat yanked the body back thirty-six pixels the next frame
      const [fx, fy] = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[this.dir];
      // Use the same resolved heading as movement, including analog input.
      const len = Math.hypot(dx, dy) || 1;
      this.dodgeDir = (dx || dy) ? [dx / len, dy / len] : [fx, fy];
      this.dodge = DODGE_FRAMES;
      this.dodgeCool = Math.round(DODGE_COOL * (this.hareFoot(game) ? 2 / 3 : 1)); /* the hare foot (LS-335) */
      /* THE GRACE DOES NOT OUTLIVE THE COOLDOWN (2026-09-18). A roll grants 26 frames of it -- fourteen of rolling and
       * twelve of grace -- and another roll may begin every eighteen, so a finger tapping dodge held the body
       * untouchable for ever: measured with something biting on every frame of six hundred, not one frame was
       * touchable and the floor of `invuln` never fell below nine. A FIRST roll keeps both numbers; a roll begun
       * while the last one's grace is still running covers only its own fourteen frames, which leaves four touchable
       * frames in every eighteen of a chain. The dodge is still the answer to a charge; it is no longer a wall. */
      this.invuln = this.invuln > 0 ? Math.max(this.invuln, DODGE_FRAMES) : DODGE_FRAMES + DODGE_GRACE;   // and a grace after the roll (LS-210)
      this.launchGrace = Math.max(this.launchGrace || 0, DODGE_FRAMES + DODGE_GRACE + 4);   // a dodge is not a hurt either: no strobe through it
      this.riposte = Math.round(RIPOSTE_FRAMES * (wearing(game.state, 'quill') ? 1.5 : 1)); /* the quill charm (LS-335) holds the counter open */
      if (this.attack > SWING.follow) this.attack = SWING.follow;   // the dodge cancels the strike into its follow-through; the chain stays open
      for (let i = 0; i < 3; i++) game.puff(this.cx - this.dodgeDir[0] * 6, this.cy + 5, UI.linen, 2, 'dust');
      if (game.wind) game.wind(this.cx - this.dodgeDir[0] * 4, this.cy, -this.dodgeDir[0], -this.dodgeDir[1], 4, 0.5, 1.3);   // the air the dodge leaves behind (LS-207)
      game.sfx('dodge', this.x + 8);
    }
  }

  /** Slide along corners so doorways feel forgiving.
   *
   *  Snapping to the NEAREST tile boundary has a dead zone exactly where a
   *  doorway needs help: a 13px body sitting just past the middle of a lane
   *  gets nudged toward the boundary it is closest to, which next to a two-tile
   *  door is the wall rather than the opening — and in the band beyond 7px it
   *  gets no assist at all. Both candidate lanes are tried instead, and the one
   *  that is actually open in the direction of travel wins. */
  /** The hare foot: the charm that lets a roll begin in the tail of the last one (2026-09-18). */
  hareFoot(game): boolean { return !!(game && game.state && wearing(game.state, 'hare')); }

  nudge(game, axis = null, sign = 0) {
    const slide = 0.85;
    // The blocked axis, when the caller knows it; `this.dir` only otherwise,
    // which is right for a cardinal walk and wrong for every diagonal.
    const horiz = axis ? axis === 'x' : (this.dir === 'left' || this.dir === 'right');
    const back = sign !== 0 ? sign < 0
      : (this.dir === 'left' || this.dir === 'up');
    const pos = horiz ? this.y : this.x;
    const lo = Math.floor(pos / TS) * TS, hi = Math.ceil(pos / TS) * TS;
    // one step further along than the body reaches, so we ask about the tile
    // being walked into rather than the one already stood in
    const reach = 3;
    const clear = (lane) => {
      const x = horiz ? this.x + (back ? -reach : this.w + reach) : lane;
      const y = horiz ? lane : this.y + (back ? -reach : this.h + reach);
      return !game.solid(x, y, false, this)
        && !game.solid(x + (horiz ? 0 : this.w - 1), y + (horiz ? this.h - 1 : 0), false, this);
    };
    // Only when you are actually walking into something. A slide that fires
    // while the way ahead is already clear is the game steering for you; a
    // slide that fires only when you are stuck is the game letting you through
    // the door you were obviously aiming at, and can afford to be generous.
    if (clear(pos)) return;
    const options = [];
    for (const lane of [lo, hi]) {
      const diff = lane - pos;
      if (Math.abs(diff) <= 0.5 || Math.abs(diff) >= 13) continue;
      options.push({ diff, open: clear(lane) });
    }
    if (!options.length) return;
    // an open lane always beats a near one; between two of a kind, the nearer
    const best = options.sort((a, b) =>
      (b.open - a.open) || (Math.abs(a.diff) - Math.abs(b.diff)))[0];
    if (!best.open) return;
    if (horiz) moveAxis(game, this, 0, Math.sign(best.diff) * slide);
    else moveAxis(game, this, Math.sign(best.diff) * slide, 0);
  }

  /** The blade has met something it cannot cut (LS-213): stone or metal at (x, y). */
  recoilFrom(game, x, y, kind = 'stone') {
    if (this.attack <= SWING.follow || this.attack > SWING.live) return false;   // only a live blade
    if (this.recoilSwing === game.swingId) return false;                        // once per swing
    this.recoilSwing = game.swingId;
    const ang = Math.atan2(this.cy - y, this.cx - x);
    const dx = Math.cos(ang), dy = Math.sin(ang);
    this.recoil = RECOIL_FRAMES; this.recoilDir = [dx, dy];
    this.kb = { dx, dy, t: 4, light: true };
    this.attack = SWING.follow;   // the strike is over; the blade comes back in the follow-through
    this.buffered = false; this.lunge = 0;
    game.sfx(kind === 'metal' ? 'clang' : 'chip', this.x + 8);
    if (kind === 'metal') game.puff(x, y, UI.gold, 5, 'spark');
    else for (let i = 0; i < 4; i++) game.puff(x + (Math.random() - 0.5) * 4, y + (Math.random() - 0.5) * 4, UI.bone, 1, 'chunk');
    game.hitstopFor(4); game.shakeFor(2, 6);
    return true;
  }
  /** The body's answer to a blow (LS-212): a flinch for one, the ragdoll for more. */
  struck(dx, dy, dmg) {
    const crown = (globalThis as any).__game && wearing((globalThis as any).__game.state, 'shellcrown') ? 0.5 : 1;   // the shell crown (LS-335): a blow throws you half as far
    if (dmg >= 2) {
      this.kb = { dx, dy, t: Math.round(14 * crown) };
      this.ragdoll = RAGDOLL_FRAMES; this.ragdollMax = RAGDOLL_FRAMES; this.ragdollDir = [dx, dy]; this.flinch = 0;
    } else {
      this.kb = { dx, dy, t: Math.round(6 * crown) };
      this.flinch = FLINCH_FRAMES; this.ragdollDir = [dx, dy]; this.ragdoll = 0;
    }
  }
  /** A blow, or the weather.
   *
   *  THE TAG WAS ALWAYS PASSED AND NEVER READ (2026-09-10). Every affliction
   *  calls `hurt(game, 1, cx, cy + 8, 'heat' | 'poison' | 'smoke' | 'bleed')`
   *  and the signature stopped at `sy`, so a scorching went through the whole
   *  blow path: it SHOVED the body -- a flinch away from a point just under
   *  its own feet -- and it granted `invuln = 72`, a second and a fifth of
   *  invulnerability, every time it took a point. So standing on burning
   *  ground in a fight handed out a free window every six seconds, and a
   *  bleed jolted you while you were walking. Found through a lunge check
   *  whose seed happens to spawn Scorched: with the first point now landing
   *  inside a second, the shove arrived in the middle of the measurement.
   *
   *  The weather takes vigor and nothing else. It still shakes the glass and
   *  the affliction still makes its own sound, which is what says it
   *  happened. */
  /** Standing still in the shell plate: a wall (see hurt). */
  braced(game): boolean {
    return !!(game && game.state && wearing(game.state, 'plate') && (this.stillFor || 0) >= 10 && !(this.attack > 0) && !(this.dodge > 0) && !this.swimming && !this.submerged && !game.aboard);
  }
  stillFor: number;
  hurt(game, dmg, sx, sy, tag = '') {
    if (this.invuln > 0 || game.state.hp <= 0) return;
    /* ...AND A NAMED ATTACK IS NOT THE WEATHER (2026-09-18). The tag was invented to keep afflictions out of the
     * blow path, and then the boss moves borrowed it: the old shell's slam, the bramble mother's lash and the
     * storm's strike all passed a tag, so they took the weather path -- no shove, no stagger, four frames of
     * invulnerability instead of seventy-two, and the shell plate turned NONE of them while turning every bite.
     * The docs and the code's own comment promise that the slam "throws and hurts whoever stands within three
     * tiles". So the affliction tags keep the light path and an attack's tag runs the whole one. */
    if (tag && /^(slam|lash|lightning|boulder|body)/.test(String(tag))) tag = '';
    if (tag) {
      game.state.hp = Math.max(0, game.state.hp - dmg);
      game.state.beam = 0;
      this.invuln = 4;   // not a window, just enough that one tick cannot land twice
      this.flash = 6;
      game.shakeFor(2, 8);
      if (game.state.hp <= 0 && game.onDeath) game.onDeath();
      return;
    }
    /* BRACED IN THE PLATE, NOTHING MOVES YOU (2026-09-27, trophies are verbs). Wearing the old shell's plate and standing
     * still -- not walking, swinging or dodging -- every blow rings off: the slam, a bite, a charge. The plate is a wall
     * you carry, and a charging beast that meets it crashes as it would into stone. Each blow it turns wears it. */
    if (this.braced(game)) {
      this.invuln = 20; this.flash = 4; game.shakeFor(2, 6); game.puff(this.cx, this.cy - 4, UI.steel, 6, 'spark'); game.sfx('clang', this.x + 8);
      wearGear(game.state, 'plate'); if (game.gearSaved) game.gearSaved('plate');
      return;
    }
    /* THE PLATE TURNS A BLOW (LS-239): the old shell's, worn on the body. The first blow in six seconds rings off it,
     * and each turn wears it; dented through, it turns nothing more. */
    if (wearing(game.state, 'plate') && game.runFrames - (this.plateAt === undefined ? -9999 : this.plateAt) >= (wearing(game.state, 'clasp') ? 240 : 360)) {   // the shell clasp (LS-335) buys back two of those six seconds
      this.plateAt = game.runFrames; this.invuln = 40; this.flash = 6; if (game.gearSaved) game.gearSaved('plate');
      game.shakeFor(3, 10); game.puff(this.cx, this.cy, UI.steel, 10, 'spark'); game.sfx('clang', this.x + 8);
      wearGear(game.state, 'plate');   // the turn wears it through in silence (LS-373)
      const away = Math.atan2(this.cy - sy, this.cx - sx); this.struck(Math.cos(away), Math.sin(away), dmg);
      return;
    }
    /* THE STONE WARD IS GONE, because nothing has been able to grant one since the shop went with the pivot
     * (2026-09-06). It was BOUGHT, not found; `state.ward` was written in exactly one place, the purchase, and that
     * branch was deleted rounds ago -- so the field has been permanently zero, this whole branch has been dead, and
     * the run has carried a mechanic it could not have. Its art -- one stone a ward, riding behind the shoulder,
     * turning to stay on the side away from where you look -- was good and is in the history if a source for wards
     * is ever written. A mechanic with no source is not a mechanic. */
    game.state.hp = Math.max(0, game.state.hp - dmg);
    game.state.beam = 0;      // being hit costs the charge, not the mechanic
    this.invuln = 72;
    const ang = Math.atan2(this.cy - sy, this.cx - sx);
    this.struck(Math.cos(ang), Math.sin(ang), wearing(game.state, 'shellmail') ? Math.min(dmg, 1) : dmg);   // shell mail (LS-335): a blow that would flatten you only staggers you -- the harm is taken in full
    game.shakeFor(4, 14);
    game.hitstopFor(6);
    game.sfx('hurt', this.x + 8);
    if (game.state.hp <= 0) game.onDeath();
  }

  /** Thrown by your own blast. Longer and faster than being hit, and it
   *  crosses one tile of water or thorn on the way — which is what makes a
   *  seed pod a way past a gate you have no charm for. */
  launch(dx, dy) {
    // remember where they were standing: if the throw lands them somewhere
    // they cannot walk out of, this is where they go back to
    this.kb = { dx, dy, t: 26, launched: true, fromX: this.x, fromY: this.y };
    this.launchFrom = [this.x, this.y];   // kept past the flight: the landing may need it
    this.invuln = Math.max(this.invuln, 20);
  }

  /** The swing sweeps an arc; sample it as three boxes. */
  /** `chained` is false for the charged spin, which is its own attack and not a chain's finisher. */
  startSwing(game, kind, chained = true) {
    this.buffered = false;
    // the weapon's SPEED is its RECOVERY (LS-228): a heavy axe takes longer to come round for the next blow than a sickle; the swing itself is the same swing
    // THE SWING CARRIES ITS WEAPON (the robustness lens, round 42): an axe picked mid-sickle-swing crushed at the sickle's pace; the blow reads the weapon the swing began with
    this.swingWeapon = (game && game.state ? wieldedWeapon(game.state) : null) || ITEMS.fist;
    this.swingSlot = game && game.state ? bagOf(game.state).selected : -1;
    const spd = this.swingWeapon ? (this.swingWeapon.weapon.speed || 1) : 1;
    const heavy = !!(this.swingWeapon && this.swingWeapon.weapon.heavy);
    const stone = !!(heavy && game && game.state && wearing(game.state, 'stoneheart'));
    this.recover = recoveryOf(spd, stone);
    /* AND A FAST WEAPON'S CHAIN OPENS SOONER (2026-09-18): the other half of the same rule, by the same shape at
     * half the scale, so speed above one is worth something and no weapon in the rank is dominated. */
    this.swingOpen = openOf(spd);
    this.attack = SWING.total;
    this.swingKind = kind;
    const chain = chainOf(this.swingWeapon && this.swingWeapon.kind ? this.swingWeapon.kind : 'sickle');
    const last = kind === 'spin' || this.combo >= chain.length - 1;
    /* THE LAST SWING OF THE CHAIN IS THE ONE THAT THROWS, and a riposte always does.
     *
     * IT IS THE POSITION THAT DECIDES, NOT THE KIND (2026-09-08). This read `kind === 'thrust'`,
     * which was the same thing while every chain ended in a thrust. The sickle's turn ends its
     * chain with a SPIN, and a starting weapon that cannot throw is a starting weapon that cannot
     * topple a shellback -- which is the counter LS-331 exists to make reachable, met by a player
     * who has nothing else. The charged spin is excluded by `chained`: it is its own attack, held
     * for, and it threw nothing before. */
    /* ...AND NOW IT REALLY IS WHATEVER KIND IT IS (2026-09-10, LS-336). The
     * line still named three kinds, so the torch's new sweep -- last in its
     * chain -- threw nothing, and every weapon added after this one would
     * have had to come back and edit this list. The rule is the one the note
     * above states: the chain's last swing throws. */
    this.swingLaunches = chained && (last || this.riposte > 0);
    this.combo = kind === 'spin' ? 0 : last ? 0 : this.combo + 1;
    this.comboT = this.combo > 0 ? 18 + COMBO_WINDOW : 0;
    /* THE LUNGE IS MOMENTUM, NOT A CHARGE. The swing carries you the way you
     * were already moving -- a stride into a forehand, a sidestep through a
     * backhand, a thrust while backing off steps back -- and standing still it
     * carries you nowhere. The first cut always lunged along the FACING, so a
     * player strafing a boss was walked into it by their own swing. */
    const inp = game.input || {};
    const analog = inp.ax || inp.ay;
    const mx = analog ? (inp.ax || 0) : (inp.left ? -1 : 0) + (inp.right ? 1 : 0);
    const my = analog ? (inp.ay || 0) : (inp.up ? -1 : 0) + (inp.down ? 1 : 0);
    const L = Math.hypot(mx, my);
    /* A JAB HOLDS ITS GROUND; THE DRIVE IS THE ONE THAT COMMITS (2026-09-08, LS-336).
     *
     * The comment this replaces says "the last stab is the big one", and that was true while a thrust
     * WAS the last swing of every chain. The spear's chain makes the first two thrusts as well, and
     * they inherited the drive's lunge -- so jab, jab, drive charged the player 3.4 forward THREE
     * times, straight into a Great Beast, when holding distance is the whole of what a spear is for.
     * That is why its own chain lost the fight it answers and why widening the jab's box made it
     * worse rather than better: the fault was never the geometry, it was that every swing committed.
     * Measured statically the two chains are level -- 84 damage against 80 in twenty seconds, and no
     * blow refused by the flash window in either -- so what the beast check was seeing was the
     * player walking into it. */
    const mag = kind === 'spin' || !L ? 0 : kind === 'thrust' ? (this.swingLaunches ? 3.4 : 1.2) : 1.5;
    this.lunge = mag;
    this.lungeDir = L ? [mx / L, my / L] : [0, 0];
    /* THE SWING SEEKS THE BODY THE STICK POINTS AT (LS-203). Vijay: "an auto
     * targeting system so that the player automatically lunges towards the
     * enemy in the direction the joystick is pointing at and then smoothly
     * transition into other combat directions, like batman arkham asylum".
     *
     * The stick says which way you MEAN; the swing goes to the nearest body
     * within a seventy-degree cone of that, or within arm's reach in any
     * direction, and closes the gap: the lunge points at the body and is as
     * long as the gap needs (never more than four pixels a frame, so it is a
     * lunge and not a teleport), and the heading turns to face it so the arc
     * lands. Each swing in a chain targets afresh from the stick as it is
     * THEN held, which is how you flow from one body to the next: swing at
     * the bramble on your right, roll the stick down, and the next swing in
     * the chain goes to the boar below. A spin seeks nobody: it is the swing
     * for being surrounded. With no body in the cone the lunge is momentum,
     * as before. */
    if (kind !== 'spin' && game.enemies) {
      const ax = L ? mx / L : Math.cos(this.faceAngle()), ay = L ? my / L : Math.sin(this.faceAngle());
      let best: any = null, bestD = 1e9;
      for (const e of game.enemies) {
        if (!e || e.dead || e.def.ghost || e.spawnFx > 0 || e.hiding) continue;
        /* NOT A BOSS. Seeking is for crowds -- the Arkham fantasy is flowing
         * between bodies, and a Warden is one body you are already facing.
         * Seeking it turned holding the button into a lunge every swing, and
         * the balance duel measured it: mashing won 15 of 16 against a
         * player who mixed throws in, where the two had been level. A boss is
         * fought by choosing when to close, so the swing keeps its momentum
         * lunge there and nothing more. */
        if (e.def.boss) continue;
        const dx = e.cx - this.cx, dy = e.cy - this.cy;
        const d = Math.hypot(dx, dy);
        if (d > 64) continue;
        const along = (dx * ax + dy * ay) / (d || 1);          // cos of the angle off the stick
        if (d > 22 && along < 0.34) continue;                  // outside a seventy-degree cone
        if (along < -0.2) continue;                            // never a body BEHIND you: you pointed away from it
        if (d < bestD) { best = e; bestD = d; }
      }
      if (best) {
        const dx = best.cx - this.cx, dy = best.cy - this.cy, d = Math.hypot(dx, dy) || 1;
        this.lungeDir = [dx / d, dy / d];
        this.lunge = Math.max(0.8, Math.min(kind === 'thrust' ? 5 : 4, (d - 12) / 5));
        this.target = best;
        // face it: the eight-way heading toward the body, and the axis under it
        const a = Math.atan2(dy, dx);
        const oct = Math.round(a / (Math.PI / 4)) & 7;
        const names = ['right', 'down-right', 'down', 'down-left', 'left', 'up-left', 'up', 'up-right'];
        this.dir8 = names[oct];
        this.dir = AXIS_OF[this.dir8] || this.dir;
        if (this.dir8 === 'up' || this.dir8 === 'down') this.dir = this.dir8;
      } else this.target = null;
    } else this.target = null;
    game.onSwing(kind);
  }
  /** The eight-way heading, and the guarantee that it agrees with `dir`.
   *
   * `dir` is set from places that know nothing about the heading -- the
   * playground, a cutscene, the suites turning the body to face a target --
   * and a `dir8` left over from the last walk would then aim the sickle
   * somewhere the body is not looking. Measured: four combat checks went red
   * because a section set `dir = 'right'` while `dir8` still said 'down', and
   * every swing after it cut at the floor. If the axis is no longer part of
   * the heading, the heading is stale: `dir` wins. */
  facing() {
    const d8 = this.dir8;
    return d8 && String(d8).indexOf(this.dir) >= 0 ? d8 : (this.dir8 = this.dir);
  }
  /** The way the body faces, in radians, screen space: one of eight. */
  faceAngle() {
    const A: Record<string, number> = {
      right: 0, 'down-right': Math.PI / 4, down: Math.PI / 2, 'down-left': Math.PI * 0.75,
      left: Math.PI, 'up-left': -Math.PI * 0.75, up: -Math.PI / 2, 'up-right': -Math.PI / 4,
    };
    const a = A[this.facing()];
    return a === undefined ? A[this.dir] : a;
  }
  /** What the painter should put in the hand.
   *
   * WHILE A SWING IS OUT IT IS THE SWING'S WEAPON, NOT THE BAG'S (2026-09-08, named by codex).
   * This read `wieldedWeapon(game.state)` -- the current selection -- so selecting another weapon
   * during a swing's live frames changed the object in the swinging hand without restarting the
   * arc: measured at attack frame 18, the swing still bound to the axe while the bag said spear,
   * and codex exercised the poser to find 38 pixels changing with the hand in the same place. An
   * axe swing that turns into a spear halfway through. The blow already belonged to the captured
   * weapon (damage, verb, reach, wear); now the picture does too. Between swings it is the bag's,
   * which is what makes picking a weapon show it. */
  heldSprite(game) {
    if (this.attack > 0 && this.swingWeapon && this.swingWeapon.kind) return this.swingWeapon.kind;
    return game && game.state ? wieldedWeapon(game.state).kind : 'sickle';
  }

  swingAngle() {
    const base = this.faceAngle();
    const t = (SWING.live - this.attack) / (SWING.live - SWING.follow);   // 0 -> 1 across the strike
    const u = Math.min(1, Math.max(0, t));
    // the follow-through (LS-207): the blade carries on past the arc's end
    // and comes back, a rise and fall over the tail
    const over = this.attack < SWING.follow ? Math.sin(Math.PI * (SWING.follow - this.attack) / SWING.follow) * 0.45 : 0;
    if (this.swingKind === 'thrust') return base;                        // straight out
    if (this.swingKind === 'chop') return base;
    if (this.swingKind === 'heave') return base - 0.45 + u * 0.9;
    if (this.swingKind === 'spin') return base - Math.PI + u * Math.PI * 2 * (14 / 13) + over;
    /* THE SWEEP (LS-336): three radians where a cut is 2.3, low and slow, and
     * it starts further behind the shoulder. The torch does not cut anything;
     * what it is for is putting fire on what it passes, so the pattern that
     * suits it covers GROUND rather than finding an edge. */
    if (this.swingKind === 'sweep') return base + (-1.5 + u * 3.0) + over;
    if (this.swingKind === 'back') return base + (1.15 - u * 2.3) - over;      // the other way
    return base + (-1.15 + u * 2.3) + over;
  }

  swingBoxes(game) {
    if (this.attack <= SWING.follow || this.attack > SWING.live) return null;
    const a = this.swingAngle();
    const out = [];
    // the weapon in hand's reach over the sickle's sixteen (LS-225: the spear reaches ten further, the axe two less), plus a Long Haft's 4px a purchase
    /* THE REACH IS THE SWING'S, NOT THE BAG'S (2026-09-08, named by codex reviewing LS-336).
     *
     * This read `wieldedWeapon(game.state)` -- whatever is in the selected slot RIGHT NOW -- while
     * the damage, the verb (pierce and crush both test it) and the wear all read `swingWeapon`,
     * captured when the swing began (`entities.ts:827`). So the boxes belonged to a different weapon
     * from the blow. Measured with the player pinned, so the lunge could not close the distance: a
     * body 30px away takes NOTHING from an axe, whose reach is 14 -- and takes the axe's full 2
     * damage if you select the spear after the swing starts and before the blade goes live. Start
     * heavy, swap long, hit for the axe at the spear's reach. It is the same capture the round-43
     * rerun made for `swingSlot`, which that card called "the stack that swung is the one that
     * wears"; the boxes were the one place still asking the bag. */
    const ext = game ? swingWeaponOf(this).weapon.reach - 16 : 0;   // `state.reach` is gone: nothing granted it, so it added zero
    if (this.swingKind === 'thrust') {
      /* THE THRUST IS A LINE WITH A SHORT FAN AT THE HAND. A pure line along a
       * four-way facing missed a body standing diagonally, which the old arc
       * had always caught -- against a Warden a third of the chain whiffed
       * and the duels went from won to lost. Near the hand the point covers
       * the diagonal; the long reach stays narrow, which is what a thrust is. */
      /* A WIDER JAB WAS TRIED HERE AND MADE IT WORSE (2026-09-08, LS-336). Giving the swings before
       * a chain's finisher the ±0.25 fan at full reach, instead of narrowing to a point, took the
       * beast check from five of six felled to FOUR. Whatever loses that fight, it is not the jab
       * being too narrow, and the obvious fix is the wrong one -- which is worth a line here so the
       * next person does not spend the afternoon I did. */
      for (const [r, da] of [[10, -0.45], [10, 0], [10, 0.45], [16, -0.35], [16, 0], [16, 0.35], [22 + ext, -0.25], [22 + ext, 0], [22 + ext, 0.25], [28 + ext, 0], [33 + ext, 0]]) {
        out.push({ x: this.cx + Math.cos(a + da) * r - 5, y: this.cy + Math.sin(a + da) * r - 5, w: 10, h: 10 });
      }
      return out;
    }
    /* A SWEEP IS BROAD IN THE HAND AS WELL AS OVER TIME (2026-09-10, LS-336).
     * Three radians of arc bought NOTHING measurable on its own: one bramble
     * set at each of nine angles round the body caught fire at seven of them
     * for the sweep and seven for the old shared cut, because the boxes are
     * ten pixels wide and the follow-through carries them another 0.45 rad --
     * the arc was already covering everything within reach. The flame itself
     * has to be wide, so the sweep lays its boxes in a fan either side of the
     * line, which is also what a torch looks like being swung. */
    if (this.swingKind === 'sweep') {
      for (const r of [8, 14, 19 + ext]) for (const da of [-0.42, 0, 0.42]) {
        out.push({ x: this.cx + Math.cos(a + da) * r - 5, y: this.cy + Math.sin(a + da) * r - 5, w: 10, h: 10 });
      }
      return out;
    }
    /* A LONG HAFT REACHES (2026-09-18). A cut lays three boxes, at 8, 14 and 19 + the weapon's reach over a sickle's
     * -- so a cut delivered about `reach + 7` however long the thing was, while a thrust delivers about `reach + 21`.
     * The two whips are cuts with a spear's printed reach: the whip prints 26, the same as the spear, and struck to
     * 33 against the spear's 47; the thornwhip prints 30, longer than either, and struck to 37 -- shorter than both.
     * Their own note sells the whip as "the reach of a harpoon with a knife's speed", and it had neither. A weapon
     * whose reach is well past a sickle's lays one box further out, which is what a long haft is. */
    for (const r of [8, 14, 19 + ext, ...(ext > 6 ? [33 + ext] : [])]) {
      out.push({
        x: this.cx + Math.cos(a) * r - 5, y: this.cy + Math.sin(a) * r - 5, w: 10, h: 10,
      });
    }
    return out;
  }

  /** Which of the forager's baked bodies is on screen, and whether it is
   *  mirrored. Read by `draw` and by the relief pass, so the picture and its
   *  shape can never name two different sprites. */
  bodyName(frame) {
    if (this.dir === 'up') return { name: 'forager_up_' + frame, flip: false };
    if (this.dir === 'down') return { name: 'forager_down_' + frame, flip: false };
    return { name: 'forager_side_' + frame, flip: this.dir === 'left' };
  }

  draw(g, game) {
    // A LAUNCH IS NOT A HIT. The blast sets `invuln` to 71 frames, and the
    // ordinary got-hit strobe hides the player for 12 of the launch's 25 —
    // 48% of a three-tile throw spent invisible, in the walk frames, dead
    // straight at a constant speed. It read as a slide. Keep the flicker for
    // damage; while the launch is running, arc the sprite instead and drop a
    // shadow so the player can see where they are going to come down.
    // ...and for the whole invulnerability the blast granted, not just the
    // flight. `invuln` is 71 frames and the throw is 26, so the strobe hid the
    // player for 23 of the 46 frames AFTER they landed — telling them they had
    // been hurt by a pod they placed on purpose.
    this.lastRig = null;
    const flying = this.kb && this.kb.launched;
    if (flying) this.launchGrace = 50;
    if (this.launchGrace > 0) this.launchGrace--;
    // (the got-hit strobe is gone, LS-212: a hit is a ragdoll now, and the
    // grace that follows it is quiet -- nothing hides the body)
    /* A BODY STANDING ON THE GROUND CASTS A SHADOW ON IT.
     *
     * There was one only while FLYING — during knockback — so for all the time
     * the player is actually walking they had none, and in a top-down view the
     * contact shadow IS the ground contact. Without it the sprite reads as a
     * decal pasted on the same plane as the tiles rather than as a thing
     * standing on them, which is exactly the read that breaks once the world
     * has cliffs and two floors in it.
     *
     * Small, soft, and under the feet: it should say where the body touches
     * down without becoming a second silhouette competing with the first. */
    let lift = 0;
    /* THE JUMP. Going off a cliff starts a fall (`checkLedge`), and the body
     * already travels an arc; what was missing was the look of it. While
     * falling the shadow stays on the GROUND under the arc and shrinks with
     * height, and the body is drawn tucked -- legs pulled up two rows -- so
     * it reads as a jump and not a walk through the air. Landing dips it a
     * pixel for six frames. */
    const fall = game.fall;
    if (fall) {
      const u = Math.min(1, fall.t / fall.dur);
      const gx = fall.x0 + (fall.x1 - fall.x0) * u, gy = fall.y0 + (fall.y1 - fall.y0) * u;
      const h = Math.sin(u * Math.PI) * fall.lift;
      const shrink = Math.round(h / 4);
      g.fillStyle = UI.shadow;
      g.fillRect(Math.round(gx) + 4 + shrink, Math.round(gy) + 13, Math.max(2, 8 - shrink * 2), 2);
      if (game.rig !== false) {
        // the rig, tucked, through the arc (LS-184); the old frame sprite drew
        // here as a clipped box for a dozen frames of every drop (LS-196)
        const pose = poseFor({ dir: this.facing(), anim: this.anim, moving: false, attack: 0, swingKind: 'fore', swingAngle: 0, dodge: 3, dodgeDir: [this.dir === 'left' ? -1 : this.dir === 'right' ? 1 : 0, 0], reel: false, lit: this.lit > 0 });
        const spr = rigSprite(pose);
        g.drawImage(spr.canvas, spr.sx || 0, spr.sy || 0, RIG_W, RIG_H, Math.round(gx) + RIG_OX, Math.round(gy - h) + RIG_OY, RIG_W, RIG_H);
        if (this.flash > 0 && ((this.flash >> 1) & 1) === 0) { const f = flashOf(pose.key, spr); if (f) { g.globalAlpha = 0.6; g.drawImage(f, 0, 0, RIG_W, RIG_H, Math.round(gx) + RIG_OX, Math.round(gy - h) + RIG_OY, RIG_W, RIG_H); g.globalAlpha = 1; } this.flash--; }
        this.lastRig = { key: pose.key, mask: spr.mask, normals: spr.normals, w: RIG_W, h: RIG_H,
          ox: Math.round(gx) + RIG_OX - Math.round(this.x), oy: Math.round(gy - h) + RIG_OY - Math.round(this.y) };
        return;
      }
      const frame0 = Math.floor(this.anim / 2) % FRAMES;
      const { name: jn, flip: jf } = this.bodyName(frame0);
      const bx = Math.round(this.x - 1), by = Math.round(this.y - 2);
      /* TUCKED, NOT CUT. A clip that dropped the bottom rows took the boots
       * and their stripe with it ("stripe getting cut off during jump"). The
       * body is drawn to its knees, then the boots are drawn again two
       * pixels higher, under the body, so the legs come UP into the tuck. */
      g.save();
      g.beginPath(); g.rect(bx - 2, by - 2, 20, 15); g.clip();          // the body, to the knees
      g.drawImage(spriteOutline(jn, jf), bx - 1, by - 1);
      g.drawImage(sprite(jn, jf), bx, by);
      g.restore();
      g.save();
      g.beginPath(); g.rect(bx - 2, by + 11, 20, 3); g.clip();           // the boots, two rows up
      g.drawImage(spriteOutline(jn, jf), bx - 1, by - 3);
      g.drawImage(sprite(jn, jf), bx, by - 2);
      g.restore();
      return;
    }
    const dip = this.landing > 0 ? 1 : 0;
    if (!flying) {
      // CAST, not stamped: the sun is up and to the left, so the forager's shadow lies down and to the right of the boots
      M3.groundShadow([9.2 + dip * 0.5, 0, 2.2], 4.2 + dip * 0.6, Math.round(this.x), Math.round(this.y) + 13,
        (px2: number, py: number, c: string) => { g.fillStyle = c; g.fillRect(px2, py, 1, 1); }, UI.shadow);
    }
    if (flying) {
      const t = 1 - this.kb.t / 26;
      lift = Math.round(7 * Math.sin(Math.PI * t));
      g.fillStyle = UI.shadow;
      // ...on whole pixels. `lift` is 0..7, so `- lift / 2` put the shadow's
      // left edge on a half pixel for every odd value and `12 + lift` made the
      // width odd with it: both edges mid-pixel, 487 measured fills. Widen in
      // twos so the shadow stays centred without leaving the grid.
      const spread = Math.round(lift / 2);
      g.fillRect(Math.round(this.x) + 2 - spread, Math.round(this.y) + 12, 12 + spread * 2, 3);
    }
    /* EIGHT FRAMES, AT THE SAME SPEED AS THE OLD TWO.
     *
     * This was `/ 7 % 2` — a fourteen-tick cycle of two poses. Leaving the
     * divisor alone and only widening the modulus would have made every cycle
     * four times longer, which is not "smoother", it is slow motion. A quarter
     * of the divisor keeps the full cycle at sixteen ticks against the old
     * fourteen, so the hero walks at the speed she always did with four times
     * the drawings. */
    const frame = this.moving || this.attack ? Math.floor(this.anim / 2) % FRAMES : 0;
    /* AND STANDING STILL IS NOT THE SAME AS BEING SWITCHED OFF.
     *
     * Every other body in this game moves while it waits — the wood breathes
     * under the wind, the grass leans, a nest pulses, water crawls. The
     * player stood on frame 0 and did not move a pixel until you pressed
     * something, which makes the one thing you are supposed to identify with
     * the only dead thing on the screen.
     *
     * A BREATH, NOT A HOP. Bouncing the whole sprite a pixel reads as a
     * hover, and at this size it is a big movement — 1px is 6% of the body.
     * The feet stay put and the top of the body settles onto them, so the
     * figure COMPRESSES: the same trick the canopy uses, where what moves is
     * the light across the crown rather than the crown itself.
     *
     * Off while walking, swinging, launched, wading or under: each of those
     * already has its own motion, and two animations on one body at once is
     * how a sprite starts to jitter.
     */
    const still = !this.moving && !this.attack && !this.kb
      && !this.submerged && !this.wading;
    // 66 frames, a little over a second, settled for the last third of it
    const breath = still && Math.floor(this.anim / 22) % 3 === 2 ? 1 : 0;
    /* ONE PLACE DECIDES WHICH BODY IS ON SCREEN. It used to be decided here,
     * inline in `draw`, which meant the relief pass would have had to work it
     * out a second time from the same three facts — and two models of one
     * question is how this project ships bugs. `bodyName` is that one place;
     * `Game.pushLight` asks it for the same answer the picture uses. */
    const { name, flip } = this.bodyName(frame);
    if (this.submerged) {
      // UNDER IT, not on it. Only the ripple where you went down.
      const t = Math.floor(this.anim / 8) % 2;
      g.fillStyle = UI.underDeep;
      g.fillRect(Math.round(this.x) - 2 + t, Math.round(this.y) + 6, 17 - t * 2, 1);
      g.fillStyle = UI.underPale;
      g.fillRect(Math.round(this.x) + 2 + t, Math.round(this.y) + 9, 9, 1);
      return;
    }
    /* THE FORAGER HAS BONES (LS-184): the figure is posed from this state and
     * painted by `rig.ts`, sickle in hand; the frame sprites stay for the fall
     * and for anything that still asks `bodyName`. `game.rig === false` puts
     * the old sprite back. */
    const rigged = game.rig !== false;
    rigFlags.on = rigged;
    if (rigged) {
      const pose = poseFor({
        dir: this.facing(), anim: this.anim, stride: this.stride, moving: !!this.moving, attack: this.attack, swingKind: this.swingKind || 'fore',
        swingAngle: this.attack > 0 ? this.swingAngle() : 0, dodge: this.dodge, dodgeMax: DODGE_FRAMES, dodgeDir: this.dodgeDir || [1, 0], ragdoll: this.ragdoll, ragdollMax: this.ragdollMax || RAGDOLL_FRAMES, ragdollDir: this.ragdollDir || [1, 0], flinch: this.flinch, flinchMax: FLINCH_FRAMES, recoil: this.recoil, recoilMax: RECOIL_FRAMES, recoilDir: this.recoilDir || [1, 0],
        reel: !!this.kb && !flying, lit: this.lit > 0, wading: !!this.wading, charge: this.charge || (this.wieldShow > 0 && this.attack <= 0 ? 24 : 0),   // picking a weapon raises it as a charge would (LS-238)
        weapon: this.heldSprite(game),   // the kind in hand: the painter draws it (LS-238)
        wet: isWetBody(this, game),   // soaked, or in the rain (2026-09-11)
        seated: !!(game && game.aboard),   // riding a hull (LS-383): the body sits, it does not stand
        gear: game && game.state && game.state.gear ? (game.state.gear.body ? game.state.gear.body.kind : '') + (game.state.gear.feet ? '+' + game.state.gear.feet.kind : '') : '',   // what is worn tints the tunic and the boots (LS-239)
      });
      const spr = rigSprite(pose);
      // the idle breath: the whole body settles one pixel every third beat, as the sprite did
      const breathPx = (!this.moving && !this.attack && !this.kb && !this.submerged && !this.wading && Math.floor(this.anim / 22) % 3 === 2) ? 1 : 0;
      this.lastRig = { key: pose.key, mask: spr.mask, normals: spr.normals, w: RIG_W, h: RIG_H, ox: RIG_OX, oy: RIG_OY - lift + breathPx };
      const bx = Math.round(this.x) + RIG_OX, by = Math.round(this.y) + RIG_OY - lift + breathPx;
      // JUICE (LS-187): the dash leaves after-images that fade
      if (this.dodge > 0 && this.ghosts) {
        const opacity = g.globalAlpha;
        this.ghosts.forEach((gp, i) => {
          g.globalAlpha = opacity * (0.12 + 0.12 * i);
          g.drawImage(spr.canvas, spr.sx || 0, spr.sy || 0, RIG_W, RIG_H, Math.round(gp[0]) + RIG_OX, Math.round(gp[1]) + RIG_OY - lift, RIG_W, RIG_H);
          if (this.flash > 0 && ((this.flash >> 1) & 1) === 0) { const f = flashOf(pose.key, spr); if (f) { g.globalAlpha = 0.6; g.drawImage(f, 0, 0, RIG_W, RIG_H, Math.round(gp[0]) + RIG_OX, Math.round(gp[1]) + RIG_OY - lift, RIG_W, RIG_H); g.globalAlpha = 1; } this.flash--; }   // the hit's flash on the walking path too (round 52)
        });
        g.globalAlpha = opacity;
      } else this.ghosts = null;
      if (this.wading) {
        g.fillStyle = UI.underPale;
        g.fillRect(Math.round(this.x) - 3, Math.round(this.y) + 9, 19, 2);
        g.save(); g.beginPath(); g.rect(bx, by, RIG_W, RIG_WADE_H); g.clip();
        { g.drawImage(spr.canvas, spr.sx || 0, spr.sy || 0, RIG_W, RIG_H, bx, by, RIG_W, RIG_H); if (this.flash > 0 && ((this.flash >> 1) & 1) === 0) { const f = flashOf(pose.key, spr); if (f) { g.globalAlpha = 0.6; g.drawImage(f, 0, 0, RIG_W, RIG_H, bx, by, RIG_W, RIG_H); g.globalAlpha = 1; } this.flash--; } }   /* the hit's flash (round 52) */
        g.restore();
      } else { g.drawImage(spr.canvas, spr.sx || 0, spr.sy || 0, RIG_W, RIG_H, bx, by, RIG_W, RIG_H); if (this.flash > 0 && ((this.flash >> 1) & 1) === 0) { const f = flashOf(pose.key, spr); if (f) { g.globalAlpha = 0.6; g.drawImage(f, 0, 0, RIG_W, RIG_H, bx, by, RIG_W, RIG_H); g.globalAlpha = 1; } this.flash--; } }   /* the hit's flash (round 52) */
    }
    if (!rigged && this.wading) {
      g.fillStyle = UI.underPale;
      g.fillRect(Math.round(this.x) - 3, Math.round(this.y) + 9, 19, 2);
      g.save();
      // ...and the waterline is a hard edge like everything else. This clipped
      // at the player's UNROUNDED position while the sprite inside is drawn
      // rounded, so the cut across his legs antialiased and slid in sub-pixel
      // steps against art that cannot — and the player is at a fractional
      // position in 56.3% of frames.
      g.beginPath();
      g.rect(Math.round(this.x) - 4, Math.round(this.y) - 4, 22, 15);
      g.clip();
      // THE OUTLINE FIRST, one pixel out on every side — see `spriteOutline`.
      // It is what puts the body over the WCAG non-text floor on every ground
      // in the game; without it the median player pixel reads 1.05:1 on ash.
      g.drawImage(spriteOutline(name, flip), Math.round(this.x - 1) - 1, Math.round(this.y - 2) - 1);
      g.drawImage(sprite(name, flip), Math.round(this.x - 1), Math.round(this.y - 2));
      g.restore();
    } else if (!rigged && this.attack > 0) {
      /* THE BODY SWINGS TOO.
       *
       * An art reviewer froze the player's position and diffed 40 frames of
       * an attack: the body was PIXEL-IDENTICAL in every one. Only the thrown
       * sickle animated. The verb the player uses most, and the character
       * does not react to their own swing — which is the single clearest
       * reason the game reads as moving stickers.
       *
       * No authored art, the same way `bobbed` gets a second walk pose out of
       * a squash: the FEET STAY PLANTED and the shoulders travel. Wind back
       * away from the arc, drive through it, settle. That is three poses out
       * of one drawing, and it is how a body actually delivers a swing —
       * nothing is added at the far end that was not first taken from the
       * near one.
       *
       * `attack` counts 18 down to 0 and `swingBoxes` is live on (4, 17], so
       * the wind-up sits before the blade is dangerous and the recovery after
       * it stops being.
       */
      const lean = this.attack > SWING.live ? -1 : this.attack > SWING.follow + 3 ? 2 : this.attack > 0 ? 1 : 0;
      const ax = this.dir === 'left' ? -lean : this.dir === 'right' ? lean : 0;
      const ay = this.dir === 'up' ? -lean : this.dir === 'down' ? lean : 0;
      // ...and a facing swing also drops the shoulder a pixel at full drive,
      // so a left/right attack is not purely horizontal
      const drop = (ax !== 0 && this.attack > 8) ? 1 : 0;
      const bx = Math.round(this.x - 1), by = Math.round(this.y - 2) - lift;
      const seam = by + 9;
      g.save();
      g.beginPath(); g.rect(bx - 3, by - 4, 22, seam - (by - 4)); g.clip();
      g.drawImage(spriteOutline(name, flip), bx - 1 + ax, by - 1 + ay + drop);
      g.drawImage(sprite(name, flip), bx + ax, by + ay + drop);
      g.restore();
      g.save();
      g.beginPath(); g.rect(bx - 3, seam, 22, 20); g.clip();
      g.drawImage(spriteOutline(name, flip), bx - 1, by - 1);
      g.drawImage(sprite(name, flip), bx, by);
      g.restore();
    } else if (!rigged && breath) {
      // the body in two pieces: shoulders down a pixel, feet where they were
      const bx = Math.round(this.x - 1), by = Math.round(this.y - 2);
      const seam = by + 9;
      g.save();
      g.beginPath(); g.rect(bx - 2, by - 3, 20, seam - (by - 3)); g.clip();
      g.drawImage(spriteOutline(name, flip), bx - 1, by);
      g.drawImage(sprite(name, flip), bx, by + 1);
      g.restore();
      g.save();
      g.beginPath(); g.rect(bx - 2, seam, 20, 20); g.clip();
      g.drawImage(spriteOutline(name, flip), bx - 1, by - 1);
      g.drawImage(sprite(name, flip), bx, by);
      g.restore();
    } else if (!rigged) {
      g.drawImage(spriteOutline(name, flip),
        Math.round(this.x - 1) - 1, Math.round(this.y - 2) - lift - 1);
      g.drawImage(sprite(name, flip), Math.round(this.x - 1), Math.round(this.y - 2) - lift);
    }
    if (!rigged && this.attack > SWING.follow - 4 && this.attack <= SWING.live) {
      drawSickle(g, this.cx, this.cy, this.swingAngle(), (this.swingKind === 'thrust' ? 26 : 18), game.state.hasEmber,
        game.state.beam >= 1);
    }
      }
}

// ------------------------------------------------------------------ enemies
/* WHERE A THROWN BODY CAN STAND, NEAR WHERE IT CAME DOWN (2026-09-06).
 *
 * Vijay, from play: "lots of strange bugs with bosses teleporting around on screen". Measured in the browser, a
 * Great Beast moved 32 px in a single frame with `stun` at 40 and its own velocity at zero -- the signature of
 * `land()` below, which put a body that came down in deep water or inside a wall back at `kb.fromX/fromY`, THE
 * POINT IT WAS LAUNCHED FROM. A rattle pod pitches a body three tiles, so that is a jump of up to fifty pixels
 * across the screen in one frame, and in a fight with pods it happens over and over: the boss appears to blink.
 *
 * The invariant is worth keeping -- a Great Beast that drowns lights no mark and the run cannot be won (LS-230), and
 * a body inside a wall is stuck. None of the three beasts floats; each survives deep water by being PUT BACK, which
 * is this rule. What was wrong was never the rule but the DISTANCE. This looks outward from where it actually landed
 * for the nearest footing that is neither deep nor solid, so the body stays where the throw put it, and only falls
 * back to the launch point when there is nothing within three tiles. */
function nearFooting(game, e, deepOk) {
  const R = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]];
  /* HALF A TILE AT A TIME. A whole tile is more than it takes to clear a wall, and every pixel of it is a pop the
   * player sees: stepping by eight rather than sixteen took the worst move from 16 px to 8, measured. Six rings is
   * still the three tiles a rattle pod throws. */
  for (let r = 1; r <= 6; r++) {
    for (const [rx, ry] of R) {
      const nx = e.x + rx * r * (TS / 2), ny = e.y + ry * r * (TS / 2);
      const cx = nx + e.w / 2, cy = ny + e.h / 2;
      const t = game.tileAtPx(cx, cy);
      if (!deepOk && (t === T.WATER || t === T.SEA)) continue;
      if (game.solid(cx, cy, !!e.def.ghost, e)) continue;
      return { x: nx, y: ny };
    }
  }
  return null;
}

/* HOW LONG A CHARGE IS TELEGRAPHED, in frames. A Great Beast winds up longer
 * so the charge can be read from across the screen (the art lens, round 43).
 * Both are named because `test/checks/beasts.ts` holds the difference between
 * them, and it used to do that with its own copy of the 34 -- a control that
 * would have stayed false, and the check green, however far this drifted. */
export const WIND_BEAST = 52, WIND_CHARGER = 34;
/** THE SOW WINDS UP LONGER STILL (LS-388, decided 2026-09-13): she is the first fight, and twice in nine she ran a body to under four vigor; a longer telegraph, the same charge. */
export const WIND_SOW = 66;

/** Is the ground under a body hot enough to scorch (the field's temperature over SCORCH_LINE), in the wilds. */
export function hotUnder(game: any, body: any): boolean {
  try { if (!game.systems || !game.systems.fieldsFor || !game.area) return false; const f = game.systems.fieldsFor(game); return f.tempAt(Math.floor(body.cx / TS), Math.floor(body.cy / TS)) > SCORCH_LINE; } catch (err) { return false; }
}

export class Enemy {
  hiding: any = null;   // in a thicket, out of a fire's way (LS-176 step 6): { tx, ty, t, from }
  /* AND FOUR MORE THE CHECKER COULD NOT SEE (2026-09-06): `home` and `chargeScreen` are set when a body is
   * placed, `shed` when it drops what it carried, and `recentre` is attached to the prototype elsewhere. */
  home: any; shed: any; recentre: any;
  panic: any;   // frames in which fear may take it into the water (LS-379)
  fed: any;     // fires eaten, on a cinder (LS-293)
  dry: any;     // frames on ground with no wetness, on a mossback (LS-335)
  slam: any; lash: any;   // the old shell's slam and the bramble mother's lash (LS-303)
  lurking: any; // an eel under the surface, the player on dry land (LS-293)
  prowler: any; fleeing: any;   // the night's pack: came for the player, and leaves at dawn (LS-379)
  /* FIELDS, DECLARED.
   *
   * The migration's single biggest source of type errors — 4,646 of about
   * 5,300 — was this: properties assigned in methods rather than in the
   * constructor are invisible to the checker, so every read of one was an
   * error. Declaring them is what turns a class from a bag into a shape, and
   * a shape is what catches the defect that started this: `drawWorld`'s parts
   * object was missing `tiles` and nothing said so until it was reported from
   * play.
   *
   * They start as `any` because 44,000 lines cannot be typed in one step and
   * a wrong type is worse than a missing one. Narrowing them is the work that
   * follows, and it is worth doing in the order the bugs came from.
   */
  alt: any;
  ang: any;
  anim: any;
  dir8: any;
  headFrame: any;
  faceX: any;
  faceY: any;
  headX: any;
  headY: any;
  trampled: any;
  chargeVec: any;
  cool: any;
  cx: any;
  cy: any;
  dead: any;
  def: any;
  dir: any;
  dive: any;
  enraged: any;
  flash: any;
  h: any;
  hp: any;
  kb: any;
  lastTag: any;
  roused: any;      // the run frame a Great Beast last wound up or was struck: awake for 900 frames from it (the design lens, round 47)
  sink: any;        // frames a body that cannot float has spent in deep water (LS-242)
  lastX: any;
  lastY: any;
  level: any;
  lunge: any;
  maxHp: any;
  nestsLeft: any;
  pattern: any;
  press: any;
  reel: any;
  rest: any;
  rng: any;
  saidReel: any;
  shelled: any;
  spawnFx: any;
  stun: any;
  flipped: any;             // a shellback on its back (LS-178)
  wetHome: number[] | undefined;   // the mossback's last wet ground
  lastCause: string | undefined;   // what took its last life (state.fellBy)
  bogged: number;   // frames stuck in mud (the sow's second answer)
  grazing: number;  // frames browsing a thicket
  windFrom: number;   // the frames the current wind-up began with
  sated: number;            // frames a lesser creature backs off after biting (2026-09-26)
  alight: any;              // a bramble that is a running torch (LS-178)
  spent: any;
  state: any;
  thornSide: any;
  timer: any;
  turned: any;
  landed: any;
  lastRig: any;
  type: any;
  volley: any;
  vx: any;
  vy: any;
  w: any;
  x: any;
  y: any;


  constructor(type, x, y, rng, opts: any = {}) {
    const def = ENEMY_DEFS[type];
    this.type = type; this.def = def;
    this.w = def.size; this.h = def.size;
    this.x = x; this.y = y;
    /* THE TIER'S BONUS IS HALF A POINT A TIER, ROUNDED DOWN (2026-09-26). A point a tier, plus one at night, put a sporeling on
     * the sow's island at four life -- four sickle blows for a thing meant to be quick -- and the newcomer probe died there
     * swinging at the second rank of trash. The lesser creatures fall in two or three blows now; the beasts keep their own. */
    this.hp = def.hp + (def.beast || def.boss ? (opts.hpBonus || 0) : Math.floor((opts.hpBonus || 0) / 2));
    this.maxHp = this.hp;    // and `enraged` is the half of it, latched once
    this.dir = rng.pick(['up', 'down', 'left', 'right']);
    this.timer = rng.irange(20, 70);
    this.cool = rng.irange(60, 180);
    this.anim = rng.int(30);
    this.flash = 0; this.kb = null; this.dead = false;
    this.rng = rng;
    this.spawnFx = 22;
    this.dive = 0;
    this.pattern = opts.pattern || 0;
    this.level = opts.level || 0;
    this.alt = opts.alt || null;
    this.state = 'idle';
    clampBox(this);
  }

  update(game) {
    this.anim++;
    if (this.spawnFx > 0) { this.spawnFx--; clampBox(this); return; }
    if (this.flash > 0) this.flash--; else if (this.lastTag) this.lastTag = null;   // the duplicate-blow guard lasts as long as the flash, not as long as the creature (2026-09-17)
    // ...AND THE ONE THAT TELLS YOU WHY YOUR SWING DID NOTHING. `turned` was
    // set to 22 and decremented nowhere, so the Broodmother's whole rule
    // announced itself ONCE and then went silent: measured, 20 swings against
    // a standing nest produced one clang, one spark, one line of text and
    // nineteen swings with no sound, no feedback and no damage. A player who
    // blinked got a boss that was a silent sponge for the rest of the fight —
    // and the rule is the entire fight.
    if (this.turned > 0) this.turned--;
    if (this.landed > 0) this.landed--;
    if (this.kb && this.kb.launched) {
      /* THROWN. A blast launches every body near it the way it launches the
       * player (LS-176): four px a frame for the flight, over whatever is
       * under it, and the matter table decides the landing in `land`. */
      /* A BODY IN THE AIR IS MATTER (LS-183): what it flies into is hit and
       * reels, and a heavy one -- armoured, or built to charge -- breaks the
       * brittle stone in its way. The thrust that threw it is now a shot. */
      const kb = this.kb;
      kb.hit = kb.hit || new Set();
      for (const o of game.enemies) {
        if (o === this || o.dead || o.spawnFx > 0 || kb.hit.has(o) || !overlaps(this, o)) continue;
        kb.hit.add(o);
        o.hurt(game, 1, this.cx, this.cy, 'body:' + (kb.id || 0) + ':' + kb.t);
        game.puff(o.cx, o.cy - 4, UI.bone, 4, 'spark');
      }
      if (this.def.armoured || this.def.charges) {
        const ax = this.cx + kb.dx * 9, ay = this.cy + kb.dy * 9;
        const tx = Math.floor(ax / TS), ty = Math.floor(ay / TS);
        const t = game.tileAt(tx, ty);
        if (t !== undefined && t !== T.NEST && matterOf(t).brittle && game.breakTileAt) {
          game.breakTileAt(tx, ty);
          game.shakeFor(3, 8);
          game.sfx(this.def.armoured ? 'clang' : 'crash_stone', this.x + 8);   // a shell rings, a boar thuds (LS-214)
        }
      }
      moveAxis(game, this, kb.dx * 4, 0, true);
      moveAxis(game, this, 0, kb.dy * 4, true);
      clampBox(this);
      if (--kb.t <= 0) { this.kb = null; this.land(game, kb); }
      return;
    }
    /* FIRE TAKES WHAT STANDS IN IT (2026-09-27). Only brambles could be set alight; now a Great Beast at the edge of a
     * burning tile, or any creature made of what burns standing in one, catches -- the sow browsing a thicket someone
     * has lit is the sow's third answer. */
    if (!(this.alight > 0) && game.systems && game.systems.burning && game.area && (game.frame % 6) === 0 && (this.def.beast || traitsOf(this.type).burns)) {
      /* the tiles under the body's own box -- a beast's widened by ten px, so a fire at her flank reaches her; the sow is
       * three tiles across, and a ring round her centre tile missed a thicket burning against her side */
      const m = this.def.beast ? 10 : 0;
      const x0 = Math.floor((this.x - m) / TS), x1 = Math.floor((this.x + this.w + m - 1) / TS), y0 = Math.floor((this.y - m) / TS), y1 = Math.floor((this.y + this.h + m - 1) / TS);   // world tiles (LS-511)
      let hot = false; for (let yy = y0; yy <= y1 && !hot; yy++) for (let xx = x0; xx <= x1 && !hot; xx++) if (game.systems.burning(xx, yy)) hot = true;
      if (hot) { this.alight = this.def.beast ? 180 : 150; this.lastCause = 'fire'; game.sfx('flame', this.x + 8); for (let i = 0; i < 5; i++) game.puff(this.cx, this.cy, UI.flame, 3, 'ember'); }
    }
    /* A RUNNING TORCH. A bramble the fire has caught is kindling on legs: it
     * runs, it lights what it crosses, and it burns out (LS-178). */
    if (this.alight > 0) {
      this.alight--;
      if (this.alight % 4 === 0) game.puff(this.cx + (this.anim % 3 - 1) * 3, this.y - 2, (this.anim % 2) ? UI.gold : UI.flame, 3, 'ember');
      if (this.alight % 12 === 0 && game.systems && !this.def.beast) game.systems.ignite(game, Math.floor(this.cx / TS), Math.floor(this.cy / TS));   // a beast does not light the ground under it, or one torch and a retreat solved her (LS-228)
      if (this.def.beast) { if (this.alight % 30 === 0) { this.lastCause = 'fire'; this.hp -= 1; this.flash = 6; game.sfx('burn', this.x + 8); /* a burning beast was silent (round 53's music lens) */ if (this.hp <= 0) { this.dead = true; game.onEnemyKilled(this); return; } } }   // a beast burns, and is hurt by it, and does not burn away
      else if (this.alight === 0) { this.dead = true; for (let i = 0; i < 6; i++) game.puff(this.cx, this.cy, UI.flame, 4, 'ember'); return; }
      if (--this.timer <= 0) { this.timer = this.rng.irange(15, 40); this.dir = this.rng.chance(0.7) ? this.faceToward(game) : this.rng.pick(['up', 'down', 'left', 'right']); }
      const [ddx, ddy] = DIRVEC[this.dir];
      if (moveAxis(game, this, ddx * this.def.speed * 1.7, ddy * this.def.speed * 1.7)) this.dir = this.rng.pick(['up', 'down', 'left', 'right']);
      clampBox(this);
      return;
    }
    if (this.bogged > 0) { this.bogged--; if (this.bogged % 9 === 0) game.puff(this.cx + (this.bogged % 18 ? 7 : -7), this.cy + this.h / 2 - 2, '#5a4128', 2, 'dust'); if (this.bogged === 0) this.stun = 0; }   // struggling in the mud
    if (this.stun > 0) { this.stun--; if (this.stun % 8 === 0) game.puff(this.cx, this.y - 2, UI.bone, 2, 'spark'); clampBox(this); return; }
    /* ON ITS BACK. A thrown shellback lands flipped: no armour, no movement,
     * twice the hurt, for two seconds (LS-178). */
    if (this.flipped > 0) {
      this.flipped--; if (this.flipped % 10 === 0) game.puff(this.cx + (this.anim % 2 ? 4 : -4), this.cy - 4, UI.bone, 2, 'spark');
      /* ...AND ON HIS BACK BY THE DEEP HE GOES IN (2026-09-27, the old shell's third answer; Vijay's call: drowning is one).
       * A Great Beast turned over within a tile of deep water slides into it, and there he drowns while he is on his back
       * -- a life every sixth of a second -- until he rights himself and hauls out. */
      if (this.def.beast && game.area) {
        const deepAt = (x: number, y: number) => { const t = game.tileAtPx(x, y); return t === T.WATER || t === T.SEA; };
        if (deepAt(this.cx, this.cy)) {
          if (this.flipped % 10 === 0) { this.lastCause = 'drown'; this.hp -= 1; this.flash = 6; game.puff(this.cx, this.cy, UI.underPale, 3, 'splash'); if (this.hp <= 0) { this.dead = true; game.onEnemyKilled(this); return; } }
          if (this.flipped === 0) { const spot = nearFooting(game, this, false); if (spot) { this.x = spot.x; this.y = spot.y; } }
        } else {
          for (const [ax, ay] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) if (deepAt(this.cx + ax * (this.w / 2 + 8), this.cy + ay * (this.h / 2 + 8))) { this.x += ax * 1.2; this.y += ay * 1.2; if (this.flipped % 8 === 0) game.puff(this.cx, this.cy + 6, UI.foamLit, 2, 'splash'); break; }
        }
      }
      clampBox(this); return;
    }
    /* REELING. Any hit staggers a creature that is not a boss and not
     * armoured: it stands for a moment and its touch does not hurt (LS-178). */
    /* A BEAST'S WINDOW CLOSES TOO. The Warden's own tick counted a boss's reel down and left with LS-226, so a
     * thrown sickle opened a window (every blow double) that never shut: any beast died in four seconds to a
     * held button (the design lens, 2026-09-05). A beast keeps moving while it reels -- it is too big to stand
     * still for a knock -- but the count runs. */
    if (this.reel > 0 && this.def.boss) this.reel--;
    if (this.reel > 0 && !this.def.boss) { this.reel--; clampBox(this); return; }
    if (this.kb) {
      // Knockback used to move with GHOST collision, whose solid set leaves
      // out blocks, idols, pedestals, locks and doors — so a hit could shove
      // an enemy inside scenery that its normal movement cannot get back out
      // of. One in 48,000 in the wilds, but a barred sigil room whose last
      // enemy becomes unreachable is an unwinnable run. Remember where it was
      // standing and put it back if the shove lands somewhere it could not
      // have walked.
      const wasX = this.x, wasY = this.y;
      moveAxis(game, this, this.kb.dx * 3, 0, true);
      moveAxis(game, this, 0, this.kb.dy * 3, true);
      this.keepToHome();   // a beast is not shoved out of its territory (the design lens, round 47)
      clampBox(this);
      /* ON THE BOX, NOT THE CENTRE (2026-09-17). A body left straddling a wall has its centre in the open, so this
       * net passed it and the creature was stuck where it stood -- the very centre-only fault that `bodyPoints`
       * exists to answer, and which was measured and fixed for the player rounds ago. */
      if (hits(game, this, this.x, this.y, false)) { this.x = wasX; this.y = wasY; }
      if (--this.kb.t <= 0) this.kb = null;
      clampBox(this);
      return;
    }

    /* IN THE THICKET (LS-176 step 6). A bramble that runs from a fire and
     * finds shelter beside it -- a thorn, a tree -- goes in and waits: still,
     * unseen by the sickle, until the fire near it is out and a moment has
     * passed, or until the thicket itself catches, which is the one thing
     * that flushes it (`hurt`). It comes out where it went in. */
    if (this.hiding) {
      const h = this.hiding; h.t++;
      const sys = game.systems;
      const near = !!(sys && sys.fires.some((f) => Math.hypot(f.x - h.tx, f.y - h.ty) < 4));
      /* A SLEEPER DOES NOT COME OUT ON ITS OWN (2026-09-10, LS-302). One that
       * ran INTO a thicket to get away from a fire leaves again once the fire
       * is out, which is the rule above; one that was in there when the world
       * was made is asleep, and only fire finds it (`struck` takes nothing but
       * a fire-tagged blow from a hidden body). The thicket rustles while it
       * is in there, because a thicket that says nothing is a thicket nobody
       * burns -- and there is no line of text in this game to say so. */
      /* ...and one that went in out of the rain stays while it falls, a minute at most (LS-528) */
      if (h.rain && !near && h.t < 3600 && game.systems && game.systems.rain(game) > 0.35) { if (h.t % 90 === 0) game.puff(this.cx, this.cy - 2, UI.leaf, 1, 'leaf'); return; }
      if (h.sleep) {
        /* FIRE IN THE THICKET IS WHAT WAKES IT, whatever it is. Relying on a
         * fire-tagged BLOW only flushes species that carry `Burns` -- a wisp
         * feels nothing and would have slept through the thicket burning down
         * around it. A fire beside the tile it is in is enough. */
        if (near) { this.flush(game); return; }
        if (h.t % 90 === 0) game.puff(this.cx, this.cy - 2, UI.leaf, 2, 'leaf');
        return;
      }
      if ((!near && h.t > 60) || h.t > 900) {
        const [fx, fy] = h.from;
        const wx = this.x, wy = this.y;
        this.x = fx; this.y = fy;
        if (typeof this.recentre === 'function') this.recentre(); else clampBox(this);
        if (game.solid(this.cx, this.cy, false, this)) { this.x = wx; this.y = wy; if (typeof this.recentre === 'function') this.recentre(); else clampBox(this); h.t = 61; }   // the way out is blocked: wait
        else { this.hiding = null; game.puff(this.cx, this.cy, UI.leaf, 3, 'leaf'); }
      }
      return;
    }
    if (this.panic > 0) this.panic--;
    /* A FLOATING BODY THE FRIGHT LEFT IN A LAKE COMES ASHORE (2026-09-19). Deep water is not a wall to a kind that
     * floats and the sink rule spares it, so a panic that ran it into a lake left it there, alive and unreachable,
     * for the rest of the run. When the fright passes it is set on the nearest bank, the way a landed beast is. */
    if (!this.panic && !this.dead && !this.def.aquatic && !this.def.ghost && traitsOf(this.type).floats && game.isSwimAt && game.isSwimAt(this.cx, this.cy)) {
      const spot = nearFooting(game, this, false);
      if (spot) { this.x = spot.x; this.y = spot.y; clampBox(this); }
    }
    /* THE CINDER EATS THE FIRE IT STANDS IN (LS-293): the fire goes out, the beetle takes a life and its embers
     * burn brighter, up to six. Fire on the ground is a thing it wants, so a ring of it is a lure and a lit hedge
     * is a meal; and a fed cinder is a harder thing to kill than a fresh one. */
    /* ...the fire under its own feet: fires are kept by world tile (LS-511), so there is no other screen's fire at the same numbers. */
    if (traitsOf(this.type).eatsFire && game.systems) {
      const tx = Math.floor(this.cx / TS), ty = Math.floor(this.cy / TS);
      if (game.systems.burning(tx, ty) && game.systems.smother(game, tx, ty)) {
        this.fed = (this.fed || 0) + 1;
        if (this.maxHp < 6) { this.maxHp++; this.hp = Math.min(this.maxHp, this.hp + 1); }
        game.puff(this.cx, this.cy - 2, UI.flame, 6, 'ember'); game.sfx('flame', this.x + 8);
      }
    }
    /* THE MOSSBACK GOES TO WET GROUND AND THE DRY KILLS IT (LS-335, 2026-09-16): a pull toward the wettest of the
     * eight cells round it, and a life lost every two seconds standing on ground with no wetness at all -- so it
     * hugs the fen's pools and the shore, and a scorched country is death to it in seconds. */
    if (this.def.wet && game.systems && game.systems.fieldsFor) {
      const f = game.systems.fieldsFor(game); const tx = Math.floor(this.cx / TS), ty = Math.floor(this.cy / TS);
      let bx = 0, by = 0, best = f.wetAt(tx, ty);
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]]) { const w = f.wetAt(tx + dx, ty + dy); if (w > best + 0.05) { best = w; bx = dx; by = dy; } }
      /* THE PULL MOVES IT (2026-09-17): this added to `vx`/`vy`, which the walker never reads (it steps through moveAxis)
       * and which start undefined -- so the only effect was a permanent NaN on the body and no pull at all. */
      /* ...AND IT REMEMBERS THE WATER (2026-09-26). The pull sees one tile out, so a mossback that wandered two onto dry
       * ground found no gradient and died where it stood -- three in eleven on their own countries once the fixture's
       * player stopped dying first. The last wet ground it stood on is where it heads, at its full pace, once it dries. */
      if (best > 0.1 && f.wetAt(tx, ty) > 0.1) this.wetHome = [this.cx, this.cy];
      else if (f.wetAt(tx, ty) <= 0.02 && this.wetHome && !(bx || by)) { const hx = this.wetHome[0] - this.cx, hy = this.wetHome[1] - this.cy, hl = Math.hypot(hx, hy); if (hl > 1) { moveAxis(game, this, hx / hl * this.def.speed, 0, false); moveAxis(game, this, 0, hy / hl * this.def.speed, false); this.dir = this.faceTo(this.wetHome[0], this.wetHome[1]); this.timer = Math.max(this.timer || 0, 20); } }   /* ...and its own walk turns home too, or the walk undid the pull step for step */
      if (bx || by) { const len = Math.hypot(bx, by), sp = this.def.speed * 0.35; if (bx) moveAxis(game, this, bx / len * sp, 0, false); if (by) moveAxis(game, this, 0, by / len * sp, false); }
      if (f.wetAt(tx, ty) <= 0.02) { this.dry = (this.dry || 0) + 1; if (this.dry % 120 === 0) { this.hp -= 1; this.flash = 6; if (this.hp <= 0) { this.dead = true; game.onEnemyKilled(this); return; } } } else this.dry = 0;
    }
    // what it fears and what draws it, before what it was doing (matter.ts)
    if (game.systems && !this.def.boss) {
      const [ux, uy] = game.systems.urgeFor(game, this);
      if (Math.hypot(ux, uy) > 0.4 && traitsOf(this.type).fearsFire >= 1) {
        // a thicket beside it is where it goes
        const tx = Math.floor(this.cx / TS), ty = Math.floor(this.cy / TS);   // world tiles (LS-511)
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const v = game.tileAt(tx + dx, ty + dy);
          if (v === undefined || !matterOf(v).shelter || v === T.NEST || game.systems.burning(tx + dx, ty + dy)) continue;
          this.hiding = { tx: tx + dx, ty: ty + dy, t: 0, from: [this.x, this.y] };
          // placed by its own centre, whatever the sprite's box is anchored to
          this.x += (tx + dx) * TS + TS / 2 - this.cx; this.y += (ty + dy) * TS + TS / 2 - this.cy;
          if (typeof this.recentre === 'function') this.recentre(); else clampBox(this);
          game.puff(this.cx, this.cy, UI.leaf, 4, 'leaf');
          return;
        }
      }
      /* A BODY DRIVEN BY FEAR RUNS WHERE IT IS DRIVEN, the lake included
       * (LS-379). Deep water is a wall to a creature, so a charger fleeing a
       * fire on a shore stopped at the waterline and burned. For half a second
       * after a fright it may enter the water instead -- and what cannot float
       * then goes under, which is the rule systems.ts already keeps: "luring
       * either into the deep is a way to fight it". Fire on one side and a
       * lake on the other is now that lure. */
      if (Math.hypot(ux, uy) > 0.4 && !traitsOf(this.type).drawnToLight) this.panic = 30;
      if (ux) moveAxis(game, this, ux, 0, false);
      if (uy) moveAxis(game, this, 0, uy, false);
      // a creature fleeing a fire or drawn to a light is doing that, not chasing
      if (Math.hypot(ux, uy) > 0.4) { clampBox(this); return; }
    }
    if ((this.friendUntil || 0) > (game.runFrames || 0)) { this.friendStep(game); clampBox(this); return; }   // fed from the hand, it is yours (LS-521)
    if (this.sleepStep(game)) { clampBox(this); return; }   // at night the day's creatures sleep (LS-526)
    /* a kind that fears you bolts when you come near (LS-522) */
    if (!this.def.boss && !this.prowler && game.state && game.player && standingOf(game.state, this.type) <= FEARED && Math.hypot(game.player.cx - this.cx, game.player.cy - this.cy) < SHY_REACH) { this.routed = Math.max(this.routed, 30); this.routFrom = [game.player.cx, game.player.cy]; }
    if (this.routed > 0 && !this.def.boss) { this.routStep(game); clampBox(this); return; }   // wounded, it bolts (LS-520)
    this.mend(game);
    if (this.needsStep(game)) { clampBox(this); return; }   // thirst, and cover from the rain (LS-528)
    if (this.foodStep(game)) { clampBox(this); return; }   // its own hunger, when the player is not the business (LS-519)
    if (this.def.charges) this.updateCharger(game);
    else if (this.type === 'wisp' || this.type === 'gloam') this.updateWisp(game);   // the gloam drifts the way a wisp does (LS-293)
    else this.updateWalker(game);
    clampBox(this);
    // A CHARGE THAT CONNECTS HURTS MORE THAN LEANING ON YOU. Pattern 0's
    // lunge is now punishable — it ends winded — so it has to be worth
    // dodging: the commitment costs the Warden a window and costs the player
    // two hearts instead of one. Threat and tell, in the same move.
    // Collision probes are inset from the sprite; even a blocked neighbor can
    // overlap the player's edge. A protected roster cannot deal contact damage.
    /* ...and a creature outside a closed chunk cannot bite across its edge into it (LS-511, game.closeTheGround) */
    const protectedContact = !!(game.closedTo && game.closedTo(this, game.player.cx, game.player.cy));
    /* A BOAT'S FREEBOARD (LS-386, 2026-09-12): a body aboard a boat held against a shore was bitten to death by a
     * shellback standing at the water's edge, and neither bot nor player can fight from a hull. A land creature does
     * not reach a body in a boat; an eel does (it is aquatic).
     * ...AND A RAFT IS A HULL TOO (2026-09-26). "A raft rides low enough for anything", and since a body afloat cannot swing,
     * the newcomer probe lost eight of its ten vigor to one shellback on a beach it was paddling along, then died stepping
     * ashore. Being bitten where you cannot answer is not a fight. Anything afloat is out of a land creature's reach. */
    const freeboard = !!(game.aboard && game.state && game.state.boat && !this.def.aquatic);
    if (this.sated > 0) this.sated--;
    if (!protectedContact && !freeboard && !this.peaceable(game) && overlaps(this, game.player) && !game.player.submerged && !this.lurking && !(this.reel > 0 && !this.def.boss) && !(this.stun > 0) && !(this.flipped > 0) && !(this.sated > 0)) {
      // A CHARGE THAT CONNECTS HURTS LIKE ONE. Pattern 0's lunge bites for two
      // and pattern 1's charge — the same commitment, wound up for thirty
      // frames and telegraphed — bit for one, so the pattern was survivable
      // 91% of the time even after a chip hit, against a bar of 85%. A boss
      // nobody can lose to is scenery, and this is the move the whole pattern
      // is built around.
      /* THE GLOAM'S DUST IS THE END OF YOUR LIGHT (LS-293). It does not bite: it lands on a torch that is lit and
       * puts it out, and the landing is the end of it; on a body carrying no fire it settles for nothing and goes. */
      if (this.type === 'gloam') {
        if (game.player.lit > 0) { game.player.lit = 0; game.sfx('flame', this.x + 8); for (let i = 0; i < 6; i++) game.puff(this.cx, this.cy, UI.linen, 3, 'ember'); this.dead = true; }
        return;
      }
      const committed = this.lunge > 0 || this.state === 'charge';
      const stocked = this.def.boss ? Math.floor(Math.max(0, game.state.maxHp - 14) / 3) : 0;
      // AN IDLE BEAST BRUSHES FOR ONE (the design lens, round 42): half a masher's losses to the sow were its idle body, not its charge; the charge keeps its three and its wound
      // ...and a STUNNED beast does not bite at all (the design lens, round 45): the punish window cost a point a touch, level with the idle brush
      const bite = this.def.beast && !committed ? (this.stun > 0 ? 0 : 1) : this.def.touch + (committed ? 1 : 0) + stocked;
      const hpBefore = game.state.hp;
      if (bite > 0) game.player.hurt(game, bite, this.cx, this.cy);
      if (!this.def.beast && game.state.hp < hpBefore) { this.sated = 75; if (!this.def.wet) { this.timer = 75; this.dir = this.faceAway(game); } }   /* a wet kind keeps to its wet ground */   /* turned away, and held on that heading by the walker's own timer; its urges still steer it */
      // A dodge or plate block cannot open (or refresh) a wound.
      if (game.state.hp < hpBefore && committed && this.def.beast) { if (!wearing(game.state, 'hide')) afflict(game.player, 'Bleeding', 720); else if (game.gearSaved) game.gearSaved('hide'); }
    }
  }

  /* A COMPANION (LS-521). It keeps within three tiles of the body, goes for any other lesser creature that comes within
   * five tiles of it and bites it, and bites nothing of yours; a gold mote rises off it now and then. */
  /* SLEEP (LS-526, after Caves of Qud's sleeping monsters). At night the day's creatures lie down where they are, once
   * the body is more than five tiles off -- the Great Beasts too, unless something has roused them. A sleeper does not
   * move, bite or notice; it wakes at dawn, when the body comes within a tile and a half, or when it is struck, and a
   * blow on a sleeper lands double (Enemy.hurt). A pale mote rises off it now and then. */
  asleep = false;
  sleepStep(game): boolean {
    const night = !!(game.systems && game.systems.clock && game.systems.clock(game).night);
    const kinds = !this.prowler && !this.def.night && !this.def.dusk && !this.def.aquatic && this.type !== 'wisp' && this.type !== 'cinder' && !((this.friendUntil || 0) > (game.runFrames || 0));
    const p = game.player, d = p ? Math.hypot(p.cx - this.cx, p.cy - this.cy) : 1e9;
    if (!night || !kinds || (this.provokedUntil || 0) > (game.runFrames || 0) || (this.def.beast && game.beastRoused && game.beastRoused(this))) { this.asleep = false; return false; }
    if (!this.asleep) { if (d > 80 && !(this.routed > 0) && !(this.kb && this.kb.launched)) this.asleep = true; else return false; }
    if (d < 24) { this.asleep = false; this.timer = 0; return false; }   // the body is on it: it wakes
    if ((game.frame || 0) % 90 === Math.abs(Math.floor(this.x)) % 90) game.puff(this.cx + 3, this.cy - 10, '#e8ecf4', 1, 'mote');
    return true;
  }
  friendUntil = 0; friendCool = 0; provokedUntil = 0; hitByPlayerAt = -1e9;
  /** Its kind is at peace with you and nothing you did has provoked it (LS-522). */
  peaceable(game): boolean { return !this.def.boss && !this.prowler && !!game.state && standingOf(game.state, this.type) >= PEACEABLE && !((this.provokedUntil || 0) > (game.runFrames || 0)); }
  friendStep(game) {
    const p = game.player; if (!p) return;
    if (this.friendCool > 0) this.friendCool--;
    if ((game.frame || 0) % 50 === 0) game.puff(this.cx, this.cy - 10, UI.gold, 1, 'mote');
    let foe: any = null, fd = GUARD_REACH;
    for (const o of game.enemies || []) {
      if (o === this || o.dead || o.def.boss || o.hiding || o.spawnFx > 0 || (o.friendUntil || 0) > (game.runFrames || 0)) continue;
      const d = Math.hypot(o.cx - p.cx, o.cy - p.cy); if (d < fd) { fd = d; foe = o; }
    }
    const [gx, gy] = foe ? [foe.cx, foe.cy] : [p.cx, p.cy];
    const dx = gx - this.cx, dy = gy - this.cy, d = Math.hypot(dx, dy) || 1;
    if (foe && d <= (this.w + foe.w) / 2 + 2) {
      if (this.friendCool <= 0) { this.friendCool = 40; foe.hurt(game, 1, this.cx, this.cy, 'bite-friend'); }
      return;
    }
    if (!foe && d < 40) return;   // at heel
    this.dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
    const sp = Math.max(this.def.speed * (foe ? 1.2 : 1.1), !foe && d > 56 ? 1.5 : 0) * speedMul(this);   // it trots to catch up with a body on the move
    moveAxis(game, this, dx / d * sp, 0, !!this.def.ghost); moveAxis(game, this, 0, dy / d * sp, !!this.def.ghost);
  }
  /* MORALE (LS-520). A routed body runs from what hurt it; beside a thicket it goes into it. A wounded one with nothing
   * near mends a point every ten seconds. */
  routed = 0; routFrom: [number, number] | null = null; mendAt = 0;
  routStep(game) {
    this.routed--;
    const tx = Math.floor(this.cx / TS), ty = Math.floor(this.cy / TS);
    if (game.systems && !this.def.ghost) for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const v = game.tileAt(tx + dx, ty + dy);
      if (v === undefined || !matterOf(v).shelter || v === T.NEST || game.systems.burning(tx + dx, ty + dy)) continue;
      this.hiding = { tx: tx + dx, ty: ty + dy, t: 0, from: [this.x, this.y] };
      this.x += (tx + dx) * TS + TS / 2 - this.cx; this.y += (ty + dy) * TS + TS / 2 - this.cy;
      if (typeof this.recentre === 'function') this.recentre(); else clampBox(this);
      game.puff(this.cx, this.cy, UI.leaf, 4, 'leaf'); this.routed = 0;
      return;
    }
    const [fx, fy] = this.routFrom || [game.player.cx, game.player.cy];
    let ax = this.cx - fx, ay = this.cy - fy; const l = Math.hypot(ax, ay) || 1; ax /= l; ay /= l;
    const sp = this.def.speed * 1.3 * speedMul(this);
    const bx = moveAxis(game, this, ax * sp, 0, !!this.def.ghost), by = moveAxis(game, this, 0, ay * sp, !!this.def.ghost);
    if (bx && by) { if (!moveAxis(game, this, -ay * sp, 0, !!this.def.ghost)) this.routFrom = [this.cx + ay * 40, this.cy - ax * 40]; }   // cornered: along the wall
    this.dir = Math.abs(ax) > Math.abs(ay) ? (ax > 0 ? 'right' : 'left') : (ay > 0 ? 'down' : 'up');
    if (this.routed % 12 === 0) game.puff(this.cx, this.cy + 6, '#6a2a22', 1, 'fleck');   // a wounded body leaves a spot of blood
  }
  mend(game) {
    if (this.def.boss || this.hp >= this.maxHp || this.hp <= 0 || !game.player) return;
    if (Math.hypot(game.player.cx - this.cx, game.player.cy - this.cy) < 160) { this.mendAt = game.runFrames || 0; return; }
    if ((game.runFrames || 0) - this.mendAt >= MEND_FRAMES) { this.hp++; this.mendAt = game.runFrames || 0; }
  }
  /* THIRST AND WEATHER (LS-528). A creature that eats also drinks: thirsty, with the player not at hand, it walks to the
   * nearest water's edge in sight and drinks; in a downpour it takes cover -- into a thicket or under a tree beside it,
   * or toward the nearest one in sight -- and stays while the rain falls. */
  thirst: number | undefined; drinking = 0; waterAt: [number, number] | null = null;
  needsStep(game): boolean {
    if (!DIETS[this.type] || this.def.boss || this.prowler || this.hiding || this.stun > 0 || this.flipped > 0 || (this.kb && this.kb.launched) || !game.player || !game.systems) return false;
    const p = game.player, near = Math.hypot(p.cx - this.cx, p.cy - this.cy) < MIND_PLAYER;
    if (this.thirst === undefined) this.thirst = seedHunger(this) * 0.8;
    this.thirst = Math.min(1, this.thirst + 1 / THIRST_FRAMES);
    if (near) { this.drinking = 0; return false; }
    const tx = Math.floor(this.cx / TS), ty = Math.floor(this.cy / TS);
    /* cover from the rain */
    if (!this.def.ghost && !this.def.aquatic && !this.def.wet && game.systems.rain(game) > DOWNPOUR) {
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const v = game.tileAt(tx + dx, ty + dy);
        if (v === undefined || !matterOf(v).shelter || v === T.NEST || game.systems.burning(tx + dx, ty + dy)) continue;
        this.hiding = { tx: tx + dx, ty: ty + dy, t: 0, from: [this.x, this.y], rain: true };
        this.x += (tx + dx) * TS + TS / 2 - this.cx; this.y += (ty + dy) * TS + TS / 2 - this.cy;
        if (typeof this.recentre === 'function') this.recentre(); else clampBox(this);
        game.puff(this.cx, this.cy, UI.leaf, 3, 'leaf');
        return true;
      }
      const cover = this.nearestTile(game, (v: number, x: number, y: number) => !!matterOf(v).shelter && v !== T.NEST && !game.systems.burning(x, y), SHELTER_SIGHT);
      if (cover && this.walkTo(game, cover[0] * TS + 8, cover[1] * TS + 8, 1.2)) return true;
    }
    /* thirst */
    if (this.drinking > 0) { if (--this.drinking === 0) this.thirst = 0; else if (this.drinking % 30 === 0) game.puff(this.cx, this.cy + 6, '#bcd8e6', 1, 'drip'); return true; }
    if (this.thirst < THIRSTY || this.def.aquatic || this.def.wet) return false;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const v = game.tileAt(tx + dx, ty + dy); if (v === T.WATER || v === T.SHALLOW) { this.drinking = DRINK_FRAMES; return true; } }
    if (!this.waterAt || (game.frame || 0) % 60 === 0) this.waterAt = this.nearestTile(game, (v: number) => v === T.WATER || v === T.SHALLOW, DRINK_SIGHT);
    if (this.waterAt && this.walkTo(game, this.waterAt[0] * TS + 8, this.waterAt[1] * TS + 8, 0.9)) return true;
    return false;
  }
  /** The nearest tile within `reach` that `ok` accepts, ring by ring, or null. */
  nearestTile(game, ok: (v: number, x: number, y: number) => boolean, reach: number): [number, number] | null {
    const cx = Math.floor(this.cx / TS), cy = Math.floor(this.cy / TS);
    for (let r = 1; r <= reach; r++) for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
      const v = game.tileAt(cx + dx, cy + dy); if (v !== undefined && ok(v, cx + dx, cy + dy)) return [cx + dx, cy + dy];
    }
    return null;
  }
  /** A step toward (x, y) at `pace` times its own speed; false when it cannot step at all (walled off). */
  walkTo(game, x: number, y: number, pace: number): boolean {
    const dx = x - this.cx, dy = y - this.cy, d = Math.hypot(dx, dy) || 1, sp = this.def.speed * pace * speedMul(this);
    this.dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
    const bx = moveAxis(game, this, dx / d * sp, 0, !!this.def.ghost), by = moveAxis(game, this, 0, dy / d * sp, !!this.def.ghost);
    return !(bx && by);
  }
  /* HUNGER (LS-519, sim/food.ts). Every kind with a diet fills a hunger over three minutes; hungry, with the player not
   * within a few tiles, it goes for the nearest thing it eats in sight -- a crop of meadow, a smaller kind it hunts,
   * remains -- and eats it. True while it is about that business this frame (the ordinary walk is skipped). */
  hunger: number | undefined; eating = 0; meal: any = null; mealAt = 0; biteCool = 0;
  foodStep(game): boolean {
    const diet = DIETS[this.type];
    if (!diet || this.def.boss || this.prowler || this.hiding || this.fleeing || this.stun > 0 || this.flipped > 0 || (this.kb && this.kb.launched) || !game.player) return false;
    if (this.hunger === undefined) this.hunger = seedHunger(this);
    this.hunger = Math.min(1, this.hunger + 1 / HUNGER_FRAMES);
    if (this.biteCool > 0) this.biteCool--;
    const p = game.player, near = Math.hypot(p.cx - this.cx, p.cy - this.cy) < MIND_PLAYER;
    if (this.eating > 0) {
      if (near && this.hunger < 0.95) { this.eating = 0; this.meal = null; return false; }   // interrupted
      if (--this.eating === 0) this.finishMeal(game);
      else if (this.eating % 24 === 0) game.puff(this.cx, this.cy + 4, this.meal && this.meal.kind === 'grass' ? UI.leaf : UI.bone, 1, this.meal && this.meal.kind === 'grass' ? 'leaf' : 'fleck');
      return true;
    }
    if (this.hunger < HUNGRY || (near && this.hunger < 0.95)) { this.meal = null; return false; }
    if (!this.meal || !mealLive(game, this.meal, diet) || (game.frame || 0) - this.mealAt > 45) { this.meal = findMeal(game, this, diet); this.mealAt = game.frame || 0; }
    if (!this.meal) return false;
    const [mx, my] = mealAt(this.meal), dx = mx - this.cx, dy = my - this.cy, d = Math.hypot(dx, dy);
    const reach = this.meal.kind === 'prey' ? (this.w + this.meal.e.w) / 2 + 2 : 9;
    if (d <= reach) {
      if (this.meal.kind === 'prey') {
        const o = this.meal.e;
        if (this.biteCool <= 0) {
          this.biteCool = 40; o.huntedAt = game.runFrames; o.hurt(game, 1, this.cx, this.cy, 'bite');
          /* the prey bolts away from what bit it */
          const ax = o.cx - this.cx, ay = o.cy - this.cy; o.dir = Math.abs(ax) > Math.abs(ay) ? (ax > 0 ? 'right' : 'left') : (ay > 0 ? 'down' : 'up'); o.timer = 70;
          if (o.dead) { const r = (game.remains || []).find((q: any) => q.of === o); if (r) { this.meal = { kind: 'remains', r }; this.eating = BITE_FRAMES; } }
        }
        return true;
      }
      this.eating = this.meal.kind === 'grass' ? GRAZE_FRAMES : BITE_FRAMES;
      return true;
    }
    this.dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
    const sp = this.def.speed * (this.meal.kind === 'prey' ? 1.1 : 0.8) * speedMul(this);
    const bx = moveAxis(game, this, dx / d * sp, 0, !!this.def.ghost), by = moveAxis(game, this, 0, dy / d * sp, !!this.def.ghost);
    if (bx && by) { this.meal = null; this.mealAt = game.frame || 0; return false; }   // walled off from it: back to its own business for a while
    return true;
  }
  /** The meal is eaten: grass is cropped, remains lose a bite, and the hunger goes. */
  finishMeal(game) {
    const m = this.meal; this.meal = null; if (!m) return;
    if (m.kind === 'bait') { m.it.dead = true; this.hunger = 0; return; }   // the bait is eaten
    if (m.kind === 'grass') { if (game.systems && game.systems.layTuft(game, m.tx, m.ty)) this.hunger = Math.max(0, (this.hunger || 0) - GRAZE_MEAL); return; }
    if (m.kind === 'remains') {
      m.r.bites--; this.hunger = 0;
      if (this.type === 'sporeling' && game.systems) { const f = game.systems.fieldsFor(game); f.bump('spores', Math.floor(m.r.x / TS), Math.floor(m.r.y / TS), 0.5); }   // what a sporeling eats goes back to the ground
    }
  }

  /** TRACKING (LS-527): the way toward the body when it is within AWARE px (or this is the night's hunter, which has
   *  your scent from the start); past that, the way the scent grows stronger, if it stands on a trail; else its own way. */
  seekDir(game): string {
    const p = game.player;
    if (this.prowler || !p || Math.hypot(p.cx - this.cx, p.cy - this.cy) <= AWARE || !game.systems) return this.faceToward(game);
    const f = game.systems.fieldsFor(game), tx = Math.floor(this.cx / TS), ty = Math.floor(this.cy / TS);
    const here = f.at('scent', tx, ty);
    let best: string | null = null, bv = Math.max(here, SCENT_TRAIL);
    for (const [d, dx, dy] of [['right', 1, 0], ['left', -1, 0], ['down', 0, 1], ['up', 0, -1]] as Array<[string, number, number]>) {
      /* two tiles out, so a trail a step wide is read the way it runs */
      const v = Math.max(f.at('scent', tx + dx, ty + dy), f.at('scent', tx + dx * 2, ty + dy * 2));
      if (v > bv) { bv = v; best = d; }
    }
    return best || this.rng.pick(['up', 'down', 'left', 'right']);
  }
  /** Out of sight of the body and standing on its trail (LS-527)? */
  onTrail(game): boolean {
    const p = game.player; if (!p || this.prowler || this.peaceable(game) || !game.systems || Math.hypot(p.cx - this.cx, p.cy - this.cy) <= AWARE) return false;
    return game.systems.fieldsFor(game).at('scent', Math.floor(this.cx / TS), Math.floor(this.cy / TS)) >= SCENT_TRAIL;
  }
  /** The way that is away from the player: a prowler at dawn. */
  faceAway(game) {
    const t = this.faceToward(game);
    return ({ up: 'down', down: 'up', left: 'right', right: 'left' } as any)[t] || 'down';
  }

  faceToward(game) { return this.faceTo(game.player.cx, game.player.cy); }
  /** Is the straight run from here to (tx, ty), along the heading this body faces, open to it? Asked the way the charge
   *  itself moves -- the same collision, a step at a time -- on the body's own position, which is put back after: sampled
   *  points disagreed with the real box (the sow's flank already brushed the rock row she slid along). Water and the
   *  leash are the charge's own business; only what would stop it counts. */
  laneClear(game, tx: number, ty: number): boolean {
    const [dx, dy] = DIRVEC[this.dir]; const along = dx ? (tx - this.cx) * dx : (ty - this.cy) * dy;
    if (along <= 0) return true;
    const x0 = this.x, y0 = this.y; let clear = true;
    for (let t = 0; t < along - 8; t += 3.1) if (hits(game, this, this.x + dx * 3.1, this.y + dy * 3.1, false)) { clear = false; break; } else { this.x += dx * 3.1; this.y += dy * 3.1; }
    this.x = x0; this.y = y0;
    return clear;
  }
  /** Which way to walk to get round a wall toward (tx, ty): toward the nearest row (or column) beside this one, within
   *  five tiles, from which the run to it is open; failing that, the other axis toward it. A sow pacing beside a column of
   *  trees used to pick a side at random and never found the gap two tiles down. */
  stepRound(game, tx: number, ty: number) {
    const [dx] = DIRVEC[this.dir], ddx = tx - this.cx, ddy = ty - this.cy, y0 = this.y, x0 = this.x;
    let best: string | null = null;
    for (let k = 1; k <= 5 && !best; k++) for (const sgn of [1, -1]) {
      if (dx) this.y = y0 + sgn * k * TS; else this.x = x0 + sgn * k * TS;
      const clear = !hits(game, this, this.x, this.y, false) && this.laneClear(game, dx ? tx : this.cx + (tx - (x0 + this.w / 2)), dx ? this.cy + (ty - (y0 + this.h / 2)) : ty);
      this.x = x0; this.y = y0;
      if (clear) { best = dx ? (sgn > 0 ? 'down' : 'up') : (sgn > 0 ? 'right' : 'left'); break; }
    }
    return best || (dx ? (ddy > 0 ? 'down' : 'up') : (ddx > 0 ? 'right' : 'left'));
  }


  /** The way toward a point: the player, or -- for a beast with its own
   *  business -- the nearest of the small kin (LS-379, creature agendas). */
  faceTo(px: number, py: number) {
    const ddx = px - this.cx, ddy = py - this.cy;
    const ax = Math.abs(ddx), ay = Math.abs(ddy);
    const horiz = ddx < 0 ? 'left' : 'right', vert = ddy < 0 ? 'up' : 'down';
    /* ...ON THE DIAGONAL WHEN THE PLAYER IS ON THE DIAGONAL. The minor axis
     * has to be worth at least two fifths of the major one, or a target a
     * little off-axis pulls the chase onto a diagonal that overshoots it. */
    if (ax > 4 && ay > 4 && Math.min(ax, ay) / Math.max(ax, ay) > 0.4) return vert + '-' + horiz;
    return ax > ay ? horiz : vert;
  }
  /* EIGHT FACINGS (LS-194). A creature's `dir` stays one of four -- its whole
   * behaviour is written on axes, and a charger that picks a diagonal lane
   * walks into walls the lane logic was built to avoid -- but what it LOOKS
   * at is where it is actually going. This is the heading of the last few
   * frames of travel, snapped to eight, and it is what the body is posed
   * from: a bramble drifting down-left leans down-left. */
  heading(frame = 0) {
    /* ONCE A FRAME. This is read from `draw`, and the camera path (LS-164)
     * draws a body more than once in a frame -- the second read would see no
     * movement since the first and decay the heading to a standstill. The
     * token is the creature's own `anim`, which steps once per update: `draw`
     * takes only the canvas, and reaching for a game that is not a parameter
     * threw inside two render sections ("one pixel is one pixel", "a
     * transition draws the same layers") rather than in anything that looked
     * like facing code. */
    if (this.headFrame === frame) return this.dir8 || this.dir;
    this.headFrame = frame;
    const dx = this.x - (this.faceX ?? this.x), dy = this.y - (this.faceY ?? this.y);
    this.faceX = this.x; this.faceY = this.y;
    const sx = (this.headX = (this.headX ?? 0) * 0.7 + dx), sy = (this.headY = (this.headY ?? 0) * 0.7 + dy);
    const ax = Math.abs(sx), ay = Math.abs(sy);
    if (ax < 0.06 && ay < 0.06) return this.dir8 || this.dir;
    const horiz = sx < 0 ? 'left' : 'right', vert = sy < 0 ? 'up' : 'down';
    const diagonal = ax > 0.06 && ay > 0.06 && Math.min(ax, ay) / Math.max(ax, ay) > 0.45;
    return (this.dir8 = diagonal ? vert + '-' + horiz : (ax > ay ? horiz : vert));
  }

  /* TWO OF THE THREE CLIMAXES WERE ENLARGED ORDINARY ENEMIES (LS-303). The sow had a charge; the old shell and
   * the bramble mother were a shellback and a bramble at four times the weight. Each has one clearly telegraphed
   * move of its own now, and a variation that arrives at half its life so the second half of the fight is not the
   * first half again. The old shell REARS AND SLAMS: forty frames of dust at its feet, then a ring of force that
   * throws whoever stands within three tiles; below half its life the slam comes twice. The bramble mother SHUDDERS
   * AND LASHES: thorns erupt in a line from her toward where you stand -- what you stand in cuts you, the rest
   * stays a hedge, so the arena closes as the fight goes on and fire is the answer to that too; at half her life
   * the hedge seeds, once, and two brambles rise beside her. */
  slamStep(game): boolean {
    if (!this.slam) this.slam = { t: 0, next: 200 + this.rng.int(120), second: false };
    const s = this.slam, p = game.player;
    if (s.t > 0) {
      s.t--;
      if (s.t % 6 === 0) game.puff(this.cx + (s.t % 12 ? 12 : -12), this.cy + 12, UI.linen, 3, 'dust');
      if (s.t === 0) {
        game.shakeFor(6, 12); game.sfx('crash_stone', this.x + 8);
        for (let i = 0; i < 14; i++) { const a = i / 14 * Math.PI * 2; game.puff(this.cx + Math.cos(a) * 22, this.cy + 6 + Math.sin(a) * 12, UI.linen, 4, 'dust'); }
        const d = Math.hypot(p.cx - this.cx, p.cy - this.cy);
        if (d < 52) p.hurt(game, 1, this.cx, this.cy, 'slam');
        /* THE MUD TIPS HIM (2026-09-27, the old shell's second answer). Slammed down on ground wet enough to be mud -- the
         * shore, after rain, or where water was thrown -- his weight sinks one side and he goes over: five seconds on his
         * back, no plate, every blow double. Beside deep water he slides in (the flipped branch). */
        if (game.systems && game.systems.fieldsFor && game.area) {
          const f = game.systems.fieldsFor(game), mtx = Math.floor(this.cx / TS), mty = Math.floor((this.cy + this.h / 2 - 4) / TS);
          const under = game.tileAt(mtx, mty);
          if (under !== undefined && f.wetAt(mtx, mty) >= 0.6) {
            this.flipped = 300; s.second = false; s.next = 240 + this.rng.int(160);
            for (let i = 0; i < 10; i++) game.puff(this.cx + (i % 5 - 2) * 8, this.cy + this.h / 2 - 2, '#5a4128', 3, 'dust');
            game.sfx('flip', this.x + 8); game.sfx('splash', this.x + 8);
            return true;
          }
        }
        if (this.hp <= this.maxHp / 2 && !s.second) { s.second = true; s.t = 24; }   // the second slam, once its life is half gone
        else { s.second = false; s.next = 240 + this.rng.int(160); }
      }
      return true;   // held still while it rears
    }
    const d = Math.hypot(p.cx - this.cx, p.cy - this.cy);
    if (--s.next <= 0 && d < 80 && game.inWindow && game.inWindow(this)) { s.t = 42; game.sfx('growl', this.x + 8); return true; }
    return false;
  }
  lashStep(game): boolean {
    if (!this.lash) this.lash = { t: 0, next: 160 + this.rng.int(120), seeded: false };
    const s = this.lash, p = game.player;
    if (s.t > 0) {
      s.t--;
      if (s.t % 4 === 0) game.puff(this.cx + (this.rng.int(24) - 12), this.cy + (this.rng.int(16) - 8), UI.leaf, 2, 'leaf');   // she shudders
      if (s.t === 0) {
        const dx = p.cx - this.cx, dy = p.cy - this.cy, dd = Math.hypot(dx, dy) || 1, ux = dx / dd, uy = dy / dd;
        let hit = false, grown = 0;
        for (let k = 1; k <= 5; k++) {
          const wx = this.cx + ux * k * TS, wy = this.cy + uy * k * TS;
          const tx = Math.floor(wx / TS), ty = Math.floor(wy / TS);   // world tiles: her hedge crosses a seam as her body does (LS-511: it stopped at the screen's edge)
          if (game.tileAt(tx, ty) === undefined) break;
          game.puff(wx, wy, UI.leaf, 4, 'leaf');
          if (Math.hypot(p.cx - wx, p.cy - wy) < 12) { if (!hit) { hit = true; p.hurt(game, 1, this.cx, this.cy, 'lash'); } continue; }
          const t = game.tileAt(tx, ty);
          if ((t === T.GROUND || t === T.MOSS || t === T.TUFT) && !game.enemies.some((e) => !e.dead && Math.hypot(e.cx - wx, e.cy - wy) < 12)) {
            game.setTileAt(tx, ty, T.THORN); grown++;
            game.markPlacedAt(tx, ty, T.THORN);   // into the placed layer, the way her own spread and the pack's bones are, so a reload keeps the hedge
          }
        }
        game.sfx('crash_brush', this.x + 8); game.shakeFor(3, 8);
        s.next = 220 + this.rng.int(140);
        if (this.hp <= this.maxHp / 2 && !s.seeded) {   // half her life gone, once: the hedge seeds
          s.seeded = true;
          const tx0 = Math.floor(this.cx / TS), ty0 = Math.floor(this.cy / TS);
          for (let i = 0; i < 2; i++) game.placeEnemyNear('bramble', this.rng, tx0, ty0, {});
          game.sfx('growl', this.x + 8);
        }
      }
      return true;
    }
    const d = Math.hypot(p.cx - this.cx, p.cy - this.cy);
    if (--s.next <= 0 && d < 96 && d > 20 && game.inWindow && game.inWindow(this)) { s.t = 36; game.sfx('growl', this.x + 8); return true; }
    return false;
  }

  updateWalker(game) {
    const def = this.def;
    if (this.def.beast && this.type === 'oldshell' && this.slamStep(game)) return;
    /* SHE LIVES ON HER HEDGE (2026-09-27, the bramble mother's third answer). With no thorn within three tiles of her
     * she withers, a life every two seconds, and the leaves she drops go brown: cut the hedge away and she starves. */
    if (this.def.beast && this.type === 'bramblemother' && game.area && game.runFrames % 120 === 0) {
      const mtx = Math.floor(this.cx / TS), mty = Math.floor(this.cy / TS);
      let near = false; for (let y = mty - 3; y <= mty + 3 && !near; y++) for (let x = mtx - 3; x <= mtx + 3 && !near; x++) if (game.tileAt(x, y) === T.THORN) near = true;
      if (!near) { this.lastCause = 'wither'; this.hp -= 1; this.flash = 6; for (let i = 0; i < 4; i++) game.puff(this.cx + (i - 1.5) * 6, this.cy - 4, '#8a6a3a', 2, 'leaf'); if (this.hp <= 0) { this.dead = true; game.onEnemyKilled(this); return; } }
    }
    if (this.def.beast && this.type === 'bramblemother' && this.lashStep(game)) return;
    /* BITE, THEN BACK OFF (2026-09-26). A lesser creature that has bitten turns tail for a moment and walks away: the
     * newcomer probe lost eight of its ten vigor to two shellbacks that stayed glued to it, one free bite a second.
     * Now a bite is a beat -- it lands, the creature turns its back (a shellback shows its open side), and that is
     * the moment to strike. The Zelda rhythm, not a grind. */
    if (this.def.armoured && this.rng.chance(0.02)) this.dir = this.faceToward(game);
    /* THE EEL LURKS UNTIL YOU ARE IN THE WATER (LS-293): on a dry player it drifts, submerged, a ripple and no more;
     * a player wading or swimming is hunted nine turns in ten, and the tide coming in over the shallows you stand on
     * is what brings it to you. */
    /* ...AND A RAFT RIDES LOW ENOUGH TO COUNT (LS-383: "the eel under it is what makes the raft a risk"): a body on a
     * raft is hunted like a wader; a boat's hull is above the eel's reach and it lurks. */
    const afloat = !!(game.aboard && game.state && game.state.boat && game.state.boat.kind === 'raft');
    const wet = !!(game.player && (game.player.wading || game.player.swimming || game.player.submerged || afloat)) && !(game.state && wearing(game.state, 'salt'));   // the salt charm (LS-335): the eel takes you for dry land
    if (this.def.aquatic) { const under = game.tileKindAt(this.cx, this.cy); this.lurking = under === T.ICE || (!wet && isWet(under)); }   // ...and sealed under ice it is under the ice, not a body on it (2026-09-19)   // ...and an eel on a bed the ebb has bared is a body on sand, not a ripple with no water (2026-09-19)
    if (--this.timer <= 0) {
      this.timer = this.rng.irange(25, 80);
      /* A PROWLER HUNTS (LS-379): the night's pack came for the player and
       * keeps coming, nine turns in ten, where the day's creature drifts. And
       * at dawn it goes -- faced away and walked off the glass, where the
       * systems take it. */
      /* A SHELL KEEPS TO ITSELF (2026-09-26). The lesser shelled kinds grazed toward the player two turns in five and
       * met them plate first, so two shellbacks on the sow's beach walked the newcomer probe down before it reached the
       * axe. They wander now and turn the plate when you come close; going round them is a choice, not a chase. */
      if (this.fleeing) this.dir = this.faceAway(game);
      else if (this.onTrail(game)) this.dir = this.seekDir(game);   // out of sight on the body's trail, it keeps to it (LS-527)
      else this.dir = this.rng.chance(this.prowler ? 0.9 : this.def.aquatic ? (wet ? 0.95 : 0.15) : this.type === 'bramble' ? 0.66 : this.def.armoured && !this.def.beast ? 0.08 : 0.42)
        ? (this.peaceable(game) ? this.rng.pick(['up', 'down', 'left', 'right']) : this.seekDir(game)) : this.rng.pick(['up', 'down', 'left', 'right']);   // at peace with you, it wanders (LS-522); out of sight it tracks (LS-527)
    }
    // sporelings hop rather than glide
    let sp = def.speed;
    if (this.type === 'sporeling') sp = (this.anim % 40) < 22 ? def.speed * 1.5 : 0;
    if (this.lurking) sp = def.speed * 0.4;   // a lurking eel idles
    /* AND WHAT AILS IT SLOWS IT (2026-09-18). `speedMul(body)` in sim/afflictions.ts -- a fifth off for Soaked,
     * Chilled or Spored, a tenth for Choking -- was written for any body and read by the PLAYER only: a creature's
     * movement called its own `speedMul` method (the sunbasker's hot-ground bonus) and never the afflictions'
     * function of the same name, so chilling, soaking or sporing a creature changed nothing about it. The frost, the
     * rain, the spore cloud and the ice all say they slow what they touch. */
    sp *= this.speedMul(game) * speedMul(this);   // the sunbasker (LS-335): half again as fast on ground hot enough to scorch -- this was read in the CHARGER's branch only, and no charger has the flag (2026-09-17)
    const [dx, dy] = DIRVEC[this.dir] || DIRVEC.down;
    /* ONE AXIS AT A TIME. `moveAxis` bisects the whole step back to the point
     * of contact, so a diagonal step into a wall stopped on BOTH axes and a
     * creature chasing along a cliff stood still against it. Moved as the
     * player is moved -- x, then y -- the blocked axis stops and the free one
     * carries on, which is a body sliding along a wall. A creature is turned
     * only when it is blocked on every axis it was trying. */
    let blocked = 0, tried = 0;
    const gh = !!def.ghost;   // a flying kind's steps are told it flies (2026-09-17): the stormgull's own flag reached its draw and its roster but not its collision, so deep water was a wall to it
    // a Great Beast's walk keeps to the screen it was made on (2026-09-19): the leash was the charger's only, and the old shell and the bramble mother walked over the seam and were snapped back by the first blow
    const leashed = this.def.beast && !!this.territory;
    if (sp && dx) { tried++; if ((leashed && this.leashHitTerritory(dx * sp, 0)) || moveAxis(game, this, dx * sp, 0, gh)) blocked++; }
    if (sp && dy) { tried++; if ((leashed && this.leashHitTerritory(0, dy * sp)) || moveAxis(game, this, 0, dy * sp, gh)) blocked++; }
    /* BOXED IN, IT TURNS ONCE AND WAITS (2026-09-28, Vijay: "the beast is glitching around a bunch"). A body blocked on every
     * axis picked a new heading here EVERY frame -- the timer set beside it is read only above, on a walk that is not
     * blocked -- so the bramble mother, hedged in by her own thorns, turned to a random side sixty times a second and her
     * mesh snapped between its side and front views (39 and 46 px wide) on the glass. It turns now at most once in the
     * timer's window, and tries the new heading until that runs out. */
    if (tried && blocked === tried && this.turnWhenBlocked(game)) this.timer = this.rng.irange(20, 50);

    if (def.shoots && --this.cool <= 0) {
      this.cool = this.rng.irange(100, 230);
      const p = game.player;
      const axis = AXIS_OF[this.dir] || this.dir;
      const aligned = axis === 'left' || axis === 'right'
        ? Math.abs(p.cy - this.cy) < 26 : Math.abs(p.cx - this.cx) < 26;
      /* ONLY WHAT YOU CAN SEE MAY SHOOT AT YOU (2026-09-06). This asked
       * whether the player was aligned and never how far away that was, so a
       * sporeling fired from anywhere its row happened to cross yours.
       * Measured over nine seeds, 234 enemy shots: 76% were thrown by
       * something OFF the glass, the median from 220px when the window is 256
       * by 192, the furthest from 485 -- nearly two screens. A spore does one
       * point and then poisons for 200 frames, and poison takes a point every
       * 90 with no source anywhere on the screen, so a walk was being ended by
       * things that were never drawn. Nine seeds in twelve died inside ninety
       * seconds of ordinary walking.
       *
       * The difficulty is not the complaint and is not reduced: a sporeling
       * you can SEE shoots exactly as it did. It simply has to be on the glass
       * to do it, which is the same bargain every other threat in the game
       * makes with the player. */
      const cam = game.camera ? game.camera() : null;
      const onGlass = !cam || (this.cx >= cam.x - 8 && this.cx <= cam.x + PW + 8
        && this.cy >= cam.y - 8 && this.cy <= cam.y + PH + 8);
      /* THE DECISION IS TAKEN FIRST AND THE GLASS ASKED SECOND. Written as
       * `onGlass && (aligned || chance(0.2))` this drew from the enemy's rng
       * only when it could see you, and a body that stops drawing takes every
       * later draw of its own stream from a different place -- every creature
       * in the world then walked a different path, and a beast two screens
       * away that used to roam sat still for forty seconds. It decides to
       * fire exactly as it always did; it simply cannot, from off the glass. */
      const wants = aligned || this.rng.chance(0.2);
      /* ...nor into ground closed to it (LS-511): kin that wait at a closed chunk's edge do not join the fight by
       * spore either -- the neighbours' tick never shot at you, and a sporeling outside the old shell's lair poisoned
       * the quest bot to death nineteen times. */
      const barred = !!(game.closedTo && game.closedTo(this, game.player.cx, game.player.cy));
      if (onGlass && wants && !barred && !this.peaceable(game)) {
        const [vx, vy] = DIRVEC[this.dir];
        game.spawnShot(def.shoots, this.cx - 4, this.cy - 4, vx * 2.1, vy * 2.1, false);
      }
    }
  }

  /** Where a thrown body comes down. Deep water drowns what does not float;
   *  fire burns what burns; a wall puts it back where it stood; and anything
   *  that lands is stunned for a moment. Read from the traits, not the kind. */
  land(game, kb) {
    const tr = traitsOf(this.type);
    const t = game.tileAtPx(this.cx, this.cy);
    const deep = t === T.WATER || t === T.SEA;
    if (deep && !tr.floats) {
      for (let i = 0; i < 6; i++) game.puff(this.cx, this.cy, UI.underPale, 4, 'splash');
      game.sfx('splash', this.x + 8);
      /* A GREAT BEAST DOES NOT DROWN ITSELF (LS-230): a sow that charged into
       * the sea died here without the mark lighting, and the run could not
       * be won. It flounders back to where it flew from, stunned; and any
       * other body that drowns dies through `onEnemyKilled` like every death. */
      if (this.def.beast) {
        const spot = nearFooting(game, this, false);
        if (spot) { this.x = spot.x; this.y = spot.y; } else { this.x = kb.fromX; this.y = kb.fromY; }
        clampBox(this); this.stun = 40; return;
      }
      this.dead = true; if (game.onEnemyKilled) game.onEnemyKilled(this);
      return;
    }
    if (game.systems && tr.burns && game.systems.burning(Math.floor(this.cx / TS), Math.floor(this.cy / TS))) this.hurt(game, 2, this.cx, this.cy + 8, 'fire');   // see below
    if (!this.dead && game.solid(this.cx, this.cy, !!this.def.ghost, this) && !(deep && tr.floats)) {
      const spot = nearFooting(game, this, !!tr.floats);
      if (spot) { this.x = spot.x; this.y = spot.y; } else { this.x = kb.fromX; this.y = kb.fromY; }
    }
    // a burning body that comes down on a brazier lights it
    if (this.alight > 0 && t === T.BRAZIER && game.lightBrazierAt) game.lightBrazierAt(Math.floor(this.cx / TS), Math.floor(this.cy / TS), true);
    game.puff(this.cx, this.cy + 5, UI.linen, 5, 'dust');
    this.landed = 8;   // a squash for a few frames (creatures.ts)
    if (this.def.armoured) { this.flipped = FLIP_FRAMES; game.sfx('flip', this.x + 8); return; }   // the clatter of a shell on its back, not the ring of a blade on it (LS-233)
    this.stun = 24;
  }

  updateWisp(game) {
    if (--this.timer <= 0) {
      this.timer = this.rng.irange(30, 90);
      this.ang = this.rng.range(0, Math.PI * 2);
      this.rest = this.rng.chance(0.25) ? this.rng.irange(20, 50) : 0;
      /* A WISP HAS A VOICE (the music lens, round 50: three levelled takes in the bank and nothing anywhere that
       * played them, on the third most common creature in the game -- 92 sightings over three runs against the
       * sporeling's 191). It sounds when it changes its mind, which is the moment the drift has a beat, and only
       * when it is on the glass, so a wood full of them offscreen is silent. One time in four, or a meadow of wisps
       * is a chorus. */
      if (this.rng.chance(0.25) && game.inWindow && game.inWindow(this)) game.sfx('wisp', this.x + 8);
    }
    if (this.ang === undefined) this.ang = this.rng.range(0, Math.PI * 2);
    if (this.rest > 0) { this.rest--; return; }
    const sp = this.def.speed;
    // a wisp is made of light: a brazier it drifts over is lit (LS-178)
    const wtx = Math.floor(this.cx / TS), wty = Math.floor(this.cy / TS);
    if (this.type === 'wisp' && game.tileAt(wtx, wty) === T.BRAZIER && game.lightBrazierAt) game.lightBrazierAt(wtx, wty, true);
    if (moveAxis(game, this, Math.cos(this.ang) * sp, 0, true)) this.ang = Math.PI - this.ang;
    if (moveAxis(game, this, 0, Math.sin(this.ang) * sp, true)) this.ang = -this.ang;
  }

  /** Telegraphs, then runs in a straight line until something stops it. */
  /** TURNED OUT OF THE THICKET (LS-302), by whichever path found it: a fire
   *  beside the tile, or a fire-tagged blow through `struck`. Both used to
   *  clear `hiding` by hand, so the bedding was given up by one of them and
   *  not the other -- and it was the OTHER that ran, because a creature that
   *  carries `Burns` is hurt by the fire it is sleeping in before it ever
   *  notices the fire. Whatever slept here was lying on two glim, which is
   *  what makes the thicket worth burning; `bedding` is spent when taken. */
  flush(game) {
    const h = this.hiding;
    this.hiding = null;
    if (!h) return;
    game.puff(this.cx, this.cy, UI.leaf, 6, 'leaf');
    if (game.sfx) game.sfx('crash_brush', this.x + 8);   // something coming out through the leaves is brush crashing, which the mix already has
    if (h.bedding && game.dropAt) {
      h.bedding = false;
      game.dropAt('glim', this.cx - 3, this.cy - 2);
      game.dropAt('glim', this.cx + 5, this.cy + 3);
    }
  }

  /** THE SOW HUNTS THE PACK (LS-379, creature agendas). A beast with the
   *  player out of reach and one of the small kin within a hundred and thirty
   *  pixels goes for the kin instead: the world has its own business, and a
   *  player who watches from a distance sees the country's beast feed. A
   *  beast's trample kills what it lands on, where an ordinary charger's only
   *  scatters. */
  prey(game) {
    if (!this.def.beast) return null;
    const p = game.player;
    if (Math.hypot(p.cx - this.cx, p.cy - this.cy) < 150) return null;
    let best: any = null, bd = 130;
    for (const o of game.enemies || []) {
      if (o === this || o.dead || o.def.boss || o.def.beast || o.hiding || o.spawnFx > 0) continue;
      const d = Math.hypot(o.cx - this.cx, o.cy - this.cy);
      if (d < bd) { bd = d; best = o; }
    }
    return best;
  }

  /** A body whose step was blocked picks a new heading -- but at most once in twenty frames, so one hemmed in by walls or its
   *  own hedge tries each way for a moment instead of spinning to a random side every frame (true when it turned). */
  turnWhenBlocked(game): boolean {
    const f = game.frame || 0; if ((this as any).turnedAt > f - 20 && (this as any).turnedAt <= f) return false;
    (this as any).turnedAt = f; this.dir = this.rng.pick(['up', 'down', 'left', 'right']); return true;
  }
  updateCharger(game) {
    // blown after a charge that missed: it stands and takes its punishment, then picks itself up
    if (this.state === 'spent') { if (--this.timer <= 0) { this.state = 'idle'; this.timer = this.rng.irange(40, 70); } return; }
    if (this.state === 'idle') {
      if (this.type === 'sow' && this.baitStep(game)) return;   // meat left out draws her, with you out of reach (LS-519)
      if (this.type === 'sow' && this.grazeStep(game)) return;
      if (--this.timer <= 0) {
        const kin = this.prey(game);
        if (!kin && this.peaceable(game)) { this.timer = this.rng.irange(40, 90); this.dir = this.rng.pick(['up', 'down', 'left', 'right']); return; }   // at peace with you: no charge (LS-522)
        if (!kin && !this.def.beast && !this.prowler && Math.hypot(game.player.cx - this.cx, game.player.cy - this.cy) > AWARE) { this.timer = this.rng.irange(30, 70); this.dir = this.seekDir(game); return; }   // out of sight it tracks, and does not charge what it cannot see (LS-527)
        this.dir = kin ? this.faceTo(kin.cx, kin.cy) : this.faceToward(game);
        /* NO CHARGE AT A WALL (2026-09-29, the playthrough: the sow stood below a cliff and charged the player standing on
         * top of it, over and over -- each charge ended in the cliff face, five of her life each time, and she was dying
         * of it with the player watching from above). A charger winds up only when the ground between it and what it
         * wants is open for its whole body; otherwise it walks round toward it. Rock BEHIND the player still stops a
         * charge that misses: that is the lure the docs describe, and it is untouched. */
        const tgt = kin || game.player;
        if (!this.laneClear(game, tgt.cx, tgt.cy)) { this.timer = this.rng.irange(20, 40); const way = this.stepRound(game, tgt.cx, tgt.cy); const f = game.frame || 0; if (way !== this.dir && !((this as any).turnedAt > f - 20 && (this as any).turnedAt <= f)) { (this as any).turnedAt = f; this.dir = way; } return; }   /* its heading turns under the same once-in-twenty-frames rule as a blocked step (beastturn) */
        this.state = 'wind';
        this.roused = game.runFrames;   // awake for fifteen seconds from here (the design lens, round 47: a sow resting between charges read as asleep, and the parked pack walked back in)
        this.timer = this.type === 'sow' ? WIND_SOW : this.def.beast ? WIND_BEAST : WIND_CHARGER; this.windFrom = this.timer;   // how long this wind-up runs, for the lane that fills as it does (2026-09-27)   // a Great Beast winds up longer, so the charge is read from across the screen (the art lens, round 43); the sow longest (LS-388)
        game.sfx('growl', this.x + 8);
      } else {
        const [dx, dy] = DIRVEC[this.dir];
        // a Great Beast's walk keeps to the screen it was made on: the seam turns it as a wall would (the design lens, round 47: the sow walked off its lair screen in six of fourteen fights)
        const spd = this.def.speed * this.speedMul(game) * speedMul(this);   // the sunbasker (LS-335): half again as fast on hot ground, and what ails it slows it (2026-09-18)
        if ((this.def.beast && this.leashHitTerritory(dx * spd, dy * spd)) || moveAxis(game, this, dx * spd, dy * spd)) this.turnWhenBlocked(game);
      }
      return;
    }
    if (this.state === 'wind') {
      if (this.def.beast && this.timer % 6 === 0) game.puff(this.cx + (this.timer % 12 ? 6 : -6), this.cy + 10, UI.linen, 2, 'dust');   // dust at the hind feet while it paws: soft and ground-coloured, not a sparkle (the art lens, round 44)
      if (--this.timer <= 0) { this.state = 'charge'; this.timer = 90; this.trampled = null; game.sfx('charge', this.x + 8); }   // the snort and the hooves: the take sat silent in the bank (LS-233); the screen the charge starts on is the one it is leashed to (the robustness lens, round 47)
      return;
    }
    /* SOAKED GROUND BOGS A CHARGE (2026-09-27, the sow's second answer). A beast running full tilt onto ground wet
     * enough to be mud -- beside water, after rain, or where a player has thrown water -- sinks to the hocks and
     * struggles: four seconds stuck fast, and every blow counts double while she is. */
    if (this.def.beast && game.systems && game.systems.fieldsFor && game.area) {
      const f = game.systems.fieldsFor(game), btx = Math.floor(this.cx / TS), bty = Math.floor((this.cy + this.h / 2 - 4) / TS);
      const under = game.tileAt(btx, bty);
      if (under !== undefined && !isWet(under) && f.wetAt(btx, bty) >= 0.6) {
        this.state = 'idle'; this.timer = this.rng.irange(50, 90); this.bogged = 240; this.stun = 240;
        for (let i = 0; i < 8; i++) game.puff(this.cx + (i % 4 - 1.5) * 6, this.cy + this.h / 2 - 2, '#5a4128', 3, 'dust');
        game.sfx('splash', this.x + 8); game.shakeFor(2, 6);
        return;
      }
    }
    const [dx, dy] = DIRVEC[this.dir];
    /* BOTH AXES ARE ATTEMPTED, THEN COMBINED.
     *
     * This was `moveAxis(...) | moveAxis(...)` — a BITWISE or, on two
     * booleans, and deliberately so: `||` short-circuits, and skipping the
     * second axis would stop the body sliding along a wall it has clipped.
     * The bitwise operator was carrying that intent by side effect, which is
     * why a type checker calls it a mistake and why swapping in `||` would
     * have been a real regression. Named locals say the same thing and cannot
     * be misread. */
    /* A CHARGE YOU MAKE MISS ENDS SOMEWHERE (LS-178). Into deep water it
     * plunges and drowns (a charger does not float); into stone it stuns
     * itself for a second, which is the window to hit it. */
    // ...seen a tile and a half ahead, so a charger at the edge of the shallows
    // that fringe a channel leaps them into the deep rather than stopping short
    const aheadT = game.tileAtPx(this.cx + dx * 12, this.cy + dy * 12), aheadT2 = game.tileAtPx(this.cx + dx * 26, this.cy + dy * 26);
    const open2 = aheadT === T.SHALLOW || (aheadT !== undefined && !game.solid(this.cx + dx * 12, this.cy + dy * 12, false, this));
    if (aheadT === T.WATER || aheadT === T.SEA || (open2 && (aheadT2 === T.WATER || aheadT2 === T.SEA))) {
      // far enough to come down in the deep, not on the shallow before it
      const near = aheadT === T.WATER || aheadT === T.SEA;
      this.kb = { dx, dy, t: near ? 5 : 9, launched: true, fromX: this.x, fromY: this.y };
      this.state = 'idle'; this.timer = this.rng.irange(50, 90);
      return;
    }
    /* THE CHARGE IS LEASHED TO ITS TERRITORY (the design lens, round 46; LS-511): a Great Beast's charge that ran out of
     * its own country took the fight with it. The edge of its territory is rock to it: it crashes there and lies
     * stunned, as it would against a cliff. (It was the seam of the screen the charge began on.) */
    const leashed = this.def.beast && this.leashHitTerritory(dx * 3.1, dy * 3.1);
    const hitX = leashed ? dx !== 0 : moveAxis(game, this, dx * 3.1, 0);
    const hitY = leashed ? dy !== 0 : moveAxis(game, this, 0, dy * 3.1);
    /* THE PLUNGE HURTS WHAT IT RUNS THROUGH (LS-183, the last item on the
     * card). A charging boar passed clean through a bramble pack, which was
     * the one thing on the field that behaved as if bodies were not matter.
     * Anything it overlaps mid-charge takes a blow from the boar's own
     * direction -- a light one, reeling rather than killing, so a boar you
     * lure through its friends scatters them and does not do your work for
     * you -- and the charge carries on. Each body is struck once per charge. */
    this.trampled = this.trampled || new Set();
    for (const o of game.enemies || []) {
      if (o === this || o.dead || o.def.boss || this.trampled.has(o)) continue;
      if (o.x + o.w <= this.x || o.x >= this.x + this.w || o.y + o.h <= this.y || o.y >= this.y + this.h) continue;
      this.trampled.add(o);
      if (typeof o.hurt === 'function') o.hurt(game, this.def.beast ? 2 : 1, this.cx - dx * 8, this.cy - dy * 8, 'body');   // a beast's trample kills the small kin it lands on (LS-379)
    }
    const intoBrace = !!(game.player && game.player.braced && game.player.braced(game) && overlaps(this, game.player));   // a braced plate is stone to a charge
    const blocked = hitX || hitY || intoBrace;
    /* STONE HURTS WHAT RUNS INTO IT (2026-09-27, the sow's first answer). A charge that ends against rock, cliff or tree
     * costs the runner: a Great Beast five of its life, a lesser charger one -- so a player who stands with their back
     * to a wall and steps aside at the last moment can fell her without a blade. Brittle stone breaks under the blow.
     * The edge of her territory is not stone: a leashed crash costs nothing. */
    if (blocked && !leashed && !this.dead && !(this.def.beast && (globalThis as any).__noCrashHarm)) {   // (a diagnostic knob: a check that asks what BLOWS can do sets a beast's crashes aside)
      const ax = this.cx + dx * (this.w / 2 + 4), ay = this.cy + dy * (this.h / 2 + 4);
      const btx = Math.floor(ax / TS), bty = Math.floor(ay / TS), bt = game.tileAt(btx, bty);
      this.flash = 0; this.lastTag = null;
      this.hurt(game, this.def.beast ? 5 : 1, ax, ay, 'crash-' + game.runFrames);
      for (let i = 0; i < 6; i++) game.puff(ax, ay, UI.linen, 4, 'dust');
      if (bt !== undefined && bt !== T.NEST && matterOf(bt).brittle && game.breakTileAt) game.breakTileAt(btx, bty);
      if (this.dead) return;
    }
    if (blocked || --this.timer <= 0) {
      /* A CHARGE YOU MADE MISS LEAVES THE BEAST SPENT (2026-09-13). The double-damage rule below has asked for
       * `state === 'spent'` since round 42 -- "what a charge you MISS leaves behind, and that one pays double" -- and
       * nothing ever set it: a charge that crashed into a wall gave a stun, and a charge you DODGED went straight back
       * to idle, so reading the wind-up and stepping aside earned a careful player nothing. Measured before this: five
       * one-life runs, two of them ended at the sow, who is the first Great Beast a player meets. She stands blown for
       * a moment now, and the axe -- her own answer, a crush -- counts double into it. A wall crash still stuns, which
       * is the bigger window of the two. */
      this.state = blocked ? 'idle' : 'spent';
      this.timer = blocked ? this.rng.irange(50, 90) : Math.round((this.def.beast ? 48 : 26) * (wearing(game.state, 'tusk') ? 1.5 : 1));
      if (blocked) { game.shakeFor(2, 8); game.puff(this.cx, this.cy, UI.linen, 5); this.stun = Math.round(CHARGE_STUN * (this.def.beast ? 2 : 1) * (wearing(game.state, 'tusk') ? 1.5 : 1)); /* the tusk charm (LS-335) holds the punish window open */ game.sfx(crashCue(game, this.cx + (hitX ? dx : 0) * 12, this.cy + (hitY ? dy : 0) * 12), this.x + 8); }   // what it hit (LS-214)
    }
  }

  /** THE SOW EATS THE THICKET (2026-09-27, ecology). With the player well off she goes to the nearest thorn within
   *  five tiles and browses it down, a tile every second and a half -- so a thicket is where she is found, and a
   *  thicket set alight with her in it is her third answer. Blocked on the way, she gives it up to her ordinary walk. */
  /** THE SOW COMES TO MEAT (LS-519). With the player more than a hundred and fifty px off, raw or cooked meat within ten
   *  tiles draws her; she walks to it and stands eating it for three seconds -- a window, and a way to draw her somewhere. */
  baitEat = 0; baitAt: any = null;
  baitStep(game): boolean {
    if (this.baitEat > 0) { if (--this.baitEat === 0 && this.baitAt) { this.baitAt.dead = true; this.baitAt = null; } else if (this.baitEat % 30 === 0) game.puff(this.cx, this.cy + 8, UI.bone, 1, 'fleck'); return true; }
    const p = game.player; if (!p || Math.hypot(p.cx - this.cx, p.cy - this.cy) < 150) { this.baitAt = null; return false; }
    let best: any = null, bd = TS * 10;
    for (const it of game.items || []) { if (it.dead || (it.kind !== 'meat' && it.kind !== 'cookedmeat') || it.offered) continue; const d = Math.hypot(it.x + 4 - this.cx, it.y + 4 - this.cy); if (d < bd) { bd = d; best = it; } }
    if (!best) return false;
    const dx = best.x + 4 - this.cx, dy = best.y + 4 - this.cy;
    if (bd < this.w / 2 + 4) { this.baitAt = best; this.baitEat = 180; return true; }
    this.dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
    const sp = this.def.speed * 0.9;
    if ((this.leashHitTerritory(dx / bd * sp, 0) || moveAxis(game, this, dx / bd * sp, 0)) && (this.leashHitTerritory(0, dy / bd * sp) || moveAxis(game, this, 0, dy / bd * sp))) return false;   // walled off from it
    return true;
  }
  grazeStep(game): boolean {
    const p = game.player;
    if (!p || this.alight > 0 || this.stun > 0 || !game.area || Math.hypot(p.cx - this.cx, p.cy - this.cy) < 110) { this.grazing = 0; return false; }
    const mx = Math.floor(this.cx / TS), my = Math.floor(this.cy / TS);   // world tiles (LS-511)
    let best: number[] | null = null, bd = 1e9;
    for (let y = my - 5; y <= my + 5; y++) for (let x = mx - 5; x <= mx + 5; x++) { if (game.tileAt(x, y) !== T.THORN) continue; const d = Math.hypot(x - mx, y - my); if (d < bd) { bd = d; best = [x, y]; } }
    if (!best) { this.grazing = 0; return false; }
    const gx = best[0] * TS + 8, gy = best[1] * TS + 8, dx = gx - this.cx, dy = gy - this.cy;
    const face = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
    if (Math.abs(dx) > this.w / 2 + 10 || Math.abs(dy) > this.h / 2 + 10) {
      this.dir = face; this.grazing = 0;
      const [vx, vy] = DIRVEC[this.dir], spd = this.def.speed * 0.8;
      if ((this.def.beast && this.leashHitTerritory(vx * spd, vy * spd)) || moveAxis(game, this, vx * spd, vy * spd)) return false;
      return true;
    }
    this.dir = face; this.grazing = (this.grazing || 0) + 1;
    if (this.grazing % 10 === 0) game.puff(gx + (this.grazing % 20 ? 3 : -3), gy - 2, UI.leaf, 2, 'leaf');
    if (this.grazing % 90 === 0) {
      game.markBrokenAt(best[0], best[1]); game.setTileAt(best[0], best[1], T.DEBRIS);
      for (let i = 0; i < 5; i++) game.puff(gx, gy, UI.leaf, 3, 'leaf'); game.sfx('break', gx);
    }
    return true;
  }
  /** A Great Beast's territory (LS-511): a box of world px -- its lair's chunk and two round it, the ground its record
   *  roams -- and the chunks near the glade it never enters. Set by the game when the body is made; none, no leash. */
  territory: { x0: number; y0: number; x1: number; y1: number; barred: Set<string> } | null = null;
  /** Would a step of (dx, dy) take this body out of its territory, or into the ground round the glade? ONLY A STEP
   *  FURTHER OUT (2026-09-29, the playthrough: a sow whose box stood over the line crashed at every wind-up, whichever
   *  way it ran): a body already over the edge may move back in or along it. */
  leashHitTerritory(dx, dy) {
    const t = this.territory; if (!t) return false;
    const nx = this.x + dx, ny = this.y + dy;
    if ((dx < 0 && nx < t.x0) || (dx > 0 && nx + this.w > t.x1) || (dy < 0 && ny < t.y0) || (dy > 0 && ny + this.h > t.y1)) return true;
    if (t.barred.size) {
      const PW = COLS * TS, PH = ROWS * TS;
      const to = Math.floor((nx + this.w / 2) / PW) + ',' + Math.floor((ny + this.h / 2) / PH), from = Math.floor(this.cx / PW) + ',' + Math.floor(this.cy / PH);
      if (to !== from && t.barred.has(to)) return true;
    }
    return false;
  }
  /** Put a beast's box back inside its territory after a shove (the design lens, round 47). */
  keepToHome() {
    const t = this.territory; if (!this.def.beast || !t) return;
    this.x = Math.max(t.x0, Math.min(t.x1 - this.w, this.x)); this.y = Math.max(t.y0, Math.min(t.y1 - this.h, this.y));
  }

  /** Shellbacks turn their plate to you; hit them from anywhere else. */
  blocksFrom(px, py) {
    if (!this.def.armoured || this.flipped > 0 || this.alight > 0) return false;   // ...and a shell alight is too hot to hold toward you (2026-09-27, the old shell's baked answer)
    const [dx, dy] = DIRVEC[this.dir];
    const rx = px - this.cx, ry = py - this.cy;
    return (rx * dx + ry * dy) > Math.max(Math.abs(rx * dy - ry * dx), 2);
  }

  /** A kind's pace where it stands: the sunbasker is half again as fast on ground hot enough to scorch (LS-335). */
  speedMul(game): number {
    if (!this.def.sun || !game || !game.systems || !game.systems.fieldsFor || !game.area) return 1;
    const f = game.systems.fieldsFor(game); const tx = Math.floor(this.cx / TS), ty = Math.floor(this.cy / TS);
    return f.tempAt(tx, ty) > SCORCH_LINE ? 1.5 : 1;
  }
  hurt(game, dmg, fromX, fromY, tag) {
    // in a thicket nothing reaches it but the fire that takes the thicket (LS-176 step 6)
    /* ...AND THE FIRE TAG HAD NO SENDER (2026-09-06). This is the one exception the design carves out -- a body in a
     * thicket is reached by nothing but the fire that takes the thicket -- and it is gated on a `tag` beginning
     * `fire`. NOT ONE CALLER ANYWHERE PASSED ONE: the burning-tile damage above called `hurt` with four arguments
     * where the signature takes five, so `tag` arrived undefined and a hiding body walked out of a burning thicket
     * untouched. `tsc` had been reporting that call as TS2554 for as long as nobody ran it. */
    if (this.hiding) { if (!(tag && String(tag).startsWith('fire'))) return; this.flush(game); }
    // A Warden caught winded takes double. The charge pattern refuses melee by
    // design, so without a reason to step in the whole fight was one throw on
    // a two-and-a-half second recharge against nineteen hit points — measured
    // at a 5-15% win rate for a competent player. The stagger is the fight.
    // THE DOUBLE IS FOR THE STAGGER YOU EARNED, not the one you bought. A
    // reel is time: the Warden stops and you may close and swing. `spent` is
    // what a charge you made MISS leaves behind, and that one pays double.
    // Giving the reel the multiplier too made every Warden easier by the same
    // stroke that made the ranged loop viable, and pattern 1 — which had the
    // least headroom on the losable side — went to 90% wins even after a chip
    // hit, against a bar of 85%. A boss nobody can lose to is scenery.
    const pierces = !!(tag && String(tag).startsWith('swing') && game.state && swingWeaponOf(game.player).weapon.verb === 'pierce');
    const throughPlate = pierces && this.blocksFrom(fromX, fromY);   // the spear's answer counts double through the guard (the design lens, round 42)
    if (this.def.beast) this.roused = game.runFrames;   // a blow rouses it too (the design lens, round 47)
    if (this.def.beast && this.alight > 0) dmg *= 2;   // a burning beast is an open one (LS-225)
    /* THE OPEN WINDOW IS THE AXE'S (the design lens, round 41: 'the answers are false both ways'). A beast stunned by its
     * own charge or spent takes double from a CRUSH -- the axe is the sow's answer -- and single from the rest; a thrown
     * sickle's reel stays every blade's window, it is the sickle's own trick. The old Wardens kept the wide rule. */
    const crush = !!(tag && String(tag).startsWith('swing') && game.state && (swingWeaponOf(game.player).weapon.verb === 'crush' || game.spendStone(game.swingId)));   // ...or the stonemilk's three blows (LS-335)
    /* A HEDGE IS CUT (2026-09-27, the bramble mother's second answer): a cutting blade -- the sickle, the knife -- takes
     * double from her, where on anything else it is the lightest blow there is. */
    if (this.type === 'bramblemother' && tag && String(tag).startsWith('swing') && game.state && swingWeaponOf(game.player).weapon.verb === 'cut' && !(this.flash > 7)) { dmg *= 2; game.puff(this.cx, this.cy, UI.leaf, 4, 'leaf'); }
    // Reject already-counted contacts before emitting bonus-hit sparks.
    /* ONE BLOW, NOT FOR EVER (2026-09-17). `lastTag` refuses a repeat of the same tagged contact, which is right
     * inside one swing -- and it was never cleared anywhere, so a CONSTANT tag could only ever land once in a
     * creature's life: a scorching took one hit point and the ground was harmless for the rest of the run, and a
     * charger's trample or a pushed boulder could hurt a given body exactly once however often it struck. The guard
     * expires with the flash it belongs to (see `update`), so a fresh contact a moment later is a fresh blow. */
    if (tag && this.lastTag === tag) return;
    if (this.dead || this.spawnFx > 0) return;
    if (throughPlate) { dmg *= 2; game.puff(this.cx, this.cy, UI.gold, 4, 'spark'); }
    if (this.def.boss && (this.reel > 0 || this.bogged > 0 || ((this.stun > 0 || this.state === 'spent') && (!this.def.beast || crush)))) {   // ...and a beast stuck in the mud takes double from anything (2026-09-27)
      dmg *= 2;
      game.puff(this.cx, this.cy, UI.gold, 4, 'spark');
    }
    /* A BLOW THAT IS TOO SOON SAYS SO (2026-09-08; Vijay: "why are the purple guys randomly
     * invulnerable? it's very unclear"). The purple one is the sporeling, and it is not armoured:
     * what refuses the blow is this line, which used to read `|| this.flash > 7` and return in
     * silence. A body flashes for twelve frames after anything hurts it and is untouchable for the
     * first five of them, so a swing landing inside that window cost the player a swing with no
     * sound, no spark and no reason given -- measured on a driven fight, one refusal in five
     * outcomes, and in every one of them the refused blow was the player's FIRST swing at a body
     * already ringing from something else. The window stays, because it is what stops one swing
     * being counted twice; what changes is that it is now audible, and that a refused swing is
     * SPENT -- stamping the tag makes the later frames of the same swing fall out at the duplicate
     * guard above, so one swing rings once instead of five times. */
    if (this.flash > 7) {
      if (tag) {
        this.lastTag = tag;
        game.sfx('chip', this.x + 8);
        game.puff(this.cx, this.cy, UI.bone, 2, 'spark');
      }
      return;
    }
    if (tag) this.lastTag = tag;
    // A PIERCE PASSES THE GUARD (the design lens, round 41): the bestiary promised 'reach past its guard with the spear' and no code read the verb against the plate
    /* THE AXE TURNS A SHELL OVER (2026-09-26). The plate faced every blade the newcomer probe had, so a shellback on the
     * sow's beach rang eight blows and bit eight times; a crush on the plate of a lesser shell now throws it on its
     * back -- the clatter, then the open belly -- and the axe in hand is felt at once. The beasts' plates keep their rule. */
    if (crush && !this.def.beast && this.def.armoured && this.blocksFrom(fromX, fromY) && !(this.flipped > 0)) {
      this.flipped = FLIP_FRAMES; this.flash = 6; game.sfx('flip', this.x + 8); game.puff(this.cx, this.cy, UI.gold, 4, 'spark');
      return;
    }
    if (this.blocksFrom(fromX, fromY) && !pierces) {
      // the blade kicks back off the shell (LS-213); the clang is the recoil's, or this one when it is not the sickle
      if (!(tag && String(tag).startsWith('swing') && game.player && game.player.recoilFrom(game, this.cx, this.cy, 'metal'))) game.sfx('clang', this.x + 8);
      game.puff(this.cx, this.cy, UI.bone, 3, 'spark');
      this.flash = 6;
      /* ...AND A LESSER SHELL TURNS AWAY FROM THE RING (2026-09-27). A screehopper and a tidecrab each took ten of the
       * newcomer probe's vigor: the sickle rang off the plate and the body stayed in contact, biting. Struck on the plate,
       * a lesser shell flinches round for a second -- no bite, its open side to you -- so clang, it turns, strike its back. */
      if (!this.def.beast) { this.sated = Math.max(this.sated || 0, 60); this.dir = this.faceAway(game); this.timer = Math.max(this.timer || 0, 60); }
      return;
    }
    // NB: A MELEE GUARD WAS HERE, AND IT DID NOT SURVIVE A WIDER SAMPLE.
    // Six landed swings in a row with nothing else in them and the stone
    // closed; any window opened it again. On 24 measured fights it looked
    // exactly right — the mash fell 79/96/100 to 67/92/100 while a player who
    // mixed throws in ROSE from 83 to 92 at the floor. At 60 fights that
    // advantage is not there: the guard takes the mash from 85% to 80% and
    // the mixed player from 88% to 80%, so it costs the player it was meant to
    // reward MORE than the one it was meant to tax. The 92-vs-67 was seed
    // noise on a small sample and it was reported as a result.
    //
    // Removed rather than re-tuned. This is the fourth melee tax this project
    // has built and measured, and every one of them has landed on the spacing
    // player. What actually moved the fight is above: a throw that opens any
    // Warden, and a pattern 0 with a window in it — the ranged loop went from
    // 0% to 23% at the floor pool and is unaffected by the guard either way.
    // A THROWN sickle (no swing tag) leaves a Warden reeling. Not a swing:
    // then the mash would open its own window and nothing would change. (The
    // other cause is a Warden's own over-commitment — see pattern 0's lunge.)
    if (!tag && this.def.boss && !(this.reel > 0)) {
      this.reel = 38;
      game.shakeFor(3, 10);
      game.sfx('clang', this.x + 8);
      if (!this.saidReel) { this.saidReel = true; /* was: 'the stone rings' — the clang and the spark are the whole message */ }
    }
    if (this.flipped > 0) { dmg *= 2; game.puff(this.cx, this.cy, UI.gold, 4, 'spark'); }
    // a swing or a blast reels; a shot (tagged by its own id) only stings
    if (!this.def.boss && !this.def.armoured && (!tag || /^(swing|body)/.test(String(tag)))) this.reel = Math.max(this.reel || 0, REEL_FRAMES);
    /* WHAT FELLED IT (2026-09-27): a beast has several answers now, and the one that took its last life is the story --
     * the blade by its name, or the world's own tag (fire, drown, body, fall). Read by onEnemyKilled into state.fellBy. */
    this.lastCause = tag && String(tag).startsWith('swing') ? 'blade:' + ((swingWeaponOf(game.player) as any).kind || 'fist') : tag ? String(tag).split(/[-:|]/)[0] : 'blow';
    if (this.asleep) { dmg *= 2; game.puff(this.cx, this.cy - 4, UI.gold, 5, 'spark'); }   // a blow on a sleeper lands double (LS-526)
    this.hp -= dmg;
    /* HALF ITS LIFE, LATCHED ONCE (2026-09-17). `enraged` is read in sixteen places -- the cap's tone goes red, every
     * thorn stands out, the eye changes, the palette takes its 'r' variant, and the 3D body's colours follow -- and
     * not one line in the game ever set it, so a creature at one hit point was drawn exactly like a fresh one. The
     * constructor said what it should be the day it was written. Latched on the way DOWN only, because the cinder
     * raises its own ceiling as it feeds. */
    if (!this.enraged && this.hp > 0 && this.hp <= this.maxHp / 2) this.enraged = true;
    if (!tag || String(tag).startsWith('swing')) this.hitByPlayerAt = game.runFrames || 0;   // your blade, swung or thrown: what standing counts (LS-523)
    if (tag !== 'bite' && tag !== 'bite-friend') this.provokedUntil = (game.runFrames || 0) + PROVOKED_FRAMES;   // struck by you, it answers whatever its kind thinks of you (LS-522)
    if (this.asleep) { this.asleep = false; }   // struck, it wakes (the double was taken above, LS-526)
    /* MORALE (LS-520, after Caves of Qud): a lesser creature brought to two fifths of its life or under bolts from what
     * hurt it for three seconds -- into a thicket beside it if there is one -- rather than fighting to the death. */
    if (!this.def.boss && !this.prowler && this.hp > 0 && this.maxHp >= 2 && this.hp <= this.maxHp * 0.4) { this.routed = ROUT_FRAMES; this.routFrom = [fromX, fromY]; }
    this.flash = 12;
    const ang = Math.atan2(this.cy - fromY, this.cx - fromX);
    this.kb = { dx: Math.cos(ang), dy: Math.sin(ang), t: this.def.boss ? 3 : 7 };
    /* JUICE (LS-187). A stop by the weight of the blow -- a plain hit 3
     * frames, twice the hurt 6, a kill 8 -- a kick of the screen on every one,
     * and sparks thrown the way the blow went, not a ring. */
    const weight = dmg >= 2 ? 6 : 3;
    game.hitstopFor(this.hp <= 0 ? 8 : weight);
    game.shakeFor(dmg >= 2 ? 3 : 2, dmg >= 2 ? 8 : 5);
    for (let i = 0; i < 4; i++) game.puff(this.cx + Math.cos(ang) * (3 + i * 2), this.cy - 2 + Math.sin(ang) * (3 + i * 2), i % 2 ? UI.linen : UI.gold, 2, 'spark');
    if (game.chip && this.hp > 0) game.chip(this, fromX, fromY);
    game.sfx(dmg >= 2 ? 'hit_heavy' : 'hit', this.x + 8);   // a two-point blow has its own weight (LS-197)
    if (this.def.boss && this.hp > 0) game.bossThreshold(this);
    if (this.hp <= 0) {
      this.dead = true;
      game.onEnemyKilled(this);
    }
  }

  draw(g, game?) {
    /* A LURKING EEL IS A RIPPLE (LS-293): under the surface while you are dry, two pale strokes that drift with
     * its swimming, and nothing to aim at. It surfaces when you wade. */
    if (this.lurking && !this.dead) {
      const rx = Math.round(this.cx) - 4 + (Math.floor(this.anim / 6) % 3) - 1, ry = Math.round(this.cy) - 1;
      g.fillStyle = 'rgba(246,242,232,0.85)'; g.fillRect(rx, ry, 5, 1); g.fillRect(rx + 3, ry + 2, 5, 1); g.fillRect(rx - 2, ry + 4, 4, 1);
      return;
    }
    if (this.spawnFx > 0) {
      // ONE `fillRect`, SHRINKING. The arrival of every enemy in the game was
      // a flat square stepping from 16px to 2px and flashing between two
      // colours — measured at a mean of 2.4 and a maximum of 5 on screen at
      // once, and it is the first thing a player sees of every fight. A ring
      // of dashes closing and turning on the point it will arrive at: the
      // frame says how long is left, and the rotation says it is coming.
      const t = this.spawnFx;
      const f = Math.min(4, Math.max(0, 4 - Math.floor(t / 6)));
      g.drawImage(spawnRing(f, t % 8 < 4 ? UI.paper : UI.gold),
        Math.round(this.cx) - 8, Math.round(this.cy) - 8);
      return;
    }
    // `scale` used to be a DRAW scale — a 16x16 sprite blown up to 32x32, so
    // the boss was the one piece of double-density art in a 1px game. It names
    // the ART now: the Warden has its own 32x32 frames.
    /* EVERY CREATURE HAS BONES (LS-185): posed from this state and painted by
     * `creatures.ts`; the frame sprites remain behind `rigFlags.on`. */
    if (rigFlags.on && hasRig(this.type)) {
      const moved = Math.abs(this.x - (this.lastX ?? this.x)) > 0.05 || Math.abs(this.y - (this.lastY ?? this.y)) > 0.05;
      const spr = creatureSprite({
        type: this.type, dir: this.heading(this.anim), anim: this.anim >> 1 << 1,   // even frames only: half the sprites baked, and thirty pictures a second is a gait (LS-227)
        moving: this.state === 'charge' || moved,
        state: this.state || 'idle', stun: this.stun || 0, flipped: this.flipped || 0, reel: this.reel || 0, alight: this.alight || 0,
        launched: !!(this.kb && this.kb.launched), enraged: !!this.enraged, turned: this.turned || 0, alt: this.alt || null,
        white: this.flash > 0 && Math.floor(this.flash / 3) % 2 === 0, pattern: this.pattern, landed: this.landed || 0,
        cool: this.def.shoots ? (this.cool || 0) : undefined,   // a shooter's telegraph (the art lens)
        wet: isWetBody(this, game),   // its wet state (2026-09-11)
      });
      this.lastX = this.x; this.lastY = this.y;
      if (spr) {
        this.lastRig = { key: spr.key, mask: spr.mask, normals: (spr as any).normals, w: spr.w, h: spr.h, ox: Math.round(this.cx - spr.w / 2) - Math.round(this.x), oy: Math.round(this.cy - spr.h / 2) - Math.round(this.y) };
        const jx = this.state === 'wind' ? (Math.floor(this.anim / 2) % 2 ? 1 : -1) : 0;
        const lift = this.kb && this.kb.launched ? Math.round(6 * Math.sin(Math.PI * (1 - this.kb.t / 26))) : (this.slam && this.slam.t > 0 ? Math.round(5 * Math.sin(Math.PI * this.slam.t / 42)) : 0);   // ...or the old shell rearing for its slam (LS-303)
        if (this.kb && this.kb.launched) {
          // JUICE (LS-187): a thrown body stretches along its flight
          const ang = Math.atan2(this.kb.dy, this.kb.dx);
          g.save();
          g.translate(Math.round(this.cx + jx), Math.round(this.cy - lift));
          g.rotate(ang); g.scale(1.3, 0.8); g.rotate(-ang);
          g.drawImage(spr.canvas, spr.sx || 0, spr.sy || 0, spr.w, spr.h, -Math.round(spr.w / 2), -Math.round(spr.h / 2), spr.w, spr.h);
          g.restore();
        } else g.drawImage(spr.canvas, spr.sx || 0, spr.sy || 0, spr.w, spr.h, Math.round(this.cx - spr.w / 2 + jx), Math.round(this.cy - spr.h / 2 - lift), spr.w, spr.h);
        this.drawTrim(g, game);
        return;
      }
    }
    const scale = this.def.scale || 1;
    /* Quartered, for the same reason as the hero above: these were per-frame
     * rates for a two-frame flip, and eight frames at the old numbers would
     * run every creature in the game at a quarter speed. Cycle lengths land
     * within a couple of ticks of what they were — wisp 16 against 12,
     * armoured 24 against 24, a charge 8 against 10, the rest 16 against 18. */
    const rate = this.type === 'wisp' ? 2 : this.def.armoured ? 3
      : this.state === 'charge' ? 1 : 2;
    const still = !this.def.boss && this.type !== 'wisp'
      && this.state === 'idle' && Math.abs(this.x - (this.lastX ?? this.x)) < 0.05
      && Math.abs(this.y - (this.lastY ?? this.y)) < 0.05;
    this.lastX = this.x; this.lastY = this.y;
    const frame = still ? 0 : Math.floor(this.anim / rate) % FRAMES;
    const white = this.flash > 0 && Math.floor(this.flash / 3) % 2 === 0;
    const flip = this.dir === 'left' && (this.type === 'bramble' || this.def.charges);
    /* WHICH WAY IT IS FACING, IF IT HAS ART FOR IT.
     *
     * `flip` on the line above is the whole of directional art for enemies —
     * two types mirrored left/right, and nothing at all for up or down. So
     * every enemy looked identical whichever way it was pointing, and nothing
     * on screen told you which way a charger was aimed. `facing` returns the
     * name unchanged when no `_up`/`_down` set exists, so it is a no-op for
     * the nine actors that do not have one and the next actor that gains one
     * needs a file and an import rather than a second list of type names
     * kept in step with this line. */
    const art = facing(scale > 1 ? this.def.art + '32' : this.def.art, this.dir);
    // A STATE HAS TO LOOK LIKE ONE. The second movement announced itself with
    // a shake, a hitstop and a line of text, and then the boss was byte-
    // identical forever: a player who blinked, or who died and came back, had
    // no standing signal at all. The palette-swap table already existed.
    const img = sprite(art + '_' + frame, flip, white,
      this.enraged && this.alt ? this.alt + 'r' : this.alt);
    // charge wind-up shudder
    const jx = this.state === 'wind' ? (Math.floor(this.anim / 2) % 2 ? 1 : -1) : 0;
    // THE SAME OUTLINE THE PLAYER GETS, for the same reason and to the same
    // thickness. A body that has to be read against every ground in the game
    // cannot rely on its own colours: the grounds span 0.081 to 0.753 relative
    // luminance, so no palette clears the WCAG non-text floor at both ends.
    // The player was measured at 1.05:1 on ash before it got one — and an
    // enemy you cannot see is worse than a player you cannot see, because the
    // player at least knows where they are.
    //
    // `white` is deliberately not passed: the hit flash is a full-white body,
    // and it needs its dark edge most at exactly that moment.
    g.drawImage(spriteOutline(art + '_' + frame, flip,
      this.enraged && this.alt ? this.alt + 'r' : this.alt),
    Math.round(this.x - 1 + jx) - 1, Math.round(this.y - 1) - 1);
    g.drawImage(img, Math.round(this.x - 1 + jx), Math.round(this.y - 1));
    this.drawTrim(g, game);
  }
  /** The guard plate and the boss bar, over either body. */
  drawTrim(g, game?) {
    if (this.def.armoured && !this.def.beast) {   // a beast's shell is its own plate; scaled up, the marker was a slab on a hill
      const [dx, dy] = DIRVEC[this.dir];
      const ax = Math.round(this.cx + dx * 5), ay = Math.round(this.cy + dy * 5);
      const aw = dy ? 12 : 4, ah = dx ? 12 : 4;
      g.fillStyle = UI.shadowHard;
      g.fillRect(ax - aw / 2, ay - ah / 2, aw, ah);
      g.fillStyle = UI.bone;
      g.fillRect(ax - aw / 2 + 1, ay - ah / 2 + 1, aw - 2, ah - 2);
      g.fillStyle = UI.steel;
      g.fillRect(ax - aw / 2 + 1 + dx, ay - ah / 2 + 1 + dy, dy ? aw - 2 : 1, dx ? ah - 2 : 1);
    }
    /* THE GAME IS PASSED IN, NOT REACHED FOR (2026-09-06). This read `game.bandShown` and `game.barsAfterCanopy`
     * inside a method whose only parameter is `g` -- so it resolved to the PAGE'S GLOBAL `window.game`, which is
     * why it has always worked in a browser and would not survive a second Game, a worker, or a bundler that scopes
     * properly. `tsc` had been calling it out as `Cannot find name 'game'` for as long as nobody ran `tsc`. */
    if (game && this.def.boss && !(this.def.beast && game.bandShown) && !game.barsAfterCanopy) this.drawBar(g, game);   // drawn again after the canopy by the frame (the art lens, round 47): here only for a frame that does not
  }

  /** A boss's bar, over everything (the art lens, round 47: the mother's bar was drawn behind the trunks and leaf lobes, because the canopy goes over the bodies on purpose and the bar was part of the body). */
  drawBar(g, game) {
    if (this.dead || !this.def.boss || (this.def.beast && game.bandShown)) return;
    {
      // attached under the sprite: floating clear of it, this read as scenery
      const full = 28;
      const w = Math.max(0, full * (Math.max(0, this.hp) / this.maxHp));
      // above the head, integer-aligned, inset ends: flush at the feet in
      // fractional coordinates it read as a plank the boss was standing on
      // ...and above the DRAWN body, not the collision box: a Great Beast's rig stands far taller than its box, and the bar sat on its back
      // BOUND TO THE BODY (the art lens, round 43): from the rig's first opaque row, not its cell -- a Great Beast's 64-pixel cell is mostly air above the crown, and the bar floated over bare ground
      const top = this.lastRig ? Math.min(Math.round(this.y), Math.round(this.cy - this.lastRig.h / 2) + crownRow(this.lastRig) + 2) : Math.round(this.y);
      const by = Math.max((game.hudTop || 48) + 22, top - 7);   // never in the toast's rows: the toast is painted last and swallowed the bar's lit line (the art lens, round 48)
      const bx = Math.round(this.cx - full / 2);
      const fill = Math.max(0, Math.round(w) - 2);
      g.fillStyle = UI.shadowHard;
      g.fillRect(bx - 1, by, full + 2, 5);
      g.fillStyle = UI.shade;
      g.fillRect(bx, by + 1, full, 3);
      g.fillStyle = UI.danger;
      g.fillRect(bx + 1, by + 1, fill, 3);
      g.fillStyle = UI.gold;
      g.fillRect(bx + 1, by + 1, fill, 1);
    }
  }
}

// -------------------------------------------------------------- projectiles
export class Shot {
  /* FIELDS, DECLARED.
   *
   * The migration's single biggest source of type errors — 4,646 of about
   * 5,300 — was this: properties assigned in methods rather than in the
   * constructor are invisible to the checker, so every read of one was an
   * error. Declaring them is what turns a class from a bag into a shape, and
   * a shape is what catches the defect that started this: `drawWorld`'s parts
   * object was missing `tiles` and nothing said so until it was reported from
   * play.
   *
   * They start as `any` because 44,000 lines cannot be typed in one step and
   * a wrong type is worse than a missing one. Narrowing them is the work that
   * follows, and it is worth doing in the order the bugs came from.
   */
  anim: any;
  cx: any;
  cy: any;
  dead: any;
  friendly: any;
  h: any;
  kind: any;
  life: any;
  vx: any;
  vy: any;
  w: any;
  x: any;
  y: any;


  constructor(kind, x, y, vx, vy, friendly) {
    this.kind = kind; this.x = x; this.y = y; this.vx = vx; this.vy = vy;
    this.friendly = friendly;
    this.w = 8; this.h = 8;
    // NB: a shorter life for the Warden's rings was measured and rejected.
    // Every Warden attack is a burst radiating from its own centre, so the
    // ring is safest at the middle of it — but capping its reach helped the
    // player standing ON the boss (5.7 -> 5.2 hearts taken) more than the one
    // keeping its distance (18.3 -> 16.0), because the hugger is at the spawn
    // point either way. Wrong lever; see docs/REVIEW.md.
    this.life = kind === 'arc' ? 110 : 260;
    this.dead = false; this.anim = 0;
  }
  /** A thrown thing in flight: it strikes the first body it meets, or comes down where its throw runs out or it meets a
   *  wall; the game decides what it does there (thrownHits / thrownLands). */
  flyThing(game) {
    this.anim++;
    this.x += this.vx; this.y += this.vy; clampBox(this);
    for (const e of game.enemies || []) {
      if (e.dead || e.spawnFx > 0 || e.hiding || !overlaps(this, e)) continue;
      this.dead = true; if (game.thrownHits) game.thrownHits(this, e); if (game.thrownLands) game.thrownLands(this, e); return;
    }
    if (--this.life <= 0 || game.solid(this.cx, this.cy, false, null)) {
      if (this.life > 0) { this.x -= this.vx; this.y -= this.vy; clampBox(this); }   // against a wall it drops at the wall's foot
      this.dead = true; if (game.thrownLands) game.thrownLands(this, null);
    }
  }
  /** Where a spore ends it settles (LS-224): the spores field takes it, and standing there is an affliction. */
  settle(game) {
    if (this.kind !== 'spore' || !game.systems || !game.systems.fieldsFor) return;
    const f = game.systems.fieldsFor(game);
    f.bump('spores', Math.floor(this.cx / TS), Math.floor(this.cy / TS), 0.6);
  }

  update(game) {
    if (this.dead) return;
    /* A THING IN HAND (2026-09-27, docs/DIRECTION.md: verbs over items). A lifted rock, a brand taken from a fire or a
     * double handful of water rides over the head: the game places it each frame, and it does nothing until thrown. */
    if ((this as any).carried) { clampBox(this); return; }
    if (this.kind === 'rock' || this.kind === 'brand' || this.kind === 'water') { this.flyThing(game); return; }
    this.anim++;
    this.x += this.vx; this.y += this.vy;
    clampBox(this);
    if (--this.life <= 0) this.dead = true;
    // out of the world's window, not the first screen's: the overworld is one map and a shot's x is a world x,
    // so the old test against PW killed every shot fired anywhere but screen 0,0 on its first frame (LS-224 found it)
    { const cam = game.camera ? game.camera() : { x: 0, y: 0 };   // a screen's width past the glass, whichever way (LS-511: past the screen underfoot)
      if (this.x < cam.x - PW - 10 || this.y < cam.y - PH - 10 || this.x > cam.x + 2 * PW + 10 || this.y > cam.y + 2 * PH + 10) this.dead = true; }
    if (this.dead) return;
    // --- THE EMBERWAKE'S SECOND USE ------------------------------------
    // The wick is braided into the sickle, so a THROWN sickle carries the
    // flame with it. It burns a hedge it strikes and it lights a pod it
    // touches — which means a thicket across a lake, or on the far side of a
    // gap, can be opened without ever standing next to it. Burning what is in
    // front of you is what the Emberwake is for; burning what is not is the
    // reason a route exists that the map does not show.
    if (this.friendly && this.kind === 'arc' && game.state.hasEmber) {
      const hit = game.tileAtPoint(this.cx, this.cy);
      if (hit && hit.t === T.THORN) {
        game.burnThornAt(hit.tx, hit.ty);
        this.dead = true;
        return;
      }
      for (const pod of game.pods) {
        if (!pod.dead && overlaps(this, pod)) {
          pod.dead = true;
          game.blast(pod.cx, pod.cy);
          this.dead = true;
          return;
        }
      }
    }
    if (game.solid(this.cx, this.cy, this.kind === 'shock', null)) {
      this.dead = true; this.settle(game);
      game.puff(this.cx, this.cy, UI.linen, 3);
      return;
    }
    if (this.friendly) {
      for (const e of game.enemies) {
        if (!e.dead && e.spawnFx <= 0 && !((e.friendUntil || 0) > (game.runFrames || 0)) && overlaps(this, e)) {   // a thrown blade passes a companion by (LS-521)
          // A THROWN SICKLE IS WORTH THROWING. Against a Warden it was worth
          // one or two hit points on a two-and-a-half second recharge, out of
          // nineteen to thirty-two — so the ranged verb the fight is built
          // around was arithmetic nobody would choose, and the only viable
          // strategy was to stand inside the guard and hold attack.
          const boss = e.def && e.def.boss;
          const dmg = boss ? 4 : (game.state.hasEmber ? 2 : 1);
          const hp0 = e.hp;
          e.hurt(game, dmg, this.cx - this.vx * 4, this.cy - this.vy * 4);
          /* ...AND IT COSTS THE BLADE A BLOW (the robustness lens). `wearWeapon` was called only from the melee
           * loop, so a beast could be felled by throwing at no cost to the edge at all. It wears on a landed hit,
           * exactly as a swing does, on the slot the throw came from. */
          if (e.hp !== hp0 && (this as any).weapon && game.wearThrown) game.wearThrown((this as any).weapon, (this as any).slot);
          this.dead = true; break;
        }
      }
    } else if (overlaps(this, game.player) && !game.player.submerged) {
      // shots pass over someone who is under the water
      const hpBefore = game.state.hp;
      game.player.hurt(game, 1, this.cx, this.cy);
      if (game.state.hp < hpBefore && this.kind === 'spore') afflict(game.player, 'Poisoned', 200);
      this.dead = true; this.settle(game);
    }
  }
  /** `game` is a PARAMETER. This was `draw(g)` and then read a bare `game`,
   *  which resolved to the `window.game` that `main.js` installs — so the
   *  thrown flame's palette worked in the shipped browser by accident, threw
   *  `ReferenceError` under node, and would silently draw the old cyan cross
   *  if the global were ever cleared. The call site was already passing it. */
  draw(g, game) {
    const x = Math.round(this.x), y = Math.round(this.y);
    const t = Math.floor(this.anim / 4) % 2;
    if (this.kind === 'rock') {   // a grey stone with a lit shoulder and a dark underside
      g.fillStyle = '#2a2622'; g.fillRect(x, y + 2, 8, 5); g.fillRect(x + 1, y + 1, 6, 7);
      g.fillStyle = '#8a8378'; g.fillRect(x + 1, y + 2, 6, 4); g.fillStyle = '#b9b2a4'; g.fillRect(x + 2, y + 2, 3, 2);
      g.fillStyle = '#5d574f'; g.fillRect(x + 1, y + 5, 6, 1);
      return;
    }
    if (this.kind === 'brand') {   // a stick, its end alight: the flame flickers between two shapes
      g.fillStyle = '#3a2618'; g.fillRect(x + 3, y + 3, 2, 6); g.fillStyle = '#7a5232'; g.fillRect(x + 3, y + 4, 1, 4);
      g.fillStyle = t ? '#f2c14e' : '#e0703a'; g.fillRect(x + 2, y, 4, 3); g.fillStyle = t ? '#e0703a' : '#fff0b0'; g.fillRect(x + 3, y + (t ? 0 : 1), 2, 2);
      return;
    }
    if (this.kind === 'water') {   // a cupped splash: three drops that shimmer
      g.fillStyle = '#1e4a66'; g.fillRect(x + 1, y + 2, 6, 5);
      g.fillStyle = '#5fb0d8'; g.fillRect(x + 2, y + 2, 4, 3); g.fillStyle = '#c8ecff'; g.fillRect(x + (t ? 2 : 4), y + 2, 2, 1);
      return;
    }
    if (this.kind === 'spore') {
      g.fillStyle = UI.shadowHard; g.fillRect(x, y, 8, 8);
      g.fillStyle = UI.bloom; g.fillRect(x + 1, y + 1, 6, 6);
      g.fillStyle = UI.bloomPale; g.fillRect(x + 2 + t, y + 2, 2, 2);
    } else if (this.kind === 'ember') {
      g.fillStyle = UI.danger; g.fillRect(x, y, 8, 8);
      g.fillStyle = UI.gold; g.fillRect(x + 2 - t, y + 2, 4, 4);
    } else if (this.kind === 'shock') {
      g.fillStyle = UI.wisp; g.fillRect(x + 1, y + 1, 6, 6);
      g.fillStyle = UI.sporeLit; g.fillRect(x + 3, y, 2, 8); g.fillRect(x, y + 3, 8, 2);
    } else {
      // steady colour, animated shape: this used to invert its whole palette
      // 7.5 times a second, which read as a rendering fault
      const grow = [0, 1, 2, 1][Math.floor(this.anim / 4) % 4];
      // A THROWN SICKLE THAT BURNS HEDGES SHOULD LOOK LIKE FIRE. This was a
      // pulsing cyan cross, byte-identical with and without the Emberwake --
      // measured, 0 pixels different -- while the ENEMY's shot was the one
      // drawn in flame colours and the held sickle went orange when upgraded.
      // The throw that burns a thicket and detonates a pod now says so, in
      // the same palette its own blade wears.
      const lit = this.friendly && game && game.state.hasEmber;
      g.fillStyle = lit ? UI.flameDeep : UI.teal;
      g.fillRect(x + 2 - grow, y + 2 - grow, 4 + grow * 2, 4 + grow * 2);
      g.fillStyle = lit ? UI.flame : UI.mint;
      g.fillRect(x + 1, y + 1, 6, 6);
      g.fillRect(x + 3, y - 2 - grow, 2, 12 + grow * 2);
      g.fillRect(x - 2 - grow, y + 3, 12 + grow * 2, 2);
      g.fillStyle = lit ? UI.goldLit : UI.paper;
      g.fillRect(x + 3, y + 3, 2, 2);
      if (lit) {
        // a trailing spark, so the direction of the flame is legible
        g.fillStyle = t ? UI.gold : UI.goldLit;
        g.fillRect(x + 3 - Math.round(this.vx * 1.5), y + 3 - Math.round(this.vy * 1.5), 2, 2);
      }
    }
  }
}

// --------------------------------------------------------------- seed pods
export class Pod {
  /* FIELDS, DECLARED.
   *
   * The migration's single biggest source of type errors — 4,646 of about
   * 5,300 — was this: properties assigned in methods rather than in the
   * constructor are invisible to the checker, so every read of one was an
   * error. Declaring them is what turns a class from a bag into a shape, and
   * a shape is what catches the defect that started this: `drawWorld`'s parts
   * object was missing `tiles` and nothing said so until it was reported from
   * play.
   *
   * They start as `any` because 44,000 lines cannot be typed in one step and
   * a wrong type is worse than a missing one. Narrowing them is the work that
   * follows, and it is worth doing in the order the bugs came from.
   */
  anim: any;
  cx: any;
  cy: any;
  dead: any;
  fuse: any;
  h: any;
  w: any;
  x: any;
  y: any;


  constructor(x, y) {
    this.x = x; this.y = y; this.w = 12; this.h = 12;
    this.fuse = 95; this.dead = false; this.anim = 0;
    clampBox(this);
  }
  update(game) {
    this.anim++;
    if (--this.fuse <= 0) { this.dead = true; game.blast(this.cx, this.cy); }
  }
  draw(g) {
    // A WHOLE-PIXEL FLASH, NOT A SCALE. `g.scale(1.15, 1.15)` about the pod's
    // centre put every one of its six fills on fractional coordinates with
    // fractional sizes — `x146.25 y211.40 w11.50` — flashing on two frames in
    // four for the last 35 of the fuse. Measured over 4,500 rendered frames it
    // was 6,696 sub-pixel fills, and the only double-density art left in the
    // playfield: a full-frame audit of 494,647 fills found exactly this and
    // one other. An armed pod says so by flaring, not by growing 15%.
    const near = this.fuse < 35;
    const x = Math.round(this.x - 2), y = Math.round(this.y - 3);
    if (near && Math.floor(this.fuse / 4) % 2 === 0) {
      g.fillStyle = UI.gold;
      g.fillRect(x - 1, y + 1, 14, 12);
      g.fillRect(x + 1, y - 1, 10, 16);
    }
    drawPod(g, x, y);
  }
}

/* ------------------------------------------------------------------ flame
 *
 * THE EMBERWAKE, AS A TOOL RATHER THAN A PERMISSION.
 *
 * One rule: an ember sits on a tile, burns what it is sitting on, and creeps
 * into any thorn touching it. It cannot cross anything that is not a thicket,
 * and it goes out on its own.
 *
 * Everything else is a consequence of that rule rather than a feature list.
 * A thicket lights from one corner. A line of thorn is a fuse, so you can
 * reach a hedge across a room you cannot walk. Water does not burn, so a
 * channel is a firebreak and where the two meet you must choose which tool.
 * It lights a brazier it reaches, which means a puzzle can be solved from
 * outside the room. And it sets off a seed pod it touches — a remote
 * detonator, made out of two tools neither of which is one.
 */
export class Flame {
  /* FIELDS, DECLARED.
   *
   * The migration's single biggest source of type errors — 4,646 of about
   * 5,300 — was this: properties assigned in methods rather than in the
   * constructor are invisible to the checker, so every read of one was an
   * error. Declaring them is what turns a class from a bag into a shape, and
   * a shape is what catches the defect that started this: `drawWorld`'s parts
   * object was missing `tiles` and nothing said so until it was reported from
   * play.
   *
   * They start as `any` because 44,000 lines cannot be typed in one step and
   * a wrong type is worse than a missing one. Narrowing them is the work that
   * follows, and it is worth doing in the order the bugs came from.
   */
  anim: any;
  dead: any;
  life: any;
  tx: any;
  ty: any;


  /** `tx, ty` are a world tile (LS-511: a flame is the picture of a burning tile, wherever it is). */
  constructor(tx, ty) {
    this.tx = tx; this.ty = ty;
    this.life = 26;
    this.dead = false; this.anim = 0;
  }
  get cx() { return this.tx * TS + 8; }
  get cy() { return this.ty * TS + 8; }
  update(game) {
    this.anim++;
    if (this.anim % 5 === 0) game.puff(this.cx, this.cy - 2, this.anim % 10 ? UI.gold : UI.flame, 3, 'ember');
    // a flame lives while its tile burns; the matter table decides the rest
    if (!game.systems || !game.systems.burning(this.tx, this.ty)) this.dead = true;
  }
  /* REAL FLAMES (2026-09-12; Vijay: "burning just looks like glowing right now we need actual flames"). A burning
   * tile used to show an eight-pixel sprite under the shader's glow, and the glow was all anyone saw. Now three
   * tongues stand on the tile and lick upward: each a teardrop of rows narrowing to a tip, dark ember at the foot,
   * orange, a yellow core and a pale tip, its height breathing and its rows swaying on their own phase, taller on a
   * fire with fuel left in it. Painted straight to the glass, no sprite: forty rectangles a fire. */
  draw(g, game?) {
    const cx = Math.round(this.cx), base = Math.round(this.cy) + 6;
    const fire = game && game.systems ? game.systems.fires.find((q) => q.x === this.tx && q.y === this.ty) : null;
    const fuel = fire ? Math.max(0.85, Math.min(1.25, 0.85 + (fire.fuel || 0) / 120)) : 1;   // a fire is a fire: fuel makes it taller, never small
    const seed = (this.tx * 7 + this.ty * 13) & 7, t = this.anim;
    const TONGUE: [number, number, number][] = [[-4, 0.0, 0.85], [0, 2.1, 1.0], [4, 4.2, 0.78]];   // x, phase, size
    for (const [ox, ph, size] of TONGUE) {
      const breathe = 1 + 0.22 * Math.sin(t * 0.31 + ph + seed) + 0.1 * Math.sin(t * 0.83 + ph * 1.7);
      const h = Math.max(6, Math.round(22 * size * fuel * breathe));   // a bush burns a head taller than its tile
      const w0 = Math.max(2, Math.round(5 * size));
      for (let r = 0; r < h; r++) {
        const k = r / h;                                             // 0 at the foot, 1 at the tip
        const sway = Math.round(Math.sin(t * 0.27 + r * 0.55 + ph + seed) * (0.6 + 1.6 * k));
        const w = Math.max(1, Math.round(w0 * (1 - k * k) * (k < 0.12 ? 0.8 + k * 1.7 : 1)));
        const y = base - r, x = cx + ox + sway - (w >> 1);
        g.fillStyle = k > 0.9 ? UI.paper : k > 0.55 ? UI.gold : k > 0.18 ? UI.flame : '#b8301a'; g.fillRect(x, y, w, 1);
        if (w >= 3 && k > 0.2 && k < 0.85) { g.fillStyle = k > 0.55 ? UI.paper : UI.gold; g.fillRect(x + (w >> 1), y, 1, 1); }   // the bright core
      }
    }
    if ((t & 3) === 0) { g.fillStyle = UI.gold; g.fillRect(cx - 5 + ((t >> 2) % 9), base - 15 - ((t >> 3) % 5), 1, 1); }   // a spark off the top
  }
}

/* PARTICLES WITH A BODY (LS-190). A puff used to be one of four 8x8 stamps
 * drifting with drag; every death was seven of them. Now a particle has a
 * kind with its own physics and its own drawing: smoke rises and grows,
 * sparks shrink and fall, embers fall with a tail, chunks (bits of the body
 * that was hit) fly out, fall under gravity, bounce once on the ground they
 * came from and lie there, a ring is a shockwave that grows and fades, a flash
 * is a bright disc for three frames, dust is low and slow, a leaf tumbles.
 * Deterministic per spawn from what it was given; the spawner rolls. */
export class Puff {
  colour: any;
  dead: any;
  kind: any;
  life: any;
  max: any;
  vx: any;
  vy: any;
  x: any;
  y: any;
  floor: any;
  size: any;
  spin: any;
  bounced: any;
  trail: any;
  curl: any;
  phase: any;
  freq: any;
  span: any;
  age: any;
  drag: any;
  bias: any;
  foam: any;
  constructor(x, y, colour, vx, vy, life, kind = 'smoke', extra: any = null) {
    this.x = x; this.y = y; this.vx = vx; this.vy = vy;
    this.colour = colour; this.life = life; this.max = life; this.dead = false;
    this.kind = kind;
    this.floor = extra && extra.floor !== undefined ? extra.floor : y + 6;
    // bigger by half (LS-202): a chip is three or four pixels, a spark and a puff four
    this.size = extra && extra.size !== undefined ? extra.size : (kind === 'chunk' ? 3 + (life % 2) : kind === 'ring' ? 3 : 4);
    this.spin = extra && extra.spin !== undefined ? extra.spin : ((life % 3) - 1) * 0.3;
    this.bounced = false;
    if (kind === 'wind') {
      /* A RIBBON, NOT A DROP (LS-209). The first wind was a straight streak
       * behind a point, which read as rain. A streak of moved air is a
       * ribbon: the path the particle has taken, kept as a trail of points
       * and drawn as a curve through them, and the path curves because the
       * velocity is steered a little every frame by a slow sine -- an S, a
       * hook, a swirl, never a line. `span` is how many points the ribbon
       * keeps (a gust keeps thirty, a sword's flick ten); `curl` and `freq`
       * are the steering's size and pace. */
      this.trail = [[x, y]];
      this.age = 0;
      this.span = extra && extra.span !== undefined ? extra.span : 10;
      this.curl = extra && extra.curl !== undefined ? extra.curl : (Math.random() < 0.5 ? -1 : 1) * (0.08 + Math.random() * 0.1);
      this.freq = extra && extra.freq !== undefined ? extra.freq : 0.18 + Math.random() * 0.14;
      this.phase = extra && extra.phase !== undefined ? extra.phase : Math.random() * Math.PI * 2;
      this.drag = extra && extra.drag !== undefined ? extra.drag : 0.98;
      this.bias = extra && extra.bias !== undefined ? extra.bias : 0.35;   // how much of the steering is one-way (a hook) against to-and-fro (an S)
      this.foam = !!(extra && extra.foam);   // the sea's streak (LS-389): laid on the water, counted apart from the air's
    }
  }
  update() {
    const k = this.kind;
    if (k === 'wind') {
      // THE WIND (LS-207, LS-209): the head is steered by a slow sine so the
      // ribbon it leaves curves; the trail keeps the last `span` positions
      this.age++;
      // steered by a sine: to and fro for an S, with a one-way bias for a hook; never a line
      const turn = this.curl * (this.bias + Math.sin(this.phase + this.age * this.freq));
      const c = Math.cos(turn), s = Math.sin(turn);
      const vx = this.vx * c - this.vy * s, vy = this.vx * s + this.vy * c;
      this.vx = vx * this.drag; this.vy = vy * this.drag;
      this.x += this.vx; this.y += this.vy;
      this.trail.push([this.x, this.y]);
      if (this.trail.length > this.span) this.trail.shift();
      if (--this.life <= 0) this.dead = true;
      return;
    }
    if (k === 'fleck') {
      // a speck off the blade (LS-209): flies, slows, and is gone in a dozen frames
      this.x += this.vx; this.y += this.vy;
      this.vx *= 0.9; this.vy *= 0.9;
      if (--this.life <= 0) this.dead = true;
      return;
    }
    if (k === 'chunk' || k === 'leaf' || k === 'ember') {
      this.vy += k === 'leaf' ? 0.04 : k === 'ember' ? 0.06 : 0.22;
      this.vx *= k === 'leaf' ? 0.96 : 0.98;
      if (k === 'leaf') this.vx += Math.sin(this.life * 0.4) * 0.15;
      this.x += this.vx; this.y += this.vy;
      if (k === 'chunk' && this.y >= this.floor && this.vy > 0) {
        this.y = this.floor;
        if (!this.bounced) { this.vy = -this.vy * 0.45; this.vx *= 0.6; this.bounced = true; }
        else { this.vy = 0; this.vx *= 0.7; }
      }
    } else if (k === 'ring' || k === 'flash') {
      this.size += k === 'ring' ? 2.4 : 0;
    } else if (k === 'dust') {
      this.x += this.vx; this.y += this.vy;
      this.vx *= 0.9; this.vy *= 0.9;
      this.size += 0.15;
    } else {
      this.x += this.vx; this.y += this.vy;
      this.vx *= 0.92; this.vy *= 0.92;
      if (k === 'smoke') { this.vy -= 0.02; this.size += 0.12; }
      if (k === 'spark') this.vy += 0.05;
    }
    if (--this.life <= 0) this.dead = true;
  }
  draw(g) {
    const t = 1 - this.life / this.max;
    const k = this.kind;
    const x = Math.round(this.x), y = Math.round(this.y);
    if (k === 'chunk') {
      /* A CHIP IN THE AIR HAS A SHADOW UNDER IT (LS-199). The height was
       * always here -- `floor` is the row the chunk lands on and it bounces
       * off it -- but nothing on the ground said so, so a spray of debris read
       * as pixels sliding across the picture rather than as bits thrown up off
       * it. The shadow stays on the floor while the chip arcs above it, and
       * fades and shrinks with height, which is the one cue that turns the two
       * into one object. Every other solid in the game casts one now. */
      const high = Math.max(0, this.floor - this.y);
      if (high > 0.5) {
        const r = Math.max(1, this.size * (1 - Math.min(0.6, high / 22)));
        g.globalAlpha = Math.max(0.12, 0.34 - high / 60);
        g.fillStyle = UI.shadowHard;
        g.fillRect(Math.round(this.x - r * 0.5), Math.round(this.floor + 1), Math.max(1, Math.round(r)), 1);
        g.globalAlpha = 1;
      }
      // a chip of the body: its colour, a shaded underside, a lit corner while
      // it flies -- a full outline made every chip a black blob at this size
      const s = Math.max(1, Math.round(this.size));
      g.fillStyle = this.colour; g.fillRect(x, y, s, s);
      g.fillStyle = PARTICLE_EDGE; g.globalAlpha = 0.6; g.fillRect(x, y + s - 1, s, 1); g.fillRect(x + s - 1, y, 1, s); g.globalAlpha = 1;
      if (this.vy !== 0 && (this.life & 2)) { g.fillStyle = UI.paper; g.fillRect(x, y, 1, 1); }
      return;
    }
    if (k === 'leaf') {
      /* A CLIPPING (LS-236): a sliver that tumbles, no edge -- the chunk's dark box read as a fleck of dirt, not cut
       * grass. ...AND IT IS A BLADE, NOT A DASH (the reference particle sheet, 2026-09-05): three pixels curved
       * against the tumble, one of them the shaded side, so the piece reads as a leaf turning over in the air. */
      const turn = Math.cos(this.life * 0.35);
      const flat = Math.abs(turn) > 0.5;
      g.fillStyle = this.colour;
      if (flat) { g.fillRect(x, y, 3, 1); g.fillRect(x + (turn > 0 ? 2 : 0), y - 1, 1, 1); }
      else { g.fillRect(x, y, 1, 3); g.fillRect(x + 1, y + (turn > 0 ? 2 : 0), 1, 1); }
      g.globalAlpha = 0.55; g.fillStyle = UI.leafDim;
      g.fillRect(flat ? x + 1 : x, flat ? y + 1 : y + 1, 1, 1);
      g.globalAlpha = 1;
      return;
    }
    if (k === 'ring') {
      const r = this.size;
      g.globalAlpha = Math.max(0, 1 - t) * 0.8;
      g.fillStyle = this.colour;
      const n = Math.max(8, Math.round(r * 2.5));
      for (let i = 0; i < n; i++) { const a = (i / n) * Math.PI * 2; g.fillRect(Math.round(this.x + Math.cos(a) * r), Math.round(this.y + Math.sin(a) * r * 0.75), 1, 1); }
      g.globalAlpha = 1;
      return;
    }
    if (k === 'flash') {
      const r = this.size * (1 - t * 0.5);
      g.globalAlpha = 0.9 - t * 0.8;
      g.fillStyle = this.colour;
      for (let dy = -r; dy <= r; dy++) { const half = Math.sqrt(Math.max(0, r * r - dy * dy)); g.fillRect(Math.round(this.x - half), Math.round(this.y + dy), Math.max(1, Math.round(half * 2)), 1); }
      g.globalAlpha = 1;
      return;
    }
    if (k === 'fleck') {
      /* A GLINT OFF THE BLADE (LS-209): a short streak along its own flight
       * while it is fast, a speck once it slows, with a dark pixel under each
       * so it reads on pale grass as on dark ground. Gold, paper and linen,
       * so the flecks are not the smear's own grey. */
      const a = Math.max(0, 1 - t);
      const sp = Math.hypot(this.vx, this.vy);
      const len = sp > 0.9 ? 3 : sp > 0.4 ? 2 : 1;
      const dx = sp > 0.01 ? this.vx / sp : 1, dy = sp > 0.01 ? this.vy / sp : 0;
      for (let i = 0; i < len; i++) {
        const px = Math.round(this.x - dx * i), py = Math.round(this.y - dy * i);
        g.globalAlpha = a * 0.5; g.fillStyle = UI.shadowHard; g.fillRect(px + 1, py + 1, 1, 1);
        g.globalAlpha = a * (i === 0 ? 1 : 0.7); g.fillStyle = this.colour; g.fillRect(px, py, 1, 1);
      }
      if (t < 0.3) { g.globalAlpha = a; g.fillStyle = this.colour; g.fillRect(x, y - 1, 1, 1); g.fillRect(x - 1, y, 1, 1); }
      g.globalAlpha = 1;
      return;
    }
    if (k === 'wind') {
      /* THE RIBBON (LS-209): a Catmull-Rom spline through the trail, walked
       * a pixel at a time and set down as single pixels so it stays on the
       * palette at 4x; brightest at the head, thinning to nothing at the
       * tail, and the whole thing fading as the puff dies. */
      const tr = this.trail;
      const n = tr.length;
      if (n < 2) return;
      const fade = Math.min(1, (1 - t) * 1.4);
      // two passes: a dark shadow a pixel below, so the ribbon reads on pale grass, then the pale ribbon
      for (let pass = 0; pass < 2; pass++) {
      const shadow = pass === 0;
      g.fillStyle = shadow ? UI.shadowHard : this.colour;
      const oy = shadow ? 1 : 0;
      let lx = NaN, ly = NaN;
      for (let i = 0; i < n - 1; i++) {
        const p0 = tr[Math.max(0, i - 1)], p1 = tr[i], p2 = tr[i + 1], p3 = tr[Math.min(n - 1, i + 2)];
        const along = (i + 1) / (n - 1);
        g.globalAlpha = fade * (shadow ? 0.12 + 0.28 * along : 0.25 + 0.7 * along);
        // the front half of the ribbon is two pixels wide, across its own direction
        const wide = along > 0.5;
        const ddx = p2[0] - p1[0], ddy = p2[1] - p1[1];
        const across = Math.abs(ddx) >= Math.abs(ddy) ? [0, 1] : [1, 0];
        const steps = Math.max(1, Math.ceil(Math.hypot(ddx, ddy)));
        for (let sI = 0; sI <= steps; sI++) {
          const u = sI / steps, u2 = u * u, u3 = u2 * u;
          const px = 0.5 * ((2 * p1[0]) + (-p0[0] + p2[0]) * u + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * u2 + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * u3);
          const py = 0.5 * ((2 * p1[1]) + (-p0[1] + p2[1]) * u + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * u2 + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * u3);
          const rx = Math.round(px), ry = Math.round(py) + oy;
          if (rx === lx && ry === ly) continue;
          g.fillRect(rx, ry, 1, 1);
          if (wide) g.fillRect(rx + across[0], ry + across[1], 1, 1);
          lx = rx; ly = ry;
        }
      }
      }
      g.globalAlpha = 1;
      return;
    }
    if (k === 'dust') {
      const r = this.size;
      g.globalAlpha = (1 - t) * 0.55;
      g.fillStyle = this.colour;
      for (let dy = -1; dy <= 1; dy++) { const half = r * (dy ? 0.6 : 1); g.fillRect(Math.round(this.x - half), y + dy, Math.max(1, Math.round(half * 2)), 1); }
      g.globalAlpha = 1;
      return;
    }
    if (k === 'leaf') {
      const w = Math.abs(Math.cos(this.life * 0.35)) > 0.5 ? 2 : 1;
      g.fillStyle = PARTICLE_EDGE; g.fillRect(x - 1, y - 1, w + 2, 3);
      g.fillStyle = this.colour; g.fillRect(x, y, w, 1);
      return;
    }
    if (k === 'ember') {
      /* A CINDER HAS A HEART (the reference particle sheet): a hot white point inside its own warm glow while it is
       * young, cooling to a single dull pixel as it dies -- one pixel of colour said nothing at all. */
      const a2 = Math.max(0, 1 - t);
      g.globalAlpha = a2 * 0.45; g.fillStyle = this.colour;
      g.fillRect(x - 1, y, 1, 1); g.fillRect(x + 1, y, 1, 1); g.fillRect(x, y - 1, 1, 1); g.fillRect(x, y + 1, 1, 1);
      g.globalAlpha = a2; g.fillStyle = t < 0.45 ? UI.paper : this.colour; g.fillRect(x, y, 1, 1);
      g.globalAlpha = a2 * 0.4; g.fillStyle = this.colour; g.fillRect(Math.round(this.x - this.vx), Math.round(this.y - this.vy), 1, 1);
      g.globalAlpha = 1;
      return;
    }
    /* SPARKS, SPLASHES AND PICKUP SPARKLES ARE LIGHT AND WATER, NOT STICKERS (2026-09-05, against the reference
     * particle sheet; Vijay: "use gemini references for every single sprite in the game including particles").
     * All three fell through to the 8x8 masks, which are dilated by a full black outline for legibility on lime
     * grass -- at four pixels across that outline IS the sprite, and the reference sheet's note was exactly this:
     * "drop the heavy black outlines where they make a particle look like a sticker instead of light or matter".
     * Each is drawn along its own flight instead, with a single dark pixel under it for the grass. */
    if (k === 'spark' || k === 'splash' || k === 'bag') {
      const a2 = Math.max(0, 1 - t);
      const sp = Math.hypot(this.vx, this.vy);
      const dx = sp > 0.01 ? this.vx / sp : 0, dy = sp > 0.01 ? this.vy / sp : -1;
      if (k === 'bag') {
        // a four-point sparkle, brightest at its heart and shrinking as it goes
        const r = t < 0.4 ? 2 : 1;
        g.globalAlpha = a2 * 0.55; g.fillStyle = UI.shadowHard; g.fillRect(x + 1, y + 1, 1, 1);
        g.globalAlpha = a2; g.fillStyle = this.colour;
        g.fillRect(x, y - r, 1, r * 2 + 1); g.fillRect(x - r, y, r * 2 + 1, 1);
        if (t < 0.5) { g.fillStyle = UI.paper; g.fillRect(x, y, 1, 1); }
        g.globalAlpha = 1;
        return;
      }
      // a streak along the flight: long and bright while it is fast, a bead once it slows
      const len = sp > 1.1 ? 4 : sp > 0.6 ? 3 : sp > 0.25 ? 2 : 1;
      for (let i = 0; i < len; i++) {
        const px = Math.round(this.x - dx * i), py = Math.round(this.y - dy * i);
        g.globalAlpha = a2 * 0.45; g.fillStyle = UI.shadowHard; g.fillRect(px + 1, py + 1, 1, 1);
        g.globalAlpha = a2 * (i === 0 ? 1 : 0.75 - i * 0.16);
        // a spark cools as it flies and a droplet does not
        g.fillStyle = k === 'spark' && i > 1 ? UI.ember : this.colour;
        g.fillRect(px, py, 1, 1);
      }
      if (k === 'splash' && len > 2) { g.globalAlpha = a2 * 0.8; g.fillStyle = UI.foamLit; g.fillRect(x, y, 1, 1); }
      if (k === 'spark' && t < 0.25) { g.globalAlpha = a2; g.fillStyle = UI.paper; g.fillRect(x, y, 1, 1); }
      g.globalAlpha = 1;
      return;
    }
    const frame = Math.min(3, Math.floor(t * 4));
    if (k === 'smoke') {
      /* A MASS, NOT A DISC (the reference particle sheet): a plume that swells has a lit crown where the light
       * falls on it, a darker underside, and a rim that breaks into single pixels as it thins away. */
      const r = this.size * (0.6 + t * 0.6);
      g.globalAlpha = (1 - t) * 0.7;
      g.fillStyle = this.colour;
      for (let dy = -r; dy <= r; dy++) { const half = Math.sqrt(Math.max(0, r * r - dy * dy)); g.fillRect(Math.round(this.x - half), Math.round(this.y + dy), Math.max(1, Math.round(half * 2)), 1); }
      g.globalAlpha = (1 - t) * 0.4; g.fillStyle = UI.shadowHard;
      for (let dy = 0; dy <= r; dy++) { const half = Math.sqrt(Math.max(0, r * r - dy * dy)); g.fillRect(Math.round(this.x - half * 0.5), Math.round(this.y + dy), Math.max(1, Math.round(half)), 1); }
      g.globalAlpha = (1 - t) * 0.5; g.fillStyle = UI.paperDim;
      for (let i = 0; i < 3; i++) { const a3 = -2.4 + i * 0.5; g.fillRect(Math.round(this.x + Math.cos(a3) * r * 0.7), Math.round(this.y + Math.sin(a3) * r * 0.7), 1, 1); }
      g.globalAlpha = 1;
      return;
    }
    g.drawImage(particle(k, frame, this.colour), x - 5, y - 5);
  }
}
export class Item {
  tossedAt?: number; // actual drop event; omitted for map placement and save restoration
  /* FIELDS, DECLARED.
   *
   * The migration's single biggest source of type errors — 4,646 of about
   * 5,300 — was this: properties assigned in methods rather than in the
   * constructor are invisible to the checker, so every read of one was an
   * error. Declaring them is what turns a class from a bag into a shape, and
   * a shape is what catches the defect that started this: `drawWorld`'s parts
   * object was missing `tiles` and nothing said so until it was reported from
   * play.
   *
   * They start as `any` because 44,000 lines cannot be typed in one step and
   * a wrong type is worse than a missing one. Narrowing them is the work that
   * follows, and it is worth doing in the order the bugs came from.
   */
  anim: any;
  dead: any;
  gear: any;
  h: any;
  kind: any;
  life: any;
  price: any;
  dur: any;      // a weapon's blows left, carried across the ground (LS-238)
  noPick: any;   // frames before the body may pick it up: a stack dropped by hand lands under your feet (2026-09-18)
  w: any;
  x: any;
  y: any;


  offered = false; forgeTx = 0; forgeTy = 0;   // laid on a forge's rock (LS-384)
  constructor(kind, x, y, permanent = false, extra: any = {}) {
    this.kind = kind; this.x = x; this.y = y; this.w = 14; this.h = 14;
    this.life = permanent ? Infinity : 460;
    this.dead = false; this.anim = 0;
    Object.assign(this, extra);
  }
  update(game) {
    this.anim++;
    clampBox(this);
    if (this.life !== Infinity && --this.life <= 0) this.dead = true;
    if (this.price && !game.canAfford(this)) return;
    // ...and a refused purchase leaves the item on the shelf. `dead = true`
    // ran whatever `collect` decided, so walking into a Long Haft at the cap
    // deleted it — no glim taken, no haft, and nothing to come back for.
    /* BOTH HANDS ARE BUSY (2026-09-10, LS-302), which is what `swimming`
     * already means a few hundred lines up: "swimming is slower, and both
     * hands are busy". So nothing is picked up out of deep water while
     * swimming in it. The silt lung opens deep water to a walker, so a pearl
     * on the bed of a basin was gated by the LUNG and nothing else -- swim
     * over it and it was yours, and the ebb, which is the lung's own verb and
     * the whole point of the situation, bought the player nothing at all.
     * Call the ebb and the basin goes shallow: then you are wading, standing
     * on the bed, and then you can take what is on it. */
    /* ...AND NOR DOES A BODY ON A HULL (2026-09-18). `swimming` is false whenever `aboard` is true, so the one thing
     * in the game that is not lying on ground a player can walk to -- the silt pearl on the bed of a deep basin,
     * whose whole point is that the lung's own ebb is the way to it -- was picked up by sailing a raft over it. A
     * raft can be built with no lung at all. The question is the water, not the pose. */
    if (game.player.swimming || (game.aboard && game.isSwimAt && game.isSwimAt(game.player.cx, game.player.cy))) return;
    if (this.offered) return;   // laid on the forge's rock: the fire's, not yours, until it is made into something (LS-384)
    if ((this.noPick || 0) > 0) { this.noPick--; return; }   // just dropped by hand: half a second to step off it (2026-09-18)
    if (overlaps(this, game.player) && game.collect(this) !== false) this.dead = true;
  }
  draw(g) {
    if (this.life !== Infinity && this.life < 120 && Math.floor(this.life / 6) % 2 === 0) return;
    // pickups bob: the thing the player is hunting should not be the only
    // motionless object on a moving field
    const bob = [0, 0, -1, -1, -1, 0, 0, 1][Math.floor(this.anim / 7) % 8];
    const x = Math.round(this.x), y = Math.round(this.y) + bob;
    /* A PICKUP SITS ON THE GROUND (LS-199). Every world-space thing now casts
     * a contact shadow through the one camera -- rocks, cairns, braziers,
     * bodies -- and a dropped item was the last class that did not, so it
     * floated over the grass while everything around it was planted. The
     * shadow stays on the GROUND while the item bobs above it, which is what
     * makes the bob read as a hover rather than as the whole thing sliding. */
    M3.groundShadow([8, 0, 1], 4, x, Math.round(this.y) + 14,
      (px2: number, py: number, c: string) => { g.fillStyle = c; g.fillRect(px2, py, 1, 1); }, UI.shadow);
    switch (this.kind) {
      case 'sap': drawLeaf(g, x + 4, y + 4, 8, UI.leaf, UI.shadowHard); break;
      case 'glim': drawGlim(g, x, y); break;
      case 'sigil': drawSigil(g, x, y); break;
      case 'pod': drawPod(g, x, y); break;
      case 'spear': case 'axe': case 'torch': case 'hide': case 'plate': case 'mantle': case 'treads': case 'bell': { M3.groundShadow([8, 0, 1], 4.2, x, y + 14, (px2, py, c) => { g.fillStyle = c; g.fillRect(px2, py, 1, 1); }, '#00000030'); drawWeapon(g, this.kind, x + 1, y, true); break; }   // a weapon on the ground (LS-225)
      case 'vigor': drawLeaf(g, x + 2, y + 1, 12,
        Math.floor(this.anim / 8) % 2 ? UI.leaf : UI.paper, UI.shadowHard); break;
      case 'seed': drawSeed(g, x, y - 1, 16, Math.floor(this.anim / 6) % 2 === 0); break;
      case 'gear': drawGear(g, x, y, this.gear, this.anim); break;
      case 'pouch': {
        g.fillStyle = UI.shadowHard; g.fillRect(x + 2, y + 3, 12, 11);
        g.fillStyle = UI.bark; g.fillRect(x + 3, y + 4, 10, 9);
        g.fillStyle = UI.barkLit; g.fillRect(x + 4, y + 5, 4, 3);
        g.fillStyle = UI.shadowHard; g.fillRect(x + 5, y + 1, 6, 3);
        g.fillStyle = UI.leafLit; g.fillRect(x + 6, y + 6, 4, 5);
        break;
      }
      /* THE SAME BAG, FULLER AND BOUND.
       *
       * The deep pouch is the pouch a player already knows at the size a late
       * run can pay for, so it should read as that bag and not as a new
       * object: same silhouette, same leather, filled to the neck and tied
       * with a second cord. A shop item with no picture is a price tag with a
       * word next to it, and the suite refuses one. */
      case 'deeppouch': {
        g.fillStyle = UI.shadowHard; g.fillRect(x + 1, y + 2, 14, 13);
        g.fillStyle = UI.bark; g.fillRect(x + 2, y + 3, 12, 11);
        g.fillStyle = UI.barkLit; g.fillRect(x + 3, y + 4, 5, 4);
        g.fillStyle = UI.shadowHard; g.fillRect(x + 5, y, 6, 3);
        g.fillStyle = UI.shadowHard; g.fillRect(x + 2, y + 7, 12, 1);
        g.fillStyle = UI.leafLit; g.fillRect(x + 4, y + 9, 4, 4);
        g.fillStyle = UI.leaf; g.fillRect(x + 9, y + 9, 3, 4);
        break;
      }
      case 'glimstone': {
        const t = Math.floor(this.anim / 6) % 2;
        g.fillStyle = UI.shadowHard; g.fillRect(x + 2, y + 2, 12, 12);
        g.fillStyle = t ? UI.gold : UI.hot; g.fillRect(x + 3, y + 3, 10, 10);
        g.fillStyle = UI.goldPale; g.fillRect(x + 5, y + 5, 3, 3);
        g.fillStyle = UI.goldDim; g.fillRect(x + 8, y + 9, 3, 3);
        break;
      }
      case 'spark': {
        // a chip of the ember, still lit
        const t2 = Math.floor(this.anim / 5) % 3;
        g.fillStyle = UI.shadowHard; g.fillRect(x + 4, y + 3, 8, 11);
        g.fillStyle = UI.flameDeep; g.fillRect(x + 5, y + 4, 6, 9);
        g.fillStyle = UI.flame; g.fillRect(x + 6, y + 5 + t2, 4, 5);
        g.fillStyle = UI.goldLit; g.fillRect(x + 7, y + 6 + t2, 2, 2);
        g.fillStyle = t2 ? UI.gold : UI.goldLit; g.fillRect(x + 7, y + 1, 2, 2);
        break;
      }
      case 'haft': {
        // a long shaft laid corner to corner, bound at the grip, with the
        // hook of a blade at its head
        for (let i = 0; i < 10; i++) {
          g.fillStyle = UI.shadowHard;
          g.fillRect(x + 2 + i, y + 12 - i, 3, 3);
        }
        for (let i = 0; i < 10; i++) {
          g.fillStyle = i < 3 ? UI.flameDeep : UI.bark;
          g.fillRect(x + 3 + i, y + 12 - i, 2, 2);
        }
        g.fillStyle = UI.barkLit; g.fillRect(x + 6, y + 9, 2, 2);
        g.fillStyle = UI.shadowHard; g.fillRect(x + 10, y + 1, 5, 3);
        g.fillRect(x + 12, y + 3, 3, 3);
        g.fillStyle = UI.bone; g.fillRect(x + 11, y + 2, 3, 1);
        g.fillStyle = UI.steel; g.fillRect(x + 12, y + 4, 2, 1);
        break;
      }
      case 'ward': {
        // A ROUND stone on a cord, not a plaque. As a 10x13 upright rectangle
        // with a bright inset it was TWELVE silhouette pixels from the sigil —
        // and the sigil is the key that opens a Warden's door while this is a
        // one-hit consumable. A disc with a chip out of its edge, hung from a
        // visible loop, shares no outline with anything else in the set.
        const t2 = Math.floor(this.anim / 9) % 2;
        g.fillStyle = UI.shadowHard;
        g.fillRect(x + 4, y + 5, 8, 10); g.fillRect(x + 3, y + 7, 10, 6);
        g.fillStyle = UI.steel;
        g.fillRect(x + 5, y + 6, 6, 8); g.fillRect(x + 4, y + 8, 8, 4);
        g.fillStyle = UI.bone; g.fillRect(x + 5, y + 7, 2, 3);
        g.fillStyle = UI.slate; g.fillRect(x + 9, y + 10, 2, 3);
        // the chip out of its edge: this is a thing that breaks
        g.fillStyle = UI.shadowHard; g.fillRect(x + 10, y + 5, 3, 3);
        g.fillStyle = t2 ? UI.glass : UI.sky; g.fillRect(x + 7, y + 9, 2, 2);
        g.fillStyle = UI.bark;
        g.fillRect(x + 6, y + 2, 1, 3); g.fillRect(x + 9, y + 2, 1, 3);
        g.fillRect(x + 6, y + 1, 4, 1);
        break;
      }
      case 'chart': {
        // a rolled hide with a line of country on it
        g.fillStyle = UI.shadowHard; g.fillRect(x + 2, y + 3, 12, 10);
        g.fillStyle = UI.linen; g.fillRect(x + 3, y + 4, 10, 8);
        g.fillStyle = UI.linenDim; g.fillRect(x + 4, y + 6, 8, 1);
        g.fillStyle = UI.linenDim; g.fillRect(x + 6, y + 8, 5, 1);
        g.fillStyle = UI.leafLit; g.fillRect(x + 5, y + 9, 2, 2);
        g.fillStyle = UI.seaDim; g.fillRect(x + 9, y + 5, 2, 2);
        g.fillStyle = UI.stone; g.fillRect(x + 2, y + 3, 1, 10);
        g.fillRect(x + 13, y + 3, 1, 10);
        break;
      }
      /* A KIND WITH NO CASE DRAWS ITS OWN ICON, NOT A MAGENTA SQUARE (2026-09-06).
       *
       * This switch had cases for `chart`, `pouch`, `deeppouch`, `glimstone`, `haft`, `seed`, `sigil`, `spark`,
       * `vigor` and `ward` -- every one of them the shop's, and the shop went with the pivot -- and no case for
       * `tide`, `ember` or `sickle`. `tide` and `ember` are the two CHARM caches, two of the eight a world lays, so
       * a quarter of the loot on the ground was a bright pink twelve-by-twelve block. Found by looking at a
       * screenshot of the old shell's lair, where the tide charm lies.
       *
       * The art was never missing: `ICON_SCENES` has had a fan shell with a pearl at its hinge for the tide and a
       * curling flame for the ember all along, and `drawWeapon` renders any of them. So the fallback asks for the
       * icon, which fixes every kind at once instead of the two that happen to be spawning today. `art.ts` already
       * says the same thing about tiles: "a kind with no painter is ground, not magenta". */
      default:
        if (itemIcon(this.kind)) drawWeapon(g, this.kind, x, y);
        else { g.fillStyle = UI.linen; g.fillRect(x + 2, y + 3, 12, 10); g.fillStyle = UI.shadowHard; g.fillRect(x + 3, y + 4, 10, 8); }
    }
  }
}

export function drawGear(g, x, y, gear, anim) {
  const t = Math.floor(anim / 8) % 2;
  if (gear === 'pods') { drawPod(g, x, y); return; }
  if (gear === 'tide') {
    g.fillStyle = UI.shadowHard; g.fillRect(x + 2, y + 2, 12, 12);
    g.fillStyle = UI.seaDim; g.fillRect(x + 3, y + 3, 10, 10);
    g.fillStyle = t ? UI.underPale : UI.foamLit;
    g.fillRect(x + 5, y + 6, 6, 2); g.fillRect(x + 4, y + 9, 8, 2);
    g.fillStyle = UI.gold; g.fillRect(x + 7, y + 1, 2, 3);
    return;
  }
  g.fillStyle = UI.shadowHard; g.fillRect(x + 2, y + 1, 11, 14);
  g.fillStyle = t ? UI.ember : UI.gold;
  g.fillRect(x + 6, y + 2, 3, 11);
  g.fillRect(x + 3, y + 3, 8, 3);
  g.fillStyle = UI.barkPale; g.fillRect(x + 6, y + 11, 3, 3);
}
